import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  FaPlus, FaRegLightbulb, FaUsers, FaTrash, FaHistory, FaUserTie,
  FaUpload, FaFileInvoiceDollar, FaFolderOpen, FaRegSave,
  FaChevronDown, FaChevronUp
} from 'react-icons/fa';
import {
  FiSave, FiFileText, FiX, FiCheck, FiUserCheck, FiMapPin, FiAward, FiChevronUp, FiChevronDown, FiUploadCloud, FiPlus, FiEdit, FiEye, FiCopy, FiPaperclip,
  FiMail, FiPhone, FiBriefcase, FiGlobe, FiClock, FiBookOpen, FiDollarSign
} from 'react-icons/fi';
import { IoArrowBackOutline, IoNewspaperSharp } from 'react-icons/io5';
import DatePicker from "react-multi-date-picker";
import Select, { createFilter } from "react-select";
import { Country, State, City } from "country-state-city";
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../utils/apiRequest';
import { DEFAULT_CURRENCY_RATES, fetchCurrencyRates, fetchCurrencyRateHistory, rateFromHistoryAsOf, ratesAsOf } from '../utils/currencyRates';
import formatDate from '../utils/formatDate';
import { invoiceDateWindow, isDateInWindow } from '../utils/invoiceDateWindow';
import { resolveInviteTemplate, fillInviteTemplate } from '../utils/inviteMailTemplate';
import { API_URLS } from '../config';
import PhoneField from "../Components/PhoneField";
import Competency from "../Components/Competency";
import { filterSelectStyles } from '../Components/FilterPanel';
import ClientSpecificApproval from "../Components/ClientSpecificApproval";
import { BRANCH_OPTIONS, DEGREE_OPTIONS} from "../pages/master/Inspectors";
import Draggable from 'react-draggable';
import { useToast } from '../Components/Toast';
import {
  makeShiftRangeMapDays, expandMonthsToDays, deriveSelectedMonthsFromDates,
  countDistinctMonths, groupDatesByMonth, monthlyShareAmount,
} from '../utils/tentativeDates';
import './Manday.css';
import './master/Inspectors.css';
import { CgNotes } from 'react-icons/cg';
import { LuDownload, LuEye } from 'react-icons/lu';
import { BsSendPlusFill } from 'react-icons/bs';

// --- Constants & Static Data ---
const CURRENCY_SYMBOLS = { INR: '₹', USD: '$', AED: 'د.إ', QAR: 'ر.ق', SAR: 'ر.س', SGD: 'S$', EUR: '€', GBP: '£', OMR: 'ر.ع.', AUD: 'A$' };

// Formats a date as "9.Sept,2026" for the invite email subject line.
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
const formatSubjectDate = (dateStr) => {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return `${d.getDate()}.${MONTH_ABBR[d.getMonth()]},${d.getFullYear()}`;
};
// Same idea for the invite email body, e.g. "9 Sept,2026" (space instead of a dot).
const formatMessageDate = (dateStr) => {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTH_ABBR[d.getMonth()]},${d.getFullYear()}`;
};

const REMARK_STATUS_OPTIONS = [
  'Sourcing', 'CV Submitted', 'Regreted', 'Follow Up', 'New CV Submitted',
  'Enquiry Won', 'Date awaiting', 'Date added', 'Completed'
];

// The free-text Remark field is only relevant (and shown) for these Remark Status
// values — everywhere else it's hidden, with no other visibility/required rule.
const REMARK_STATUS_SHOWS_REMARK = ['CV Submitted', 'Regreted', 'Follow Up'];

// Remark Status choices narrow as the enquiry Status progresses through the funnel —
// New starts it (Sourcing → CV Submitted → Regreted), In Process continues it
// (CV Submitted → Follow Up / New CV Submitted / Enquiry Won, or drops out to
// Regreted), and Won finishes it (Enquiry Won → Date awaiting → Date added →
// Completed), but can still drop out to Regreted too (a won enquiry can fall
// through, e.g. the client backs out). Lost normally only shows Regreted, but
// also offers CV Submitted as a way back into In Process (a lost enquiry can be
// revived).
const REMARK_STATUS_OPTIONS_BY_STATUS = {
  NEW: ['Sourcing', 'CV Submitted', 'Regreted'],
  INPROCESS: ['CV Submitted', 'Follow Up', 'New CV Submitted', 'Enquiry Won', 'Date awaiting', 'Regreted'],
  WON: ['Enquiry Won', 'Date added', 'Completed', 'Regreted'],
  LOST: ['Regreted', 'CV Submitted'],
};

// Picking certain Remark Status values drives the enquiry Status forward automatically
// (New→CV Submitted moves Status to In Process, New→Regreted moves it to Lost,
// In Process→Enquiry Won moves it to Won, In Process→Regreted moves it to Lost,
// Won→Regreted moves it to Lost, Lost→CV Submitted moves it back to In Process) —
// { [fromStatus]: { [remarkStatusValue]: toStatus } }
const REMARK_STATUS_TRANSITIONS = {
  NEW: { 'CV Submitted': 'INPROCESS', 'Regreted': 'LOST' },
  INPROCESS: { 'Enquiry Won': 'WON', 'Regreted': 'LOST' },
  WON: { 'Regreted': 'LOST' },
  LOST: { 'CV Submitted': 'INPROCESS' },
};

// Picking a Status by hand (or auto-advancing into one via REMARK_STATUS_TRANSITIONS
// above) resets Remark Status to that stage's starting point so the two fields stay
// in sync.
const STATUS_DEFAULT_REMARK = { NEW: 'Sourcing', INPROCESS: 'CV Submitted', LOST: 'Regreted', WON: 'Enquiry Won' };

// Remark Status options for the current enquiry Status, always including whatever
// value is already selected (even a legacy one outside today's funnel) so the
// <select> never silently blanks out.
const remarkStatusOptionsFor = (status, current) => {
  const base = REMARK_STATUS_OPTIONS_BY_STATUS[status] || REMARK_STATUS_OPTIONS;
  const withCurrent = current && !base.includes(current) ? [current, ...base] : base;
  return current ? withCurrent : ['', ...withCurrent];
};

// Location dropdowns match from the start of the name, so "oman" finds Oman, not Romania.
const matchFromStart = createFilter({ matchFrom: "start" });

// One overtime entry's amount — hours × rate. Mirrors CustomerInvoice.jsx's
// overtimeEntryAmount. Overtime lives per-invoice only (currentInvoice.overtime).
function overtimeEntryAmount(entry) {
  return (parseFloat(entry?.hours) || 0) * (parseFloat(entry?.rate) || 0);
}

// Sum of a work order's itemized other-cost entries (e.g. "Travel" / 100).
function otherCostEntriesTotalOf(billing) {
  return (billing?.otherCostEntries || []).reduce((s, oc) => s + (parseFloat(oc?.amount) || 0), 0);
}

const ALL_COUNTRIES = Country.getAllCountries();

const payableStatusStyle = (s) => {
  if (s === 'Paid')         return { background: '#d1fae5', color: '#065f46' };
  if (s === 'Not Payable')  return { background: '#fee2e2', color: '#991b1b' };
  if (s === 'Partial Paid') return { background: '#fef3c7', color: '#92400e' };
  if (s === 'Payable')      return { background: '#dbeafe', color: '#1e40af' };
  return { background: '#e5e7eb', color: '#374151' }; // Booked (default)
};

const Manday = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const handleCopy = async (text, label = 'Copied') => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isView = searchParams.get('view') === 'true';
  const isAdd = !id;
  // Where the header/bottom Back button returns to — normally the Enquiry list, but
  // when opened via a details/"View Enquiry" link from another page (e.g. Finance's
  // Inspection Invoice or Customer Invoice, which open this in a new tab with
  // ?from=...), it should return there instead.
  const FROM_RETURN_PATHS = {
    'inspection-invoice': '/finance/inspection-invoice',
    'customer-invoice': '/finance/customer-invoice',
  };
  // Pages with their own query string (e.g. the dashboard drill-down view) pass
  // their full URL as router state instead of a fixed ?from= key.
  const returnTo = location.state?.returnTo || FROM_RETURN_PATHS[searchParams.get('from')] || '/enquiry';
  // Opened with a returnTo: that page is the previous history entry (saves below
  // replace rather than push), so step back to it instead of pushing a duplicate.
  const goReturnTo = () => (location.state?.returnTo ? navigate(-1) : navigate(returnTo));
  const { user } = useAuth();
  const canSeePricing = user?.role === 'global_superadmin' || user?.role === 'company_admin';

  // --- 1. Master Data State ---
  const [companies, setCompanies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [subDisciplines, setSubDisciplines] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [dbInspectors, setDbInspectors] = useState([]);
  const [certificateOptions, setCertificateOptions] = useState([]);
  // DB-backed exchange rates (INR per 1 unit of foreign currency); defaults until fetched
  const [CURRENCY_RATES, setCurrencyRates] = useState(DEFAULT_CURRENCY_RATES);
  // Dated rate history — used to look up the rate as of a specific date
  const [rateHistory, setRateHistory] = useState({});

  // Finance status + Send to Finance (same flow as the Enquiry list page)
  const [financeStatus, setFinanceStatus] = useState({ sentToFinance: false, invoiceExists: false, hasNewWork: false, invoices: [] });
  // inspectorId -> { dates: [invoiced YYYY-MM-DD], hasInvoice } — drives the invoiced-date/cost lock
  const [invoicedByInspector, setInvoicedByInspector] = useState({});
  const [sendingToFinance, setSendingToFinance] = useState(false);
  // Send-to-Finance popup: pick which inspection dates to bill
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendInspectorDates, setSendInspectorDates] = useState([]);   // report-status inspectorDates rows
  const [pickedDates, setPickedDates] = useState({});                 // row index -> array of picked dates ('*' = whole legacy row)

  // --- 2. Main Form State ---
  const [formData, setFormData] = useState({
    company: '',
    enquiryDate: new Date().toISOString().split('T')[0],
    customerId: '',
    customerCoordinator: [],
    customerRequirement: '',
    manufacturerVendor: '',
    country: '',
    selectedProducts: [],
    tentativeDates: '',
    totalDays: 0,
    status: 'NEW',
    remark: '',
    remarkStatus: 'Sourcing',
    competency: {},
    createdBy: null,
    // Manual admin override to exclude this enquiry's revenue from the coordinator's
    // quarterly incentive calc — see quarterlyIncentiveEligible in backend/models/Enquiry.js.
    quarterlyIncentiveEligible: true,
    // MANDAY: pick individual inspection dates. MANMONTH: pick whole month(s) and the
    // system auto-generates that month's calendar days as the plan; billing then uses
    // a monthly rate (see the Enquiry Type field + Billing Details below) instead of a
    // per-day rate.
    enquiryType: 'MANDAY',
  });

  // Coordinator-incentive tier tables (Confirm table's "Incentive" column) — read-only
  // preview of what this inspector's own margin would earn on its own, computed live
  // client-side and never saved. Deliberately NOT the full incentive-report logic
  // (no combined-WON-floor gate, no monthly aggregation, no manmonth flat-rate/gate) —
  // just the plain margin→rate tier lookup from that company's own settings, same
  // rate table backend/utils/incentiveCalculator.js uses for the real payout calc.
  // Visible to everyone (not gated by canSeePricing) — uses the open /tiers endpoint,
  // which only ever exposes the rate table itself, never Cost/Offered/Margin figures.
  const [incentiveTiers, setIncentiveTiers] = useState(null); // { enabled, othersTiers, indiaTiers } | null

  // Keep CURRENCY_RATES pinned to the enquiry date so the Inspector Confirm Table
  // and Financials always show the rate that applied when the enquiry was created.
  useEffect(() => {
    if (!formData.enquiryDate) return;
    setCurrencyRates(ratesAsOf(rateHistory, formData.enquiryDate));
  }, [rateHistory, formData.enquiryDate]);

  useEffect(() => {
    if (!formData.company) { setIncentiveTiers(null); return; }
    const token = localStorage.getItem('token');
    const companyId = formData.company?._id || formData.company;
    apiRequest(`${API_URLS.INCENTIVE}/tiers/${companyId}`, {}, token).then((res) => {
      if (res?.ok) setIncentiveTiers(res.data.data);
      else setIncentiveTiers(null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.company]);

  // Mirrors getBucket()/getTierRate() in backend/utils/incentiveCalculator.js exactly —
  // India vs Others by country, then the first tier whose [min, max) the margin falls
  // into (max: null on the DB's last tier means unbounded).
  const incentiveBucketFor = (countryString) =>
    (countryString || '').trim().toLowerCase() === 'india' ? 'INDIA' : 'OTHERS';
  const incentiveRateFor = (marginPercent) => {
    if (!incentiveTiers || incentiveTiers.enabled === false) return null;
    if (marginPercent < 0) return null; // a loss-making line has no incentive tier to fall into
    const tiers = incentiveBucketFor(formData.country) === 'INDIA' ? incentiveTiers.indiaTiers : incentiveTiers.othersTiers;
    if (!Array.isArray(tiers) || tiers.length === 0) return null;
    const tier = tiers.find((t) => marginPercent >= t.min && marginPercent < (t.max ?? Infinity));
    return (tier || tiers[tiers.length - 1]).rate;
  };

  // Month(s) selected in the Project Details month-picker when Enquiry Type is
  // Manmonth — the only UI control for tentativeDates in that mode (no day-by-day
  // adjustment picker). Kept as Date objects so react-multi-date-picker's
  // onlyMonthPicker can bind directly. Unused/ignored when Enquiry Type is Manday.
  const [selectedMonths, setSelectedMonths] = useState([]);

  // Display-only name of the coordinator (creator) — for a new enquiry that's the
  // logged-in user (they'll become createdBy on save); for an existing one it's
  // whoever created it, fetched via createdBy populate. Kept separate from formData
  // so the raw createdBy id (not this populated name) is what gets sent back on save.
  const [coordinatorName, setCoordinatorName] = useState(() => user?.fullName || user?.name || '');

  // --- 3. Inspector Management State ---
  const [inspectors, setInspectors] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [inspSearch, setInspSearch] = useState('');
  const [inspFilterCountry, setInspFilterCountry] = useState('');
  const [inspFilterState, setInspFilterState] = useState('');
  const [inspFilterCity, setInspFilterCity] = useState('');
  const [inspFilterProduct, setInspFilterProduct] = useState('');
  const [inspFilterCertificate, setInspFilterCertificate] = useState('');
  // Work history with this enquiry's company — { [inspectorId]: { totalJobs, wonJobs, customerJobs, totalDays, lastJobDate, lastJobNumber } }
  const [inspHistory, setInspHistory] = useState({});
  const [inspFilterHistory, setInspFilterHistory] = useState('');   // '' | 'company' | 'customer' | 'new'
  const [inspFilterLastUsed, setInspFilterLastUsed] = useState(''); // '' | months as string ('3', '6', '12')
  const [inspMinJobs, setInspMinJobs] = useState('');
  const [inspSort, setInspSort] = useState('');                     // '' | 'jobs' | 'last' | 'name'
  const [inspPage, setInspPage] = useState(1);
  const [inspPageSize, setInspPageSize] = useState(25);

  // --- 4. Deliverables & Invoices State ---
  const [deliverables, setDeliverables] = useState([]);
  const [currentDeliverable, setCurrentDeliverable] = useState({
    inspectorName: '',
    documentType: '',
    number: '',
    date: [],
    file: null
  });
  const [invoices, setInvoices] = useState([]);
  const [currentInvoice, setCurrentInvoice] = useState({
    inspectorAgency: '',
    workOrder: [],
    invoiceNumber: '',
    invoiceDate: '',
    datesCovered: [],
    daysCovered: '',
    taxes: [],
    overtime: [],
    otherCostEntries: [],
    file: null
  });

  // Client Documents — client-facing paperwork (IR, NOT, ITP, ...) uploaded to
  // OneDrive; restricted to PDF/Word/Excel/ZIP/RAR files only.
  const [clientDocuments, setClientDocuments] = useState([]);
  const [currentClientDocument, setCurrentClientDocument] = useState({
    documentType: '',
    number: '',
    file: null
  });

  // Remark Logs — a read-only, auto-generated history of Remark Status changes.
  // No manual add/edit/delete: a row is appended automatically whenever Save
  // Enquiry / Save Draft is clicked and Remark Status has changed since the last
  // save (tracked via lastLoggedRemarkStatusRef below).
  const [remarkLogs, setRemarkLogs] = useState([]);
  const lastLoggedRemarkStatusRef = useRef(null);

  // Snapshot of the enquiry as last loaded/saved — used to detect unsaved changes
  // when the user tries to navigate away. Captured on first render (empty baseline
  // for a new enquiry) and refreshed once an existing enquiry finishes loading.
  const initialSnapshotRef = useRef(null);
  if (initialSnapshotRef.current === null) {
    initialSnapshotRef.current = JSON.stringify({ formData, inspectors, deliverables, invoices, clientDocuments, remarkLogs });
  }

  // --- 5. UI/Modal State ---
  const [collapsed, setCollapsed] = useState({
    basicInfo: false,
    projectDetails: false,
    customerDetails: false,
    vendorDetails: false,
    clientDocuments: false,
    suggestedInspectors: false
  });
  const [dropdownVisible, setDropdownVisible] = useState({
    customer: false,
    coordinator: false,
    product: false,
    vendor: false
  });
  const [searchTerms, setSearchTerms] = useState({
    customer: '',
    coordinator: '',
    product: '',
    vendor: ''
  });
  const [dropdownFocusIdx, setDropdownFocusIdx] = useState({ customer: -1, coordinator: -1, product: -1, vendor: -1 });

  // Modal Visibility
  const [showAddCoordModal, setShowAddCoordModal] = useState(false);
  const [addCoordForm, setAddCoordForm] = useState({ customerId: '', name: '', email: '', mobileNumber: '' });
  const [savingCoord, setSavingCoord] = useState(false);
  const [showAddVendorModal, setShowAddVendorModal] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteModalDragged, setInviteModalDragged] = useState(false);
  const [inviteTarget, setInviteTarget] = useState(null); // suggestion row ('new') or inspectors[] row ('resend')
  const [inviteMode, setInviteMode] = useState('new'); // 'new' | 'resend' | 'bulk'
  const [bulkInviteTargets, setBulkInviteTargets] = useState([]); // suggestion rows, when inviteMode === 'bulk'
  const [inviteForm, setInviteForm] = useState({ subject: '', message: '' });
  const [inviteAttachments, setInviteAttachments] = useState([]); // File[] — emailed via Graph, 20 MB total cap (frontend warning only)
  const [sendingInvite, setSendingInvite] = useState(false);
  const [selectedSuggestionIds, setSelectedSuggestionIds] = useState(new Set()); // checked rows in Available Inspectors
  const [showGenerateWorkOrderModal, setShowGenerateWorkOrderModal] = useState(false);
  const [editInspectorName, setEditInspectorName] = useState(false);
  const [showAddInspectorModal, setShowAddInspectorModal] = useState(false);
  const [inspModalDragged, setInspModalDragged] = useState(false);
  const [coordModalDragged, setCoordModalDragged] = useState(false);
  const [vendorModalDragged, setVendorModalDragged] = useState(false);
  const [productModalDragged, setProductModalDragged] = useState(false);
  const [suggestionModalDragged, setSuggestionModalDragged] = useState(false);
  const [workOrderModalDragged, setWorkOrderModalDragged] = useState(false);
  const [remarkLogsModalDragged, setRemarkLogsModalDragged] = useState(false);
  const [woSectionCollapsed, setWoSectionCollapsed] = useState({});
  const toggleWoSection = (key) => setWoSectionCollapsed(prev => ({ ...prev, [key]: !prev[key] }));
  const [showViewInspectorModal, setShowViewInspectorModal] = useState(false);
  const [showConfirmTable, setShowConfirmTable] = useState(false);
  const [showRemarkLogsModal, setShowRemarkLogsModal] = useState(false);

  // New Inspector Form State
  const [newInspForm, setNewInspForm] = useState({
    inspectorName: "",
    email: "",
    phone: "",
    cvUrl: "", cvFile: null,
    inspectorSource: "Direct",
    age: "",
    experience: "",
    degree: "",
    branch: "",
    country: "",
    state: "",
    city: "",
    competency: {},
    certificates: [],
    clientApprovals: { clients: [], otherClients: [] },
    bankIndia: { beneficiaryName: "", bankName: "", branch: "", bankAddress: "", accountNo: "", ifscCode: "" },
    bankOther: { beneficiaryName: "", beneficiaryAddress: "", bankName: "", bankAddress: "", ibanNo: "", swiftCode: "" },
    contactPersons: [],
    profileImageUrl: "",
    profileImageFile: null,
  });

  // Work Order Modal State
  const [selectedInspectorForWO, setSelectedInspectorForWO] = useState(null);
  const [workOrderForm, setWorkOrderForm] = useState({
    date: new Date().toISOString().split('T')[0],
    workOrderNumber: '',
    inspectorName: '',
    orderReceivedBy: '',
    workLocation: '',
    workDescription: '',
    additionalComments: '',
    issuingCompany: 'APS Consultancy Private Limited',
    expectedStartDate: '',
    expectedEndDate: '',
    billing: { days: 0, rate: 0, months: 0, monthlyRate: 0, workingDaysByMonth: {}, amount: 0, tax: 0, otherCosts: 0, total: 0 },
    administrative: { orderReceivedBy: '', compiledBy: '', approvingParty: '', dateOfApproval: new Date().toISOString().split('T')[0] }
  });

  // Inspector Sub-Section Toggles
  const [secBasic, setSecBasic] = useState(true);
  const [secLocation, setSecLocation] = useState(true);
  const [secQual, setSecQual] = useState(true);

  // Other Modal Sub-States
  const [newVendorForm, setNewVendorForm] = useState({ vendorName: '', country: '', state: '', city: '', address: '' });
  const [vSearch, setVSearch] = useState({ country: '', state: '', city: '' });
  const [vOpen, setVOpen] = useState({ country: false, state: false, city: false });
  const [vFocusIdx, setVFocusIdx] = useState({ country: -1, state: -1, city: -1 });
  // eslint-disable-next-line no-unused-vars
  const [iSearch, setISearch] = useState({ country: '', state: '', city: '' });
  // eslint-disable-next-line no-unused-vars
  const [iOpen, setIOpen] = useState({ country: false, state: false, city: false });
  const [newProductForm, setNewProductForm] = useState({ name: '', disciplineId: '', subDisciplineId: '' });
  const [certFormOpen, setCertFormOpen] = useState(false);
  const [certInput, setCertInput] = useState({ certificateId: "", expiryDate: "", fileUrl: "", certFile: null });
  const [viewingInspector, setViewingInspector] = useState(null);

  // Loading States
  const [savingVendor, setSavingVendor] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);
  const [savingEnquiry, setSavingEnquiry] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [savingInspector, setSavingInspector] = useState(false);
  const [savingWorkOrder, setSavingWorkOrder] = useState(false);

  // Signed work order upload state
  const [showSignedWOModal, setShowSignedWOModal] = useState(false);
  const [signingInspector, setSigningInspector] = useState(null);
  const [signedWOFile, setSignedWOFile] = useState(null);
  const [uploadingSignedWO, setUploadingSignedWO] = useState(false);

  // Invoice download loading state — Set of invoice _ids currently being fetched
  const [downloadingInvoices, setDownloadingInvoices] = useState(new Set());
  const [previewingInvoices, setPreviewingInvoices] = useState(new Set());

  // OneDrive upload state
  const [uploadingRows, setUploadingRows] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [pendingUpload, setPendingUpload] = useState(null); // { type: 'invoice', mongoId } — invoices only
  const [addingDeliverable, setAddingDeliverable] = useState(false);
  const [addingInvoice, setAddingInvoice] = useState(false);
  const [addingClientDocument, setAddingClientDocument] = useState(false);
  const [editingDeliverableId, setEditingDeliverableId] = useState(null);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [showInvoiceFormModal, setShowInvoiceFormModal] = useState(false);
  const [editingClientDocumentId, setEditingClientDocumentId] = useState(null);
  const uploadInputRef = useRef(null);
  const addFileRef = useRef(null);
  const addInvoiceFileRef = useRef(null);
  const addClientDocFileRef = useRef(null);
  const tentativeAnchorRef = useRef(null);
  const inspectionAnchorsRef = useRef({});
  const invoiceDatesCoveredAnchorRef = useRef(null);
  const addInspectorModalRef = useRef(null);
  const addCoordModalRef = useRef(null);
  const addVendorModalRef = useRef(null);
  const addProductModalRef = useRef(null);
  const suggestionModalRef = useRef(null);
  const inviteModalRef = useRef(null);
  const workOrderModalRef = useRef(null);
  const remarkLogsModalRef = useRef(null);

  // --- 6. Effects ---

  // Fetch Master Data
  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      const [compRes, custRes, vendRes, userRes, inspRes, prodRes, discRes, certRes] = await Promise.all([
        apiRequest(API_URLS.COMPANIES, { method: 'GET' }, token),
        apiRequest(`${API_URLS.CUSTOMERS}?limit=1000`, { method: 'GET' }, token),
        apiRequest(`${API_URLS.VENDORS}?limit=1000`, { method: 'GET' }, token),
        apiRequest(`${API_URLS.USERS}?limit=1000`, { method: 'GET' }, token),
        apiRequest(`${API_URLS.INSPECTORS}?all=true`, { method: 'GET' }, token),
        apiRequest(`${API_URLS.PRODUCTS}?limit=1000`, { method: 'GET' }, token),
        apiRequest(`${API_URLS.DISCIPLINES}?limit=1000`, { method: 'GET' }, token),
        apiRequest(`${API_URLS.CERTIFICATES}?limit=1000`, {}, token)
      ]);

      if (compRes?.ok) {
        setCompanies(compRes.data);
      } else {
        // Fallback for non-superadmin roles (coordinator, admin, etc.) — fetch own company only
        const myCompRes = await apiRequest(`${API_URLS.COMPANIES}/my`, { method: 'GET' }, token);
        if (myCompRes?.ok) setCompanies(Array.isArray(myCompRes.data) ? myCompRes.data : [myCompRes.data]);
      }
      if (custRes?.ok) setCustomers(custRes.data.rows || []);
      if (vendRes?.ok) setVendors(vendRes.data.rows || []);
      if (userRes?.ok) setAvailableUsers((userRes.data.rows || []).filter(u => u.status === 'active'));
      if (inspRes?.ok) setDbInspectors(inspRes.data.rows || []);
      if (prodRes?.ok) setProducts(prodRes.data.rows || []);
      if (discRes?.ok) setDisciplines(discRes.data.rows || []);
      if (certRes?.ok) setCertificateOptions(certRes.data.rows || []);
    };
    fetchData();
    const token = localStorage.getItem('token');
    fetchCurrencyRates(token).then(setCurrencyRates);
    fetchCurrencyRateHistory(token).then(setRateHistory);
  }, []);

  // Fetch Finance status — also re-run after a successful Send to Finance
  const fetchFinanceStatus = useCallback(async () => {
    if (!id) return;
    const token = localStorage.getItem('token');
    const res = await apiRequest(`${API_URLS.FINANCE_NOTIFICATIONS}/report-status/${id}`, {}, token);
    if (res?.ok) {
      setFinanceStatus({
        sentToFinance: !!res.data.sentToFinance,
        invoiceExists: !!res.data.invoiceExists,
        hasNewWork: !!res.data.hasNewWork,
        invoices: res.data.invoices || [],
      });
      const locked = {};
      (res.data.inspectorDates || []).forEach(r => {
        if (!r._id) return;
        // Keyed by the inspector row's own instance id, not inspectorId — the
        // same inspector can be added twice (separate engagements) and only
        // the row(s) actually invoiced should lock.
        locked[String(r._id)] = { dates: [...new Set(r.invoicedDates || [])], hasInvoice: !!r.hasInvoice };
      });
      setInvoicedByInspector(locked);
    }
  }, [id]);

  useEffect(() => { fetchFinanceStatus(); }, [fetchFinanceStatus]);

  const hasUploadedReport = deliverables.some(
    d => d.fileUrl || (Array.isArray(d.fileUrls) && d.fileUrls.length > 0)
  );

  const doSendToFinance = async (selectedDates) => {
    setSendingToFinance(true);
    try {
      const token = localStorage.getItem('token');
      const body = { enquiryId: id };
      if (Array.isArray(selectedDates) && selectedDates.length > 0) body.selectedDates = selectedDates;
      const res = await apiRequest(
        `${API_URLS.FINANCE_NOTIFICATIONS}/report-uploaded`,
        { method: 'POST', body },
        token
      );
      if (res?.ok) {
        toast.success('Enquiry sent to Finance for invoicing.');
        setShowSendModal(false);
      } else if (res?.status === 409 || res?.data?.invoiceExists) {
        toast.info(res?.data?.message || 'Finance has already created an invoice for this enquiry.');
        setShowSendModal(false);
      } else {
        toast.info(res?.data?.message || 'Could not send to Finance.');
      }
      await fetchFinanceStatus();
    } finally {
      setSendingToFinance(false);
    }
  };

  // Opens the date-selection popup (falls back to a direct send when there are no date lists)
  const handleSendToFinance = async () => {
    if (isAdd || sendingToFinance) return;
    const token = localStorage.getItem('token');
    const res = await apiRequest(`${API_URLS.FINANCE_NOTIFICATIONS}/report-status/${id}`, {}, token);
    if (!res?.ok) { toast.info(res?.data?.message || 'Could not load inspection dates.'); return; }
    const list = res.data.inspectorDates || [];
    if (list.length === 0) { await doSendToFinance([]); return; }

    // Pre-tick everything that can be billed: report-uploaded dates not yet invoiced,
    // and legacy manual-days rows that are not invoiced yet
    const picked = {};
    list.forEach((ins, idx) => {
      if ((ins.dates || []).length === 0) {
        picked[idx] = !ins.hasInvoice && (ins.reportedDates || []).length > 0 ? ['*'] : [];
      } else {
        const reported = new Set(ins.reportedDates || []);
        const invoiced = new Set(ins.invoicedDates || []);
        picked[idx] = (ins.dates || []).filter(d => reported.has(d) && !invoiced.has(d));
      }
    });
    setSendInspectorDates(list);
    setPickedDates(picked);
    setShowSendModal(true);
  };

  const toggleSendDate = (idx, date) =>
    setPickedDates(prev => {
      const cur = prev[idx] || [];
      return { ...prev, [idx]: cur.includes(date) ? cur.filter(d => d !== date) : [...cur, date] };
    });

  const pickedCount = Object.values(pickedDates).reduce((n, arr) => n + (arr?.length || 0), 0);

  const confirmSendToFinance = () => {
    const payload = sendInspectorDates
      .map((ins, idx) => {
        const picked = pickedDates[idx] || [];
        if (picked.length === 0) return null;
        const isLegacy = (ins.dates || []).length === 0;
        return { inspectorId: ins.inspectorId, inspectorName: ins.inspectorName, dates: isLegacy ? [] : picked };
      })
      .filter(Boolean);
    if (payload.length === 0) { toast.info('Select at least one inspection date.'); return; }
    doSendToFinance(payload);
  };

  const handleDownloadInvoice = async (invoiceId, invoiceNumber) => {
    setDownloadingInvoices(prev => new Set(prev).add(invoiceId));
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URLS.FINANCE_NOTIFICATIONS}/invoice-export/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        toast.info('Could not download invoice.');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice-${invoiceNumber || invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Invoice-${invoiceNumber || invoiceId}.pdf downloaded`);
    } catch {
      toast.info('Could not download invoice.');
    } finally {
      setDownloadingInvoices(prev => { const s = new Set(prev); s.delete(invoiceId); return s; });
    }
  };

  const handlePreviewInvoice = async (invoiceId) => {
    setPreviewingInvoices(prev => new Set(prev).add(invoiceId));
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URLS.FINANCE_NOTIFICATIONS}/invoice-export/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { toast.info('Could not load invoice preview.'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      toast.info('Could not load invoice preview.');
    } finally {
      setPreviewingInvoices(prev => { const s = new Set(prev); s.delete(invoiceId); return s; });
    }
  };

  // Fetch Enquiry Data for Edit Mode
  useEffect(() => {
    if (id) {
      const fetchEnquiry = async () => {
        const token = localStorage.getItem('token');
        const res = await apiRequest(`${API_URLS.ENQUIRIES}/${id}`, { method: 'GET' }, token);
        if (res?.ok) {
          const data = res.data;
          const loadedInspectors = (data.inspectors || []).map((insp, idx) => ({
            ...insp,
            id: insp.id || idx + 1
          }));
          const loadedFormData = {
            ...data,
            enquiryDate: data.enquiryDate ? data.enquiryDate.split('T')[0] : '',
            customerCoordinator: data.customerCoordinator || [],
            selectedProducts: data.selectedProducts || [],
            // Pre-existing rows may not carry this yet — schema default is Manday.
            enquiryType: data.enquiryType === 'MANMONTH' ? 'MANMONTH' : 'MANDAY',
            // Pre-existing rows (lean() skips schema defaults) may not carry this yet —
            // only an explicit false means "not eligible".
            quarterlyIncentiveEligible: data.quarterlyIncentiveEligible !== false,
            // createdBy comes back populated ({ _id, fullName }) for display purposes only —
            // flatten it back to a plain id here so a later save doesn't send the populated
            // object where the schema expects an ObjectId.
            createdBy: data.createdBy?._id || data.createdBy || null,
          };
          const loadedDeliverables = data.deliverables || [];
          const loadedInvoices = data.invoices || [];
          const loadedClientDocuments = data.clientDocuments || [];
          const loadedRemarkLogs = data.remarkLogs || [];
          setFormData(loadedFormData);
          // Pre-existing rows may not carry enquiryType yet (added after they were
          // created) — treat missing as Manday, matching the schema default.
          if (loadedFormData.enquiryType === 'MANMONTH') {
            setSelectedMonths(deriveSelectedMonthsFromDates(loadedFormData.tentativeDates));
          }
          setCoordinatorName(data.createdBy?.fullName || '');
          setInspectors(loadedInspectors);
          setDeliverables(loadedDeliverables);
          setInvoices(loadedInvoices);
          setClientDocuments(loadedClientDocuments);
          setRemarkLogs(loadedRemarkLogs);
          // Baseline for the auto-log-on-save comparison — a save that doesn't touch
          // Remark Status shouldn't add a duplicate row.
          lastLoggedRemarkStatusRef.current = loadedFormData.remarkStatus || null;
          // Re-baseline the dirty-check now that the real saved data has loaded
          initialSnapshotRef.current = JSON.stringify({
            formData: loadedFormData, inspectors: loadedInspectors,
            deliverables: loadedDeliverables, invoices: loadedInvoices,
            clientDocuments: loadedClientDocuments, remarkLogs: loadedRemarkLogs,
          });
          if (loadedInspectors.some(i => i.status === 'CONFIRM' || i.status === 'APPROVED')) {
            setShowConfirmTable(true);
          }
        }
      };
      fetchEnquiry();
    }
  }, [id]);

  // Update Coordinators when Customer changes
  useEffect(() => {
    const cust = customers.find(c => c.id === formData.customerId);
    setCoordinators(cust?.coordinators || []);
  }, [formData.customerId, customers]);

  // Compute total days from tentative dates
  useEffect(() => {
    const days = formData.tentativeDates ? formData.tentativeDates.split(',').filter(d => d.trim()).length : 0;
    setFormData(prev => ({ ...prev, totalDays: days }));
  }, [formData.tentativeDates]);

  // --- 7. Computed Values ---
  const confirmedInspectors = useMemo(() => inspectors.filter(i => i.status === 'CONFIRM' || i.status === 'APPROVED'), [inspectors]);
  // Only APPROVED inspectors can have documents uploaded or invoices raised against them
  const approvedInspectors = useMemo(() => inspectors.filter(i => i.status === 'APPROVED'), [inspectors]);

  // Confirm table's header-level "Incentive" preview — the average margin across
  // every confirmed inspector (not one incentive figure per inspector, which
  // double-counted a single enquiry's incentive once per inspector selected).
  // Same read-only, not-saved margin→rate lookup as incentiveRateFor, plus a
  // hard cutoff: a margin over 95% is treated as a pricing anomaly, not a real
  // incentive-worthy margin, so it always shows ₹0.
  const avgConfirmedMargin = confirmedInspectors.length === 0 ? 0 : confirmedInspectors.reduce((sum, insp) => {
    const offeredINR = parseFloat(insp.offeredINR) || 0;
    const costINR = parseFloat(insp.costINR) || 0;
    return sum + (offeredINR > 0 ? ((offeredINR - costINR) / offeredINR) * 100 : 0);
  }, 0) / confirmedInspectors.length;
  const headerIncentiveRate = avgConfirmedMargin > 95 ? 0 : incentiveRateFor(avgConfirmedMargin);

  // Always render modal from the latest inspector data so table edits flow in
  const liveSelectedInspector = useMemo(() => {
    if (!selectedInspectorForWO) return null;
    return inspectors.find(i =>
      i.id === selectedInspectorForWO.id || String(i._id) === String(selectedInspectorForWO._id)
    ) || selectedInspectorForWO;
  }, [inspectors, selectedInspectorForWO]);
  const allowedDates = useMemo(() => formData.tentativeDates ? formData.tentativeDates.split(',').map(d => d.trim()) : [], [formData.tentativeDates]);

  // Dates currently assigned to at least one inspector in the financials table.
  // These are locked in the Tentative Dates picker — deselecting a tentative date
  // that is already assigned to an inspector would break the financials link, so
  // the user must remove it from the inspector's row first.
  const assignedToInspectorDates = useMemo(() => {
    const set = new Set();
    inspectors.forEach(insp => {
      const dates = Array.isArray(insp.inspectionDates)
        ? insp.inspectionDates
        : (insp.inspectionDates ? [insp.inspectionDates] : []);
      dates.forEach(d => set.add(d));
    });
    return set;
  }, [inspectors]);

  // --- 8. Helper Handlers ---

  const toggleSection = (section) => {
    setCollapsed(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getDisplayName = (inspectorId, inspectorName, currentInspectorLocalId) => {
    let displayName = inspectorName;
    if (!displayName || displayName === inspectorId) {
      const found = dbInspectors.find(i => i.id === inspectorId || i._id === inspectorId);
      if (found) displayName = found.inspectorName;
    }
    if (!displayName) displayName = inspectorId;

    const instances = inspectors.filter(i => i.inspectorId === inspectorId);
    if (instances.length === 0) return displayName;

    const instanceIndex = instances.findIndex(i => i.id === currentInspectorLocalId) + 1;
    return instances.length > 1 ? `${displayName} (${instanceIndex})` : displayName;
  };

  // Work orders belonging to the inspector/agency selected in the Inspection Invoice form
  const workOrderOptions = useMemo(() => {
    if (!currentInvoice.inspectorAgency) return [];
    return inspectors
      .filter(i => i.workOrder && getDisplayName(i.inspectorId, i.inspectorName || i.inspectorId, i.id) === currentInvoice.inspectorAgency)
      .map(i => ({ value: i.workOrder, label: i.workOrder }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspectors, currentInvoice.inspectorAgency, dbInspectors]);

  // The specific inspector row (and its billing) the invoice being added/edited is for —
  // resolved from the selected Work Order, since inspector name alone isn't unique when
  // an inspector has more than one work order. Lets the invoice form edit that inspector's
  // Overtime/Other Costs directly (same billing the Work Order uses), since the final
  // figures are often only known once the inspector's own invoice comes in.
  const invoiceInspectorWO = Array.isArray(currentInvoice.workOrder) ? currentInvoice.workOrder[0] : currentInvoice.workOrder;
  const invoiceInspector = useMemo(() => {
    if (!invoiceInspectorWO) return null;
    return inspectors.find(i => i.workOrder === invoiceInspectorWO) || null;
  }, [inspectors, invoiceInspectorWO]);

  // Every other invoice already on this same work order (excluding the one being edited).
  const otherInvoicesOnWO = useMemo(() => {
    if (!invoiceInspectorWO) return [];
    return invoices.filter(i => {
      if ((i.id ?? i._id) === editingInvoiceId) return false;
      const wo = Array.isArray(i.workOrder) ? i.workOrder : (i.workOrder ? [i.workOrder] : []);
      return wo.includes(invoiceInspectorWO);
    });
  }, [invoices, invoiceInspectorWO, editingInvoiceId]);

  // How many of this work order's planned days are already claimed by other invoices,
  // so the form can show/enforce how many days remain for this one. Derived from
  // attended inspectionDates rather than billing.days, since Manmonth's billing has no
  // days field of its own (it bills by month, prorated per day — see
  // monthlyShareAmount) and it should match Manday's billing.days anyway.
  const invoiceDaysInfo = useMemo(() => {
    if (!invoiceInspector) return null;
    const attended = (invoiceInspector.inspectionDates || []).filter(d => !(invoiceInspector.absentDates || []).includes(d));
    const planned = attended.length;
    const specificDatesUsed = new Set(otherInvoicesOnWO.flatMap(i => Array.isArray(i.datesCovered) ? i.datesCovered : []));
    const legacyCountUsed = otherInvoicesOnWO
      .filter(i => !(Array.isArray(i.datesCovered) && i.datesCovered.length))
      .reduce((s, i) => s + (parseFloat(i.daysCovered) || 0), 0);
    const alreadyUsed = specificDatesUsed.size + legacyCountUsed;
    return { planned, alreadyUsed, remaining: Math.max(0, planned - alreadyUsed) };
  }, [invoiceInspector, otherInvoicesOnWO]);

  // Invoice Date window for non-Finance users (null = no limit): last month on the
  // 1st–5th, else the current month up to today. The server enforces the same rule
  // (backend/utils/invoiceDateWindow.js).
  const invoiceDateWin = useMemo(() => invoiceDateWindow(user?.role), [user?.role]);
  // The Invoice Date the invoice being edited was saved with — left as-is, it isn't
  // re-checked, so an older invoice can still be edited without changing its date.
  const editingInvoiceSavedDate = useMemo(() => {
    if (editingInvoiceId == null) return '';
    const inv = invoices.find(i => (i.id ?? i._id) === editingInvoiceId);
    return inv?.invoiceDate ? String(inv.invoiceDate).slice(0, 10) : '';
  }, [invoices, editingInvoiceId]);

  // Dates this invoice can pick from: planned dates, minus Absent, minus any date
  // another invoice on this work order already claims.
  const invoiceAvailableDates = useMemo(() => {
    if (!invoiceInspector) return [];
    const allDates = Array.isArray(invoiceInspector.inspectionDates) ? invoiceInspector.inspectionDates : [];
    const absent = new Set(Array.isArray(invoiceInspector.absentDates) ? invoiceInspector.absentDates : []);
    const claimedByOthers = new Set(otherInvoicesOnWO.flatMap(i => Array.isArray(i.datesCovered) ? i.datesCovered : []));
    return allDates.filter(d => !absent.has(d) && !claimedByOthers.has(d));
  }, [invoiceInspector, otherInvoicesOnWO]);

  // Date-by-date breakdown of what this invoice is worth — reconciles exactly to what
  // computeInvoiceShare in payableMatching.js computes server-side. Manmonth splits its
  // monthly rate across invoices by day: for each month this invoice's dates touch, the
  // rate is divided by how many days were ORIGINALLY assigned that month (the full
  // plan, absences included — not the calendar month's fixed 28-31 days) and multiplied
  // by however many of those days THIS invoice covers — so an absence genuinely cuts
  // the pay rather than shrinking numerator and denominator together.
  const invoiceBreakdown = useMemo(() => {
    if (!invoiceInspector) return null;
    const billing = invoiceInspector.billing || {};
    const isManmonth = formData.enquiryType === 'MANMONTH';
    const rate = parseFloat((isManmonth ? billing.monthlyRate : billing.rate) || 0);
    const dates = [...currentInvoice.datesCovered].sort();
    const n = dates.length || (parseFloat(currentInvoice.daysCovered) || 0);
    let perMonth = [];
    let subtotal;
    if (isManmonth) {
      // The FULL original assignment for the month (denominator) — not filtered by
      // absence, so absent days still shrink everyone else's share of that month's rate.
      const assignedDates = invoiceInspector.inspectionDates || [];
      const workingDaysByMonth = billing.workingDaysByMonth || {};
      const assignedByMonth = new Map(groupDatesByMonth(assignedDates).map(g => [g.ym, g.count]));
      perMonth = groupDatesByMonth(dates).map(g => {
        const workingDays = parseFloat(workingDaysByMonth[g.ym]);
        const divisor = workingDays > 0 ? workingDays : (assignedByMonth.get(g.ym) || g.count);
        return {
          ...g,
          assignedCount: divisor,
          rate,
          amount: parseFloat((rate > 0 && divisor > 0 ? (rate / divisor) * g.count : 0).toFixed(2)),
        };
      });
      subtotal = dates.length
        ? parseFloat(monthlyShareAmount(dates, assignedDates, rate, workingDaysByMonth).toFixed(2))
        : parseFloat((rate * n / 30).toFixed(2)); // legacy manual day-count, no date list — rough 30-day fallback
    } else {
      subtotal = parseFloat((rate * n).toFixed(2));
    }
    const otTotal = parseFloat(currentInvoice.overtime.reduce((s, ot) => s + overtimeEntryAmount(ot), 0).toFixed(2));
    const otherCosts = parseFloat(otherCostEntriesTotalOf({ otherCostEntries: currentInvoice.otherCostEntries }).toFixed(2));
    const taxBase = subtotal + otTotal;
    const taxEntries = currentInvoice.taxes || [];
    const taxRows = taxEntries.map(t => {
      const pct = parseFloat(t.percentage) || 0;
      return { ...t, amount: parseFloat((taxBase * pct / 100).toFixed(2)) };
    });
    const taxPct = taxRows.length > 0
      ? parseFloat(taxRows.reduce((s, t) => s + (parseFloat(t.percentage) || 0), 0).toFixed(4))
      : (parseFloat(billing.tax) || 0);
    const tax = taxRows.length > 0
      ? parseFloat(taxRows.reduce((s, t) => s + t.amount, 0).toFixed(2))
      : parseFloat((taxBase * (parseFloat(billing.tax) || 0) / 100).toFixed(2));
    const total = parseFloat((subtotal + otTotal + tax + otherCosts).toFixed(2));
    return { rate, taxPct, taxRows, dates, perMonth, isManmonth, n, subtotal, otTotal, otherCosts, tax, total };
  }, [invoiceInspector, currentInvoice.datesCovered, currentInvoice.daysCovered, currentInvoice.taxes, currentInvoice.overtime, currentInvoice.otherCostEntries, formData.enquiryType]);

  // Inspection dates of the inspector selected in the Document Upload form
  const deliverableAllowedDates = useMemo(() => {
    const insp = approvedInspectors.find(i =>
      getDisplayName(i.inspectorId, i.inspectorName || i.inspectorId, i.id) === currentDeliverable.inspectorName
    );
    if (!insp) return [];
    return Array.isArray(insp.inspectionDates) ? insp.inspectionDates
         : (insp.inspectionDates ? [insp.inspectionDates] : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvedInspectors,currentDeliverable.inspectorName, inspectors, dbInspectors]);

  // Invoiced-work lock: once Finance has created an invoice for an inspector,
  // their invoiced dates can't be removed and their pricing can't change
  const invoicedDatesFor = (insp) => invoicedByInspector[String(insp._id)]?.dates || [];
  const isInspectorLocked = (insp) => !!invoicedByInspector[String(insp._id)]?.hasInvoice;

  // Inspector-invoice lock: once the inspector's own invoice file has been
  // uploaded against a work order, only that inspector's Cost/Cost Currency
  // are locked (they should match what was invoiced) — dates and offered
  // pricing remain editable.
  const isInspectorInvoiceUploaded = (insp) => {
    if (!insp.workOrder) return false;
    return invoices.some(inv => inv.fileUrl && (
      Array.isArray(inv.workOrder) ? inv.workOrder.includes(insp.workOrder) : inv.workOrder === insp.workOrder
    ));
  };

  // Billing Details in the Work Order modal locks once either of the above is true —
  // Finance already invoiced this inspector, or the inspector's own invoice was already
  // uploaded against this work order — so pricing can't drift from what was billed/paid.
  const isWOBillingLocked = (insp) => isInspectorLocked(insp) || isInspectorInvoiceUploaded(insp);

  const generateNextWONumber = () => {
    if (!formData.jobNumber) return '';
    const existingWOs = inspectors
      .map(i => i.workOrder)
      .filter(wo => wo && wo.startsWith(formData.jobNumber));

    let maxSeq = 0;
    existingWOs.forEach(wo => {
      const parts = wo.split('/');
      const seqStr = parts[parts.length - 1];
      const seq = parseInt(seqStr, 10);
      if (!isNaN(seq) && seq > maxSeq) maxSeq = seq;
    });

    return `${formData.jobNumber}/${(maxSeq + 1).toString().padStart(3, '0')}`;
  };

  // --- 9. Form & Input Handlers ---

  const handleInputChange = (e, manualValue) => {
    if (e && e.target) {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));
    } else {
      setFormData(prev => ({ ...prev, [e]: manualValue }));
    }
  };

  const handleInspectorFieldChange = (id, field, value) => {
    setInspectors(prev => prev.map(insp =>
      (insp.id === id || String(insp._id) === String(id)) ? { ...insp, [field]: value } : insp
    ));
  };

  // --- Work Order ↔ Inspector Confirm Table sync helpers ---
  const isModalForInspector = (id) =>
    showGenerateWorkOrderModal && selectedInspectorForWO &&
    (selectedInspectorForWO.id === id || String(selectedInspectorForWO._id) === String(id));

  // Modal Rate is in INR and mirrors the inspector's costINR. Tax applies to the
  // Amount only (Overtime is per-invoice only). Other Costs is a flat work-order
  // figure, added after tax. Manday: Amount = days × rate. Manmonth: Amount = one
  // monthlyRate per distinct month the inspector is assigned to (this is the work
  // order's own FULL assignment total — splitting a month's rate across multiple
  // invoices happens separately, at invoice time; see monthlyShareAmount/
  // invoiceBreakdown below).
  const syncCostToModal = (costINR) => {
    const r = parseFloat(costINR) || 0;
    const isManmonth = formData.enquiryType === 'MANMONTH';
    const inspectionDates = selectedInspectorForWO?.inspectionDates || [];
    const attendedDates = inspectionDates.filter(d => !(selectedInspectorForWO?.absentDates || []).includes(d));
    setWorkOrderForm(prev => {
      const amount = isManmonth
        ? parseFloat(monthlyShareAmount(attendedDates, inspectionDates, r, prev.billing.workingDaysByMonth).toFixed(2))
        : prev.billing.days * r;
      const ttax = parseFloat((amount * prev.billing.tax / 100).toFixed(2));
      const total = parseFloat((amount + ttax + prev.billing.otherCosts).toFixed(2));
      return isManmonth
        ? { ...prev, billing: { ...prev.billing, monthlyRate: r, amount, total } }
        : { ...prev, billing: { ...prev.billing, rate: r, amount, total } };
    });
  };

  // Recompute billing.total from the current amount/tax/otherCosts — used by the
  // Days/Rate (or Months/Monthly Rate)/Tax Rate/Other Costs inputs in Billing Details
  // above after they change.
  const recomputeWOTotal = (billing) => {
    const ttax = parseFloat((billing.amount * billing.tax / 100).toFixed(2));
    return parseFloat((billing.amount + ttax + billing.otherCosts).toFixed(2));
  };

  // billing.days/amount/total for a given day-count (Manday) — factored out so the
  // Absent Dates picker below can shrink billing the same way the manual Days input
  // does.
  const computeBillingForDays = (billing, days) => {
    const rate = parseFloat(billing?.rate) || 0;
    const next = { ...billing, days, amount: days * rate };
    return { ...next, total: recomputeWOTotal(next) };
  };

  // billing.months/amount/total for a work order's full assignment (Manmonth) —
  // factored out so the Absent Dates picker below can shrink billing the same way the
  // auto-recompute does. `assignedDates` is the FULL set of dates originally assigned
  // that month (including absences) — the denominator; `attendedDates` (a subset,
  // absences removed) is what's actually paid — the numerator. So an inspector
  // assigned all 30 days of September who is absent for 3 is paid 27/30 of that
  // month's rate, not the full monthlyRate — the absence genuinely cuts the pay,
  // rather than cancelling out.
  const computeBillingForMonths = (billing, assignedDates, attendedDates) => {
    const rate = parseFloat(billing?.monthlyRate) || 0;
    const months = countDistinctMonths(attendedDates);
    const amount = parseFloat(monthlyShareAmount(attendedDates, assignedDates, rate, billing?.workingDaysByMonth).toFixed(2));
    const next = { ...billing, months, amount };
    return { ...next, total: recomputeWOTotal(next) };
  };

  // Updates one calendar month's "working days" figure in a Manmonth work order's
  // billing and recomputes amount/total from it — same shape as the Months/Monthly
  // Rate/Tax Rate handlers above, just keyed by month instead of a single value.
  const handleWorkingDaysChange = (ym, value) => {
    if (!selectedInspectorForWO) return;
    const wd = value === '' ? '' : (parseFloat(value) || 0);
    setWorkOrderForm(prev => {
      const nextWorkingDaysByMonth = { ...(prev.billing.workingDaysByMonth || {}), [ym]: wd };
      const inspectionDates = selectedInspectorForWO?.inspectionDates || [];
      const attendedDates = inspectionDates.filter(d => !(selectedInspectorForWO?.absentDates || []).includes(d));
      const rate = parseFloat(prev.billing.monthlyRate) || 0;
      const amount = parseFloat(monthlyShareAmount(attendedDates, inspectionDates, rate, nextWorkingDaysByMonth).toFixed(2));
      const billing = { ...prev.billing, workingDaysByMonth: nextWorkingDaysByMonth, amount };
      return { ...prev, billing: { ...billing, total: recomputeWOTotal(billing) } };
    });
  };

  // Applies an Inspection Dates and/or Absent Dates edit for one inspector: keeps
  // absentDates a subset of inspectionDates, recomputes billing as planned-minus-absent
  // (Manday: days; Manmonth: re-shares the monthly rate over the dates actually
  // attended), and writes the updated billing straight onto the inspector row once a
  // work order exists for them, so a 4-day work order with 1 day absent is billed for
  // 3 immediately.
  const applyAttendanceChange = (insp, nextInspectionDates, nextAbsentDates) => {
    const cleanAbsent = (nextAbsentDates || []).filter(d => nextInspectionDates.includes(d));
    const days = Math.max(0, nextInspectionDates.length - cleanAbsent.length);
    const attendedDates = nextInspectionDates.filter(d => !cleanAbsent.includes(d));
    const hasBilling = !!(insp.workOrder && insp.billing);
    const nextBilling = hasBilling
      ? (formData.enquiryType === 'MANMONTH' ? computeBillingForMonths(insp.billing, nextInspectionDates, attendedDates) : computeBillingForDays(insp.billing, days))
      : insp.billing;
    // Expected Start/End bound the full planned window, not the attended subset — an
    // absence in the middle of a date range shouldn't shrink the range itself.
    const sorted = [...nextInspectionDates].sort();
    const expectedStartDate = sorted[0] || '';
    const expectedEndDate = sorted[sorted.length - 1] || '';

    setInspectors(prev => prev.map(i =>
      i.id === insp.id
        ? {
            ...i,
            inspectionDates: nextInspectionDates,
            absentDates: cleanAbsent,
            numberOfDays: days,
            ...(hasBilling ? { expectedStartDate, expectedEndDate } : {}),
            ...(hasBilling ? { billing: nextBilling } : {}),
          }
        : i
    ));

    if (hasBilling && isModalForInspector(insp.id)) {
      setWorkOrderForm(prev => ({ ...prev, expectedStartDate, expectedEndDate, billing: nextBilling }));
    }
  };

  // Overtime/Other Costs entered from the Add Invoice form — specific to THIS invoice
  // (currentInvoice.overtime/otherCostEntries), not the shared Work Order billing.
  const handleAddInvoiceOvertime = () => {
    setCurrentInvoice(prev => ({ ...prev, overtime: [...prev.overtime, { dates: [], hours: '', rate: '', totalAmount: 0 }] }));
  };

  const handleInvoiceOvertimeChange = (idx, updates) => {
    setCurrentInvoice(prev => ({
      ...prev,
      overtime: prev.overtime.map((entry, i) => {
        if (i !== idx) return entry;
        const updated = { ...entry, ...updates };
        return { ...updated, totalAmount: +overtimeEntryAmount(updated).toFixed(2) };
      }),
    }));
  };

  const handleRemoveInvoiceOvertime = (idx) => {
    setCurrentInvoice(prev => ({ ...prev, overtime: prev.overtime.filter((_, i) => i !== idx) }));
  };

  const handleAddInvoiceOtherCost = () => {
    setCurrentInvoice(prev => ({ ...prev, otherCostEntries: [...prev.otherCostEntries, { description: '', amount: '' }] }));
  };

  const handleInvoiceOtherCostChange = (idx, field, value) => {
    setCurrentInvoice(prev => ({
      ...prev,
      otherCostEntries: prev.otherCostEntries.map((entry, i) => i === idx ? { ...entry, [field]: value } : entry),
    }));
  };

  const handleRemoveInvoiceOtherCost = (idx) => {
    setCurrentInvoice(prev => ({ ...prev, otherCostEntries: prev.otherCostEntries.filter((_, i) => i !== idx) }));
  };

  // --- 10. Logic Handlers (Add/Delete/Save) ---

  const deleteInspector = (id) => {
    setInspectors(prev => prev.filter(i => i.id !== id && String(i._id) !== String(id)));
  };

  const suggestInspector = () => {
    setSuggestions(dbInspectors);
    const vendor = vendors.find(v => v.id === formData.manufacturerVendor);
    if (vendor) {
      setInspFilterCountry(vendor.country || '');
      setInspFilterState(vendor.state || '');
      setInspFilterCity(vendor.city || '');
    } else {
      setInspFilterCountry('');
      setInspFilterState('');
      setInspFilterCity('');
    }
    loadInspectorHistory();
  };

  // Fetches each inspector's past jobs with this enquiry's company (current job excluded)
  // for the Work History filters/columns. Non-superadmins are scoped to their own company server-side.
  const loadInspectorHistory = async () => {
    const params = new URLSearchParams();
    const companyId = formData.company?._id || formData.company;
    if (companyId) params.set('company', companyId);
    if (formData.customerId) params.set('customerId', formData.customerId);
    if (id) params.set('excludeEnquiryId', id);
    const res = await apiRequest(`${API_URLS.INSPECTORS}/work-history?${params}`, { method: 'GET' }, localStorage.getItem('token'));
    setInspHistory(res?.ok ? (res.data.data || {}) : {});
  };

  // Next free local row id — `offset` lets a bulk-add loop reserve several ids in a row
  // without colliding (inspectors state doesn't change between iterations of a single loop).
  const nextInspectorLocalId = (offset = 0) => {
    const maxId = inspectors.length > 0 ? Math.max(...inspectors.map(i => i.id)) : 0;
    return maxId + 1 + offset;
  };

  // Builds a new inspectors[] row from a suggestion (dbInspectors entry) — shared by the
  // invite-send flow below, `extra` layers on invite-specific fields (mailSent/mailSentAt).
  const buildRowFromSuggestion = (s, extra = {}, idOverride) => ({
    id: idOverride ?? nextInspectorLocalId(), inspectorId: s.id, inspectorName: s.inspectorName, inspectionDates: [], absentDates: [],
    numberOfDays: 0, contactNumber: s.phone || '', email: s.email || '', products: '',
    status: 'APPROACHED', selected: false, currencyCost: 'INR', cost: '', costINR: '',
    currencyOffered: 'INR', offered: '', offeredINR: '', margin: '', workOrder: '',
    workOrderDate: new Date().toISOString().split('T')[0],
    mailSent: false, mailSentAt: null,
    ...extra,
  });

  // Direct add — no invitation email, no attachments; just puts the suggestion straight
  // onto the job like the original "Select" button used to. Kept alongside Invite so a
  // coordinator can skip emailing when the inspector's already confirmed some other way.
  const addInspectorFromSuggestion = (s) => {
    setInspectors(prev => [...prev, buildRowFromSuggestion(s)]);
    setShowSuggestionModal(false);
    setShowConfirmTable(true);
  };

  // Bulk counterpart of the above, for the "N selected" bar.
  const addSelectedInspectors = () => {
    const targets = suggestions.filter(s => selectedSuggestionIds.has(s.id));
    if (targets.length === 0) return;
    setInspectors(prev => [...prev, ...targets.map((t, idx) => buildRowFromSuggestion(t, {}, nextInspectorLocalId(idx)))]);
    setSelectedSuggestionIds(new Set());
    setShowSuggestionModal(false);
    setShowConfirmTable(true);
    toast.success(`Added ${targets.length} inspector${targets.length > 1 ? 's' : ''}`);
  };

  // Prefilled subject/message for the invite-preview modal, built from the enquiry's own
  // context (customer, products, tentative dates, location). `greetingName` personalizes
  // the opening line for a single invite; omitted for a bulk send addressed to several.
  const buildInviteDefaults = (greetingName) => {
    const productNames = (formData.selectedProducts || [])
      .map(pid => products.find(p => p.id === pid || String(p._id) === String(pid))?.name)
      .filter(Boolean).join(', ');
    const vendor = vendors.find(v => v.id === formData.manufacturerVendor);
    const vendorName = vendor?.vendorName || vendor?.companyName || 'TBD';
    const fullAddress = vendor?.address || 'TBD';
    const dateList = formData.tentativeDates ? formData.tentativeDates.split(',').map(d => d.trim()).filter(Boolean) : [];
    const inspectionDateStr = dateList.length > 0 ? dateList.map(formatSubjectDate).join(' & ') : 'TBD';
    const messageDateStr = dateList.length > 0 ? dateList.map(formatMessageDate).join(' & ') : 'TBD';
    const jobNumber = formData.jobNumber || formData._id || 'TBD';
    const enquiryCompany = companies.find(c => c.id === formData.company || String(c._id) === String(formData.company));
    const companyName = enquiryCompany?.companyName || 'APS Consultancy Private Limited';
    // Subject/message format comes from the enquiry's company (Company Management →
    // Inspector Invitation Mail Format), falling back to the default format.
    const template = resolveInviteTemplate(enquiryCompany);
    const values = {
      inspectorName: greetingName || '',
      vendorName,
      location: fullAddress,
      products: productNames || 'TBD',
      subjectDates: inspectionDateStr,
      dates: messageDateStr,
      duration: formData.totalDays ? `${formData.totalDays} day(s)` : 'TBD',
      jobNumber,
      clientRequirement: formData.customerRequirement || 'TBD',
      companyName,
    };
    return {
      subject: fillInviteTemplate(template.subject, values),
      message: fillInviteTemplate(template.message, values),
    };
  };

  // Opens the invite-preview modal — mode 'new' for a suggestion not yet in the
  // inspectors table, 'resend' for an already-invited row (re-sends the same mail).
  const openInviteModal = (target, mode = 'new') => {
    setInviteMode(mode);
    setInviteTarget(target);
    setInviteForm(buildInviteDefaults(target.inspectorName));
    setInviteAttachments([]);
    setInviteModalDragged(false);
    setShowInviteModal(true);
  };

  // Opens the invite-preview modal for several selected suggestions at once — one shared
  // subject/message, sent individually to each on Send Invitation.
  const openBulkInviteModal = (targets) => {
    setInviteMode('bulk');
    setBulkInviteTargets(targets);
    setInviteForm(buildInviteDefaults());
    setInviteAttachments([]);
    setInviteModalDragged(false);
    setShowInviteModal(true);
  };

  // Graph's sendMail accepts attachments inline as base64 — kept small on purpose.
  // Mirrors the 3 MB cap enforced server-side in uploadInviteAttachmentsMiddleware.
  const INVITE_ATTACHMENTS_MAX_BYTES = 20 * 1024 * 1024;
  const inviteAttachmentsBytes = inviteAttachments.reduce((sum, f) => sum + f.size, 0);

  const handleSendInvite = async () => {
    if (!inviteForm.subject.trim() || !inviteForm.message.trim()) { toast.warning('Subject and message are required.'); return; }
    if (user?.role === 'global_superadmin' && !formData.company) { toast.info('Please select a company before inviting inspectors.'); return; }
    if (!formData.enquiryDate) { toast.info('Please select an enquiry date before inviting inspectors.'); return; }
    if (inviteAttachmentsBytes > INVITE_ATTACHMENTS_MAX_BYTES) { toast.warning('Attachments exceed the 20 MB total limit.'); return; }

    const token = localStorage.getItem('token');
    const sendOne = (inspectorId) => {
      const fd = new FormData();
      fd.append('inspectorId', inspectorId);
      fd.append('subject', inviteForm.subject);
      fd.append('message', inviteForm.message);
      inviteAttachments.forEach(f => fd.append('attachments', f));
      return apiRequest(`${API_URLS.ENQUIRIES}/notify-inspector`, { method: 'POST', body: fd, isFormData: true }, token);
    };

    if (inviteMode === 'bulk') {
      const targets = bulkInviteTargets.filter(t => t.email);
      if (targets.length === 0) { toast.error('None of the selected inspectors have an email on file.'); return; }

      setSendingInvite(true);
      const results = await Promise.allSettled(targets.map(t => sendOne(t.id)));
      const mailSentAt = new Date().toISOString();
      const succeeded = targets.filter((_, idx) => results[idx].status === 'fulfilled' && results[idx].value?.ok);
      const failed = targets.filter((_, idx) => !(results[idx].status === 'fulfilled' && results[idx].value?.ok));

      if (succeeded.length > 0) {
        const newRows = succeeded.map((t, idx) => buildRowFromSuggestion(t, { mailSent: true, mailSentAt }, nextInspectorLocalId(idx)));
        const nextInspectors = [...inspectors, ...newRows];
        setInspectors(nextInspectors);
        setSelectedSuggestionIds(new Set());
        setShowSuggestionModal(false);
        setShowConfirmTable(true);
        setShowInviteModal(false);
        toast.success(failed.length === 0
          ? `Invitation sent to ${succeeded.length} inspector${succeeded.length > 1 ? 's' : ''}`
          : `Sent to ${succeeded.length}, failed for ${failed.length} (${failed.map(f => f.inspectorName).join(', ')})`);
        await saveEnquiry(nextInspectors);
      } else {
        toast.error('Failed to send any invitations.');
      }
      setSendingInvite(false);
      return;
    }

    if (!inviteTarget?.email) { toast.error('This inspector has no email on file.'); return; }
    setSendingInvite(true);
    const inspectorId = inviteMode === 'resend' ? inviteTarget.inspectorId : inviteTarget.id;
    const res = await sendOne(inspectorId);

    if (res?.ok) {
      const mailSentAt = new Date().toISOString();
      const nextInspectors = inviteMode === 'resend'
        ? inspectors.map(i => i.id === inviteTarget.id ? { ...i, mailSent: true, mailSentAt } : i)
        : [...inspectors, buildRowFromSuggestion(inviteTarget, { mailSent: true, mailSentAt })];
      setInspectors(nextInspectors);
      if (inviteMode === 'new') { setShowSuggestionModal(false); setShowConfirmTable(true); }
      setShowInviteModal(false);
      toast.success(`Invitation sent to ${inviteTarget.inspectorName}`);
      await saveEnquiry(nextInspectors);
    } else {
      toast.error(res?.data?.message || 'Failed to send invitation email.');
    }
    setSendingInvite(false);
  };

  // Add Coordinator Handler
  const handleAddCoordinator = async (e) => {
    if (e) e.preventDefault();
    if (!addCoordForm.customerId) { toast.info('Please select a customer.'); return; }
    if (!addCoordForm.name) { toast.warning('Coordinator name is required.'); return; }
    if (!addCoordForm.email) { toast.warning('Coordinator email is required.'); return; }
    setSavingCoord(true);
    const cust = customers.find(c => c.id === addCoordForm.customerId);
    if (!cust) { toast.error('Customer not found.'); setSavingCoord(false); return; }
    const updatedCoordinators = [
      ...(cust.coordinators || []),
      { name: addCoordForm.name, email: addCoordForm.email, mobileNumber: addCoordForm.mobileNumber },
    ];
    const token = localStorage.getItem('token');
    const res = await apiRequest(`${API_URLS.CUSTOMERS}/${addCoordForm.customerId}`, {
      method: 'PUT',
      body: { coordinators: updatedCoordinators },
    }, token);
    setSavingCoord(false);
    if (res?.ok) {
      // Refresh customers so new coordinator appears in dropdown
      const custRes = await apiRequest(`${API_URLS.CUSTOMERS}?limit=1000`, { method: 'GET' }, token);
      if (custRes?.ok) setCustomers(custRes.data.rows || []);
      setShowAddCoordModal(false);
      setAddCoordForm({ customerId: '', name: '', email: '', mobileNumber: '' });
      toast.success('Coordinator added successfully!');
    } else {
      toast.error(res?.data?.message || 'Failed to add coordinator.');
    }
  };

  // Vendor / Product Handlers
  const handleAddVendor = async (e) => {
    if (e) e.preventDefault();
    if (!newVendorForm.vendorName || !newVendorForm.country || !newVendorForm.address) return toast.warning('Vendor Name, Country and Address are required!');
    setSavingVendor(true);
    const token = localStorage.getItem('token');
    const result = await apiRequest(API_URLS.VENDORS, { method: 'POST', body: newVendorForm }, token);
    setSavingVendor(false);
    if (result?.ok) {
      toast.success('Vendor added successfully!');
      const addedId = result.data.id || String(result.data._id || '');
      const vendRes = await apiRequest(`${API_URLS.VENDORS}?limit=1000`, { method: 'GET' }, token);
      if (vendRes?.ok) {
        const updatedVendors = vendRes.data.rows || [];
        setVendors(updatedVendors);
        const found = updatedVendors.find(v => v.id === addedId || String(v._id) === addedId);
        setFormData(prev => ({ ...prev, manufacturerVendor: found ? found.id : addedId }));
      } else {
        const newVendor = { ...result.data, id: addedId };
        setVendors(prev => [...prev, newVendor]);
        setFormData(prev => ({ ...prev, manufacturerVendor: addedId }));
      }
      setShowAddVendorModal(false);
      setNewVendorForm({ vendorName: '', country: '', state: '', city: '', address: '' });
    } else toast.error(result?.data?.message || 'Failed to add vendor');
  };

  const handleFetchSubDisciplines = async (discId) => {
    if (!discId) return setSubDisciplines([]);
    const token = localStorage.getItem('token');
    const res = await apiRequest(`${API_URLS.SUBDISCIPLINES}?disciplineId=${discId}&limit=1000`, {}, token);
    if (res?.ok) setSubDisciplines(res.data.rows || []);
  };

  const handleAddProduct = async (e) => {
    if (e) e.preventDefault();
    if (!newProductForm.name || !newProductForm.disciplineId || !newProductForm.subDisciplineId) return toast.warning('All fields are required!');
    setSavingProduct(true);
    const token = localStorage.getItem('token');
    const result = await apiRequest(API_URLS.PRODUCTS, { method: 'POST', body: newProductForm }, token);
    setSavingProduct(false);
    if (result?.ok) {
      toast.success('Product added successfully!');
      const p = result.data.product;
      const newEntry = { id: p._id, name: p.name };
      setProducts(prev => [...prev, newEntry]);
      setFormData(prev => ({ ...prev, selectedProducts: [...prev.selectedProducts, p._id] }));
      setShowAddProductModal(false);
      setNewProductForm({ name: '', disciplineId: '', subDisciplineId: '' });
      setSubDisciplines([]);
    } else toast.error(result?.data?.message || 'Failed to add product');
  };

  // Inspector Modal Handlers
  const handleSaveNewInspector = async (e) => {
    if (e) e.preventDefault();
    if (!newInspForm.inspectorName || !newInspForm.email || !newInspForm.country) return toast.warning("Name, Email and Country are required!");
    setSavingInspector(true);
    try {
      const token = localStorage.getItem('token');
      const submitData = new FormData();
      // Backend matches certFiles[i] to certs[i] by index — put certs with files first.
      const certsWithFile = (newInspForm.certificates || []).filter((c) => c.certFile);
      const certsWithoutFile = (newInspForm.certificates || []).filter((c) => !c.certFile);
      const orderedCerts = [...certsWithFile, ...certsWithoutFile].map(({ certFile, ...rest }) => rest);
      const payload = { ...newInspForm, certificates: orderedCerts };
      delete payload.profileImageFile;
      delete payload.cvFile;
      submitData.append("data", JSON.stringify(payload));
      if (newInspForm.profileImageFile) submitData.append("profileImage", newInspForm.profileImageFile);
      if (newInspForm.cvFile) submitData.append("cv", newInspForm.cvFile);
      certsWithFile.forEach((c) => submitData.append("certFiles", c.certFile));
      const result = await apiRequest(API_URLS.INSPECTORS, { method: "POST", body: submitData, isFormData: true }, token);
      if (result?.ok) {
        toast.success("Inspector added successfully!");
        const inspRes = await apiRequest(`${API_URLS.INSPECTORS}?all=true`, { method: 'GET' }, token);
        if (inspRes?.ok) setDbInspectors(inspRes.data.rows || []);
        else setDbInspectors(prev => [...prev, result.data.inspector]);
        setShowAddInspectorModal(false);
        setNewInspForm({
          inspectorName: "", email: "", phone: "", cvUrl: "", cvFile: null, inspectorSource: "Direct",
          age: "", experience: "", degree: "", branch: "", country: "", state: "", city: "",
          competency: {}, certificates: [], clientApprovals: { clients: [], otherClients: [] },
          bankIndia: { beneficiaryName: "", bankName: "", branch: "", bankAddress: "", accountNo: "", ifscCode: "" },
          bankOther: { beneficiaryName: "", beneficiaryAddress: "", bankName: "", bankAddress: "", ibanNo: "", swiftCode: "" },
          contactPersons: [], profileImageUrl: "", profileImageFile: null,
        });
      } else toast.error(result?.data?.message || "Failed to add inspector");
    } catch (error) {
      console.error('Save inspector error:', error);
      toast.error('An error occurred while saving the inspector.');
    } finally { setSavingInspector(false); }
  };

  // Unsaved-changes guards — shown when the user clicks the X on a quick-add modal
  // after typing something, so data isn't silently lost.
  const isCoordFormDirty = () => !!(addCoordForm.name || addCoordForm.email || addCoordForm.mobileNumber);
  const isVendorFormDirty = () => !!(newVendorForm.vendorName || newVendorForm.country || newVendorForm.state || newVendorForm.city || newVendorForm.address);
  const isInspectorFormDirty = () => !!(
    newInspForm.inspectorName || newInspForm.email || newInspForm.phone || newInspForm.country ||
    (newInspForm.certificates && newInspForm.certificates.length > 0)
  );

  const requestCloseCoordModal = () => {
    if (isCoordFormDirty()) {
      toast.confirm(
        'You have entered coordinator details. Do you want to save before closing?',
        () => handleAddCoordinator(),
        () => { setAddCoordForm({ customerId: '', name: '', email: '', mobileNumber: '' }); setShowAddCoordModal(false); }
      );
    } else {
      setShowAddCoordModal(false);
    }
  };

  const requestCloseVendorModal = () => {
    if (isVendorFormDirty()) {
      toast.confirm(
        'You have entered vendor details. Do you want to save before closing?',
        () => handleAddVendor(),
        () => { setNewVendorForm({ vendorName: '', country: '', state: '', city: '', address: '' }); setShowAddVendorModal(false); }
      );
    } else {
      setShowAddVendorModal(false);
    }
  };

  const requestCloseInspectorModal = () => {
    if (isInspectorFormDirty()) {
      toast.confirm(
        'You have entered inspector details. Do you want to save before closing?',
        () => handleSaveNewInspector(),
        () => {
          setNewInspForm({
            inspectorName: "", email: "", phone: "", cvUrl: "", cvFile: null, inspectorSource: "Direct",
            age: "", experience: "", degree: "", branch: "", country: "", state: "", city: "",
            competency: {}, certificates: [], clientApprovals: { clients: [], otherClients: [] },
            bankIndia: { beneficiaryName: "", bankName: "", branch: "", bankAddress: "", accountNo: "", ifscCode: "" },
            bankOther: { beneficiaryName: "", beneficiaryAddress: "", bankName: "", bankAddress: "", ibanNo: "", swiftCode: "" },
            contactPersons: [], profileImageUrl: "", profileImageFile: null,
          });
          setShowAddInspectorModal(false);
        }
      );
    } else {
      setShowAddInspectorModal(false);
    }
  };

  const handleAddCert = () => {
    if (!certInput.certificateId) return;
    setNewInspForm(f => ({ ...f, certificates: [...f.certificates, { ...certInput }] }));
    setCertInput({ certificateId: "", expiryDate: "", fileUrl: "", certFile: null });
    setCertFormOpen(false);
  };

  // Deliverables & Invoices Handlers
  const handleDeliverableChange = (field, value) => setCurrentDeliverable(prev => ({ ...prev, [field]: value }));

  const startEditDeliverable = (d) => {
    setEditingDeliverableId(d.id ?? d._id);
    setCurrentDeliverable({
      inspectorName: d.inspectorName || '',
      documentType: d.documentType || '',
      number: d.number || '',
      date: Array.isArray(d.date) ? d.date : (d.date ? [d.date] : []),
      file: null,
    });
  };

  const cancelEditDeliverable = () => {
    setEditingDeliverableId(null);
    setCurrentDeliverable({ inspectorName: '', documentType: '', number: '', date: [], file: null });
    if (addFileRef.current) addFileRef.current.value = '';
  };

  const addDeliverable = async () => {
    if (!currentDeliverable.documentType) return toast.info('Please select document type.');

    const isEditing = editingDeliverableId != null;
    const updatedDeliverables = isEditing
      ? deliverables.map(d => (d.id ?? d._id) === editingDeliverableId ? { ...d, ...currentDeliverable, file: undefined } : d)
      : [...deliverables, { ...currentDeliverable, id: deliverables.length > 0 ? Math.max(...deliverables.map(i => i.id || 0)) + 1 : 1, file: undefined }];
    const fileToUpload = isEditing ? null : currentDeliverable.file;

    setDeliverables(updatedDeliverables);
    setCurrentDeliverable({ inspectorName: '', documentType: '', number: '', date: [], file: null });
    setEditingDeliverableId(null);
    if (addFileRef.current) addFileRef.current.value = '';

    if (!id) return;

    setAddingDeliverable(true);
    try {
      const token = localStorage.getItem('token');
      const deliverablesToSend = updatedDeliverables.map(({ id: _localId, file: _f, ...rest }) => rest);

      const res = await apiRequest(
        `${API_URLS.ENQUIRIES}/${id}`,
        { method: 'PUT', body: { deliverables: deliverablesToSend } },
        token
      );
      if (!res?.ok) {
        toast.info(res?.data?.message || 'Row added but could not save.');
        return;
      }

      const freshDeliverables = res.data?.enquiry?.deliverables || [];
      setDeliverables(freshDeliverables);

      if (fileToUpload) {
        const oldIds = new Set(deliverables.map(d => d._id).filter(Boolean));
        const savedRow = freshDeliverables.find(d => d._id && !oldIds.has(d._id));

        if (savedRow?._id) {
          const rowKey = `d_${savedRow._id}`;
          setUploadingRows(prev => ({ ...prev, [rowKey]: true }));
          setUploadProgress(prev => ({ ...prev, [rowKey]: 0 }));
          const fd = new FormData();
          fd.append('file', fileToUpload);
          const uploadRes = await uploadWithProgress(
            `${API_URLS.ENQUIRIES}/${id}/deliverables/${savedRow._id}/upload`, fd, token, rowKey
          );
          if (uploadRes?.ok) {
            setUploadProgress(prev => ({ ...prev, [rowKey]: 100 }));
            setTimeout(() => { setUploadingRows(prev => ({ ...prev, [rowKey]: false })); setUploadProgress(prev => { const n = { ...prev }; delete n[rowKey]; return n; }); }, 800);
            const { fileUrl } = uploadRes.data;
            setDeliverables(prev => prev.map(d => d._id === savedRow._id ? { ...d, fileUrl } : d));
          } else {
            setUploadingRows(prev => ({ ...prev, [rowKey]: false }));
            setUploadProgress(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
            toast.info(uploadRes?.data?.message || 'Row added but file upload failed.');
          }
        }
      }
    } finally {
      setAddingDeliverable(false);
    }
  };

  const deleteDeliverable = (rowId) => {
    toast.deleteConfirm('Delete this deliverable?', async () => {
      const updatedDeliverables = deliverables.filter(i => i.id !== rowId && i._id !== rowId);
      setDeliverables(updatedDeliverables);
      if (editingDeliverableId === rowId) cancelEditDeliverable();

      if (!id) return;

      const token = localStorage.getItem('token');
      const deliverablesToSend = updatedDeliverables.map(({ id: _localId, ...rest }) => rest);
      const res = await apiRequest(
        `${API_URLS.ENQUIRIES}/${id}`,
        { method: 'PUT', body: { deliverables: deliverablesToSend } },
        token
      );
      if (res?.ok) {
        setDeliverables(res.data?.enquiry?.deliverables || updatedDeliverables);
      } else {
        toast.error(res?.data?.message || 'Delete failed. Please save the enquiry manually.');
      }
    });
  };

  const handleInvoiceChange = (field, value) => setCurrentInvoice(prev => ({ ...prev, [field]: value }));

  const handleAddInvoiceTax = () => setCurrentInvoice(prev => ({ ...prev, taxes: [...prev.taxes, { type: '', percentage: '' }] }));
  const handleInvoiceTaxChange = (idx, field, value) => setCurrentInvoice(prev => ({ ...prev, taxes: prev.taxes.map((t, i) => i === idx ? { ...t, [field]: value } : t) }));
  const handleRemoveInvoiceTax = (idx) => setCurrentInvoice(prev => ({ ...prev, taxes: prev.taxes.filter((_, i) => i !== idx) }));

  const startEditInvoice = (inv) => {
    setEditingInvoiceId(inv.id ?? inv._id);
    setCurrentInvoice({
      inspectorAgency: inv.inspectorAgency || '',
      workOrder: Array.isArray(inv.workOrder) ? inv.workOrder : (inv.workOrder ? [inv.workOrder] : []),
      invoiceNumber: inv.invoiceNumber || '',
      invoiceDate: inv.invoiceDate ? inv.invoiceDate.slice(0, 10) : '',
      datesCovered: Array.isArray(inv.datesCovered) ? inv.datesCovered : [],
      daysCovered: inv.daysCovered || '',
      taxes: Array.isArray(inv.taxes) ? inv.taxes : [],
      overtime: Array.isArray(inv.overtime) ? inv.overtime : [],
      otherCostEntries: Array.isArray(inv.otherCostEntries) ? inv.otherCostEntries : [],
      file: null,
    });
    setShowInvoiceFormModal(true);
  };

  const cancelEditInvoice = () => {
    setEditingInvoiceId(null);
    setShowInvoiceFormModal(false);
    setCurrentInvoice({ inspectorAgency: '', workOrder: [], invoiceNumber: '', invoiceDate: '', datesCovered: [], daysCovered: '', taxes: [], overtime: [], otherCostEntries: [], file: null });
    if (addInvoiceFileRef.current) addInvoiceFileRef.current.value = '';
  };

  const addInvoice = async () => {
    if (!currentInvoice.invoiceNumber) return toast.info('Please enter invoice number.');

    // Invoice Date window (non-Finance) — skipped when editing without changing the date.
    const invDate = String(currentInvoice.invoiceDate || '').slice(0, 10);
    if (invoiceDateWin && invDate && invDate !== editingInvoiceSavedDate && !isDateInWindow(invDate, invoiceDateWin)) {
      return toast.info(`${invoiceDateWin.hint}`);
    }

    // Client-side check — the server independently rejects this too
    // (applyInvoiceDaySplit), but catching it here avoids a round trip.
    const datesCoveredArr = currentInvoice.datesCovered || [];
    const daysCoveredNum = datesCoveredArr.length || (currentInvoice.daysCovered === '' ? 0 : parseFloat(currentInvoice.daysCovered) || 0);
    if (invoiceDaysInfo && daysCoveredNum > invoiceDaysInfo.remaining) {
      return toast.info(
        `Only ${invoiceDaysInfo.remaining} of ${invoiceDaysInfo.planned} planned day(s) remain unbilled for this work order.`
      );
    }
    // daysCoveredNum === 0 means "covers the whole work order" — only valid as the sole
    // invoice on it, otherwise it would silently double-bill the work order.
    if (daysCoveredNum === 0 && otherInvoicesOnWO.length > 0) {
      return toast.info(
        'This work order already has other invoices on it — pick which days this invoice covers instead of leaving it blank.'
      );
    }

    const invoiceToSave = { ...currentInvoice, datesCovered: datesCoveredArr, daysCovered: daysCoveredNum };

    const isEditing = editingInvoiceId != null;
    const updatedInvoices = isEditing
      ? invoices.map(i => (i.id ?? i._id) === editingInvoiceId ? { ...i, ...invoiceToSave, file: undefined } : i)
      : [...invoices, { ...invoiceToSave, id: invoices.length > 0 ? Math.max(...invoices.map(i => i.id || 0)) + 1 : 1, file: undefined }];
    const fileToUpload = isEditing ? null : currentInvoice.file;
    const previousInvoices = invoices; // kept to roll back if the server rejects this save

    setInvoices(updatedInvoices);
    setCurrentInvoice({ inspectorAgency: '', workOrder: [], invoiceNumber: '', invoiceDate: '', datesCovered: [], daysCovered: '', taxes: [], overtime: [], otherCostEntries: [], file: null });
    setEditingInvoiceId(null);
    setShowInvoiceFormModal(false);
    if (addInvoiceFileRef.current) addInvoiceFileRef.current.value = '';

    if (!id) return;

    setAddingInvoice(true);
    try {
      const token = localStorage.getItem('token');
      const invoicesToSend = updatedInvoices.map(({ id: _localId, file: _f, ...rest }) => rest);
      const res = await apiRequest(
        `${API_URLS.ENQUIRIES}/${id}`,
        { method: 'PUT', body: { invoices: invoicesToSend } },
        token
      );
      if (!res?.ok) {
        setInvoices(previousInvoices); // server rejected it (e.g. days-covered validation) — undo the optimistic add
        toast.error(res?.data?.message || 'Row could not be saved.');
        return;
      }

      const freshInvoices = res.data?.enquiry?.invoices || [];
      setInvoices(freshInvoices);

      if (fileToUpload) {
        const oldIds = new Set(invoices.map(i => i._id).filter(Boolean));
        const savedRow = freshInvoices.find(i => i._id && !oldIds.has(i._id));

        if (savedRow?._id) {
          const rowKey = `i_${savedRow._id}`;
          setUploadingRows(prev => ({ ...prev, [rowKey]: true }));
          setUploadProgress(prev => ({ ...prev, [rowKey]: 0 }));
          const fd = new FormData();
          fd.append('file', fileToUpload);
          const uploadRes = await uploadWithProgress(
            `${API_URLS.ENQUIRIES}/${id}/invoices/${savedRow._id}/upload`, fd, token, rowKey
          );
          if (uploadRes?.ok) {
            setUploadProgress(prev => ({ ...prev, [rowKey]: 100 }));
            setTimeout(() => { setUploadingRows(prev => ({ ...prev, [rowKey]: false })); setUploadProgress(prev => { const n = { ...prev }; delete n[rowKey]; return n; }); }, 800);
            setInvoices(prev => prev.map(i => i._id === savedRow._id ? { ...i, fileUrl: uploadRes.data.fileUrl } : i));
          } else {
            setUploadingRows(prev => ({ ...prev, [rowKey]: false }));
            setUploadProgress(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
            toast.info(uploadRes?.data?.message || 'Row added but file upload failed.');
          }
        }
      }
    } finally {
      setAddingInvoice(false);
    }
  };

  const deleteInvoice = (rowId) => {
    toast.deleteConfirm('Delete this invoice?', async () => {
      const updatedInvoices = invoices.filter(i => i.id !== rowId && i._id !== rowId);
      setInvoices(updatedInvoices);
      if (editingInvoiceId === rowId) cancelEditInvoice();

      if (!id) return;

      const token = localStorage.getItem('token');
      const invoicesToSend = updatedInvoices.map(({ id: _localId, ...rest }) => rest);
      const res = await apiRequest(
        `${API_URLS.ENQUIRIES}/${id}`,
        { method: 'PUT', body: { invoices: invoicesToSend } },
        token
      );
      if (res?.ok) {
        setInvoices(res.data?.enquiry?.invoices || updatedInvoices);
      } else {
        toast.error(res?.data?.message || 'Delete failed. Please save the enquiry manually.');
      }
    });
  };

  // Client Documents Handlers — job-level, client-facing (IR/NOT/ITP/Document),
  // restricted to PDF/Word/Excel/ZIP/RAR. Multiple rows of the same document type
  // are allowed (e.g. two IRs), same as the Deliverables table.
  const CLIENT_DOC_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/zip,application/x-zip-compressed,application/x-rar-compressed,application/vnd.rar,application/x-rar';
  const CLIENT_DOC_EXT_RE = /\.(pdf|docx?|xlsx?|zip|rar)$/i;

  const handleClientDocumentChange = (field, value) => setCurrentClientDocument(prev => ({ ...prev, [field]: value }));

  const startEditClientDocument = (d) => {
    setEditingClientDocumentId(d.id ?? d._id);
    setCurrentClientDocument({
      documentType: d.documentType || '',
      number: d.number || '',
      file: null,
    });
  };

  const cancelEditClientDocument = () => {
    setEditingClientDocumentId(null);
    setCurrentClientDocument({ documentType: '', number: '', file: null });
    if (addClientDocFileRef.current) addClientDocFileRef.current.value = '';
  };

  const addClientDocument = async () => {
    if (!currentClientDocument.documentType) return toast.info('Please select document type.');
    if (currentClientDocument.file && !CLIENT_DOC_EXT_RE.test(currentClientDocument.file.name)) {
      return toast.warning('Only PDF, Word, Excel, ZIP or RAR files are allowed.');
    }

    const isEditing = editingClientDocumentId != null;
    const updatedClientDocuments = isEditing
      ? clientDocuments.map(d => (d.id ?? d._id) === editingClientDocumentId ? { ...d, ...currentClientDocument, file: undefined } : d)
      : [...clientDocuments, { ...currentClientDocument, id: clientDocuments.length > 0 ? Math.max(...clientDocuments.map(i => i.id || 0)) + 1 : 1, file: undefined }];
    const fileToUpload = isEditing ? null : currentClientDocument.file;

    setClientDocuments(updatedClientDocuments);
    setCurrentClientDocument({ documentType: '', number: '', file: null });
    setEditingClientDocumentId(null);
    if (addClientDocFileRef.current) addClientDocFileRef.current.value = '';

    if (!id) return;

    setAddingClientDocument(true);
    try {
      const token = localStorage.getItem('token');
      const clientDocumentsToSend = updatedClientDocuments.map(({ id: _localId, file: _f, ...rest }) => rest);

      const res = await apiRequest(
        `${API_URLS.ENQUIRIES}/${id}`,
        { method: 'PUT', body: { clientDocuments: clientDocumentsToSend } },
        token
      );
      if (!res?.ok) {
        toast.info(res?.data?.message || 'Row added but could not save.');
        return;
      }

      const freshClientDocuments = res.data?.enquiry?.clientDocuments || [];
      setClientDocuments(freshClientDocuments);

      if (fileToUpload) {
        const oldIds = new Set(clientDocuments.map(d => d._id).filter(Boolean));
        const savedRow = freshClientDocuments.find(d => d._id && !oldIds.has(d._id));

        if (savedRow?._id) {
          const rowKey = `c_${savedRow._id}`;
          setUploadingRows(prev => ({ ...prev, [rowKey]: true }));
          setUploadProgress(prev => ({ ...prev, [rowKey]: 0 }));
          const fd = new FormData();
          fd.append('file', fileToUpload);
          const uploadRes = await uploadWithProgress(
            `${API_URLS.ENQUIRIES}/${id}/client-documents/${savedRow._id}/upload`, fd, token, rowKey
          );
          if (uploadRes?.ok) {
            setUploadProgress(prev => ({ ...prev, [rowKey]: 100 }));
            setTimeout(() => { setUploadingRows(prev => ({ ...prev, [rowKey]: false })); setUploadProgress(prev => { const n = { ...prev }; delete n[rowKey]; return n; }); }, 800);
            const { fileUrl } = uploadRes.data;
            setClientDocuments(prev => prev.map(d => d._id === savedRow._id ? { ...d, fileUrl } : d));
          } else {
            setUploadingRows(prev => ({ ...prev, [rowKey]: false }));
            setUploadProgress(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
            toast.info(uploadRes?.data?.message || 'Row added but file upload failed.');
          }
        }
      }
    } finally {
      setAddingClientDocument(false);
    }
  };

  const deleteClientDocument = (rowId) => {
    toast.deleteConfirm('Delete this document?', async () => {
      const updatedClientDocuments = clientDocuments.filter(i => i.id !== rowId && i._id !== rowId);
      setClientDocuments(updatedClientDocuments);
      if (editingClientDocumentId === rowId) cancelEditClientDocument();

      if (!id) return;

      const token = localStorage.getItem('token');
      const clientDocumentsToSend = updatedClientDocuments.map(({ id: _localId, ...rest }) => rest);
      const res = await apiRequest(
        `${API_URLS.ENQUIRIES}/${id}`,
        { method: 'PUT', body: { clientDocuments: clientDocumentsToSend } },
        token
      );
      if (res?.ok) {
        setClientDocuments(res.data?.enquiry?.clientDocuments || updatedClientDocuments);
      } else {
        toast.error(res?.data?.message || 'Delete failed. Please save the enquiry manually.');
      }
    });
  };

  // Remark Logs — builds the array to send on save, appending today's date + the
  // current Remark Status (plus whatever Remark text goes with it) only when the
  // Remark Status actually changed since the last time it was logged (so re-saving
  // without touching Remark Status doesn't add a duplicate row).
  const buildRemarkLogsForSave = () => {
    const strippedLogs = remarkLogs.map(({ id: _localId, ...rest }) => rest);
    if (formData.remarkStatus === lastLoggedRemarkStatusRef.current) return strippedLogs;
    return [...strippedLogs, {
      date: new Date().toISOString().split('T')[0],
      remarkStatus: formData.remarkStatus,
      remark: REMARK_STATUS_SHOWS_REMARK.includes(formData.remarkStatus) ? (formData.remark || '') : '',
    }];
  };

  // ── XHR upload with progress tracking ────────────────────────────────────
  const uploadWithProgress = (url, fd, token, rowKey) =>
    new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          // Cap at 90% — remaining reflects server→SharePoint transfer
          setUploadProgress(prev => ({ ...prev, [rowKey]: Math.round((e.loaded / e.total) * 90) }));
        }
      };
      xhr.upload.onload = () => {
        // All bytes sent to server; server is now uploading to SharePoint
        setUploadProgress(prev => ({ ...prev, [rowKey]: 95 }));
      };
      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, data });
        } catch {
          resolve({ ok: false, data: { message: 'Invalid response' } });
        }
      };
      xhr.onerror = () => resolve({ ok: false, data: { message: 'Network error' } });
      xhr.send(fd);
    });

  // ── OneDrive upload handlers ──────────────────────────────────────────────
  const handleUploadClick = (type, mongoId) => {
    setPendingUpload({ type, mongoId });
    uploadInputRef.current.value = '';
    uploadInputRef.current.accept = type === 'clientDocument' ? CLIENT_DOC_ACCEPT : '';
    uploadInputRef.current.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !pendingUpload || !id) return;

    const { type, mongoId } = pendingUpload;
    if (type === 'clientDocument' && !CLIENT_DOC_EXT_RE.test(file.name)) {
      e.target.value = '';
      setPendingUpload(null);
      return toast.warning('Only PDF, Word, Excel, ZIP or RAR files are allowed.');
    }
    const rowKey = `${type === 'deliverable' ? 'd' : type === 'clientDocument' ? 'c' : 'i'}_${mongoId}`;

    setUploadingRows(prev => ({ ...prev, [rowKey]: true }));
    setUploadProgress(prev => ({ ...prev, [rowKey]: 0 }));
    setPendingUpload(null);

    const token = localStorage.getItem('token');
    const fd = new FormData();
    fd.append('file', file);

    const url = type === 'deliverable'
      ? `${API_URLS.ENQUIRIES}/${id}/deliverables/${mongoId}/upload`
      : type === 'clientDocument'
      ? `${API_URLS.ENQUIRIES}/${id}/client-documents/${mongoId}/upload`
      : `${API_URLS.ENQUIRIES}/${id}/invoices/${mongoId}/upload`;

    const res = await uploadWithProgress(url, fd, token, rowKey);
    e.target.value = '';

    if (res?.ok) {
      setUploadProgress(prev => ({ ...prev, [rowKey]: 100 }));
      setTimeout(() => { setUploadingRows(prev => ({ ...prev, [rowKey]: false })); setUploadProgress(prev => { const n = { ...prev }; delete n[rowKey]; return n; }); }, 800);
      const { fileUrl } = res.data;
      if (type === 'deliverable') {
        setDeliverables(prev => prev.map(d => d._id === mongoId ? { ...d, fileUrl } : d));
      } else if (type === 'clientDocument') {
        setClientDocuments(prev => prev.map(d => d._id === mongoId ? { ...d, fileUrl } : d));
      } else {
        setInvoices(prev => prev.map(inv => inv._id === mongoId ? { ...inv, fileUrl } : inv));
      }
    } else {
      setUploadingRows(prev => ({ ...prev, [rowKey]: false }));
      setUploadProgress(prev => { const n = { ...prev }; delete n[rowKey]; return n; });
      toast.error(res?.data?.message || 'Upload failed. Please try again.');
    }
  };

  // --- 11. Work Order Logic ---

  const handleGenerateWorkOrder = async () => {
    if (!workOrderForm.workOrderNumber) {
      toast.warning('Work Order Number is required!');
      return;
    }
    const inspectorMongoId = selectedInspectorForWO._id;
    if (!id || !inspectorMongoId) {
      toast.info('Please save the enquiry first before generating a work order.');
      return;
    }

    const updatedInspector = {
      ...selectedInspectorForWO,
      workOrder: workOrderForm.workOrderNumber,
      workOrderDate: workOrderForm.date,
      issuingCompany: workOrderForm.issuingCompany,
      workLocation: workOrderForm.workLocation,
      workDescription: workOrderForm.workDescription,
      additionalComments: workOrderForm.additionalComments,
      billing: { ...workOrderForm.billing },
      administrative: { ...workOrderForm.administrative },
    };

    setSavingWorkOrder(true);
    try {
      const token = localStorage.getItem('token');
      const res = await apiRequest(
        `${API_URLS.ENQUIRIES}/${id}/inspectors/${inspectorMongoId}/work-order/generate`,
        {
          method: 'POST',
          body: {
            inspector: {
              inspectorName:   workOrderForm.inspectorName || selectedInspectorForWO.inspectorName,
              contactNumber:   selectedInspectorForWO.contactNumber,
              email:           selectedInspectorForWO.email,
              inspectionDates: selectedInspectorForWO.inspectionDates,
              currencyOffered: selectedInspectorForWO.currencyCost || selectedInspectorForWO.currencyOffered || 'INR',
            },
            workOrderForm,
          },
        },
        token
      );

      if (res?.ok) {
        const { fileUrl, workOrderNumber } = res.data;
        const isManmonth = formData.enquiryType === 'MANMONTH';
        const billingRate = isManmonth ? workOrderForm.billing.monthlyRate : workOrderForm.billing.rate;
        const costCurrency = selectedInspectorForWO.currencyCost || 'INR';
        const newCostINR = (billingRate * (CURRENCY_RATES[costCurrency] || 1)).toFixed(2);
        setInspectors(prev =>
          prev.map(insp =>
            (insp.id === selectedInspectorForWO.id || String(insp._id) === String(selectedInspectorForWO._id))
              ? {
                  ...updatedInspector,
                  workOrderUrl: fileUrl,
                  workOrder: workOrderNumber,
                  // numberOfDays stays a day count (like Manday) — billing.months is the
                  // separate figure the monthly rate is billed against.
                  numberOfDays: isManmonth ? (selectedInspectorForWO.inspectionDates || []).length : workOrderForm.billing.days,
                  cost: String(billingRate),
                  costINR: newCostINR,
                }
              : insp
          )
        );
        setShowGenerateWorkOrderModal(false);
        toast.success('Work order saved to OneDrive!');
      } else {
        toast.error(`Failed to save work order: ${res?.data?.message || 'Unknown error'}`);
      }
    } catch (err) {
      toast.error(`Failed to save work order: ${err.message}`);
    } finally {
      setSavingWorkOrder(false);
    }
  };

  const handleUploadSignedWO = async () => {
    if (!signedWOFile || !signingInspector || !id) return;
    setUploadingSignedWO(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', signedWOFile);
      const res = await uploadWithProgress(
        `${API_URLS.ENQUIRIES}/${id}/inspectors/${signingInspector._id}/work-order/upload-signed`,
        fd, token, `signed_${signingInspector._id}`
      );
      if (res?.ok) {
        const { fileUrl } = res.data;
        setInspectors(prev => prev.map(insp =>
          String(insp._id) === String(signingInspector._id)
            ? { ...insp, signedWorkOrderUrl: fileUrl }
            : insp
        ));
        setShowSignedWOModal(false);
        setSignedWOFile(null);
        setSigningInspector(null);
        toast.success('Signed work order uploaded to OneDrive!');
      } else {
        toast.error(res?.data?.message || 'Upload failed.');
      }
    } catch (err) {
      toast.error(`Upload failed: ${err.message}`);
    } finally {
      setUploadingSignedWO(false);
    }
  };

  const openWorkOrderModal = (insp) => {
    const nextWO = insp.workOrder || generateNextWONumber();
    const inspDates = Array.isArray(insp.inspectionDates) ? [...new Set(insp.inspectionDates)].sort() : [];
    const isManmonth = formData.enquiryType === 'MANMONTH';
    const attendedDates = inspDates.filter(d => !(insp.absentDates || []).includes(d));
    const days = inspDates.length || insp.numberOfDays || insp.billing?.days || 0;
    const months = countDistinctMonths(attendedDates) || insp.billing?.months || 0;
    const rate = isManmonth
      ? parseFloat(insp.cost || insp.billing?.monthlyRate || 0)
      : parseFloat(insp.cost || insp.billing?.rate || 0);
    // Working Days defaults to 26 for any month newly touched by this assignment that
    // doesn't already have a figure saved — the user can edit it per month below.
    const workingDaysByMonth = { ...(insp.billing?.workingDaysByMonth || {}) };
    groupDatesByMonth(inspDates).forEach(({ ym }) => {
      if (workingDaysByMonth[ym] === undefined) workingDaysByMonth[ym] = 26;
    });
    // Manmonth's Amount prorates the monthly rate per day using the inspector's FULL
    // original assignment (absences included) as the denominator, so an absence
    // genuinely cuts the pay — see computeBillingForMonths/monthlyShareAmount.
    const amount = isManmonth
      ? parseFloat(monthlyShareAmount(attendedDates, inspDates, rate, workingDaysByMonth).toFixed(2))
      : parseFloat((days * rate).toFixed(2));
    const tax = insp.billing?.tax || 0;
    const otherCosts = insp.billing?.otherCosts || 0;
    // Tax applies to the Amount only — Overtime is per-invoice only;
    // Other Costs stays a flat figure here.
    const totalTax = parseFloat((amount * tax / 100).toFixed(2));
    const total = parseFloat((amount + totalTax + otherCosts).toFixed(2));
    const customer = customers.find(c => c.id === formData.customerId);
    const vendor = vendors.find(v => v.id === formData.manufacturerVendor);
    const enquiryCompany = companies.find(c => c.id === formData.company || String(c._id) === String(formData.company));
    const companyAddress = enquiryCompany?.address || '';
    const issuingCompanyFull = enquiryCompany
      ? `${enquiryCompany.companyName}${companyAddress ? '\n' + companyAddress : ''}`
      : 'APS Consultancy Private Limited';

    setWorkOrderForm({
      date: insp.workOrderDate ? new Date(insp.workOrderDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      workOrderNumber: nextWO,
      inspectorName: insp.inspectorName || '',
      orderReceivedBy: '',
      workLocation: insp.workLocation || (vendor ? `Vendor: ${vendor.vendorName}\nAddress: ${vendor.address || ''}` : customer ? `Vendor: ${customer.customerName}` : ''),
      workDescription: insp.workDescription || '',
      additionalComments: insp.additionalComments || '',
      issuingCompany: issuingCompanyFull,
      expectedStartDate: insp.expectedStartDate ? new Date(insp.expectedStartDate).toISOString().split('T')[0] : (inspDates[0] || ''),
      expectedEndDate: insp.expectedEndDate ? new Date(insp.expectedEndDate).toISOString().split('T')[0] : (inspDates[inspDates.length - 1] || ''),
      billing: isManmonth ? { months, monthlyRate: rate, workingDaysByMonth, amount, tax, otherCosts, total } : { days, rate, amount, tax, otherCosts, total },
      administrative: {
        orderReceivedBy: '',
        compiledBy: user?.fullName || user?.name || '',
        approvingParty: insp.administrative?.approvingParty || '',
        dateOfApproval: insp.administrative?.dateOfApproval
          ? new Date(insp.administrative.dateOfApproval).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0]
      }
    });
    setEditInspectorName(false);
    setSelectedInspectorForWO(insp);
    setWorkOrderModalDragged(false); setShowGenerateWorkOrderModal(true);
  };

  // --- 12. Main Save Handlers ---

  const handleSave = async (redirectTo = null) => {
    if (savingEnquiry) return toast.info('Save already in progress. Please wait...');
    if (user?.role === 'global_superadmin' && !formData.company) return toast.info('Please select a company.');
    if (!formData.customerId?.trim()) return toast.info('Please select a customer.');
    if (!formData.manufacturerVendor?.trim()) return toast.info('Please select a vendor.');
    if (!formData.enquiryDate) return toast.info('Please select an enquiry date.');

    setSavingEnquiry(true);
    try {
      const token = localStorage.getItem('token');
      const preparedInspectors = inspectors.map(insp => ({ ...insp, numberOfDays: insp.numberOfDays || 0 }));
      const payload = {
        ...formData,
        company: formData.company?.trim() ? formData.company : null,
        customerId: formData.customerId?.trim() ? formData.customerId : null,
        manufacturerVendor: (formData.manufacturerVendor?.trim() && formData.manufacturerVendor !== 'TBD') ? formData.manufacturerVendor : null,
        inspectors: preparedInspectors,
        // Strip local row ids/files — a numeric `id` would be cast onto the subdocument `_id`
        deliverables: deliverables.map(({ id: _localId, file: _f, ...rest }) => rest),
        invoices: invoices.map(({ id: _localId, file: _f, ...rest }) => rest),
        clientDocuments: clientDocuments.map(({ id: _localId, file: _f, ...rest }) => rest),
        remarkLogs: buildRemarkLogsForSave()
      };

      const res = await apiRequest(id ? `${API_URLS.ENQUIRIES}/${id}` : API_URLS.ENQUIRIES, { method: id ? 'PUT' : 'POST', body: payload }, token);
      if (res?.ok) {
        const savedEnquiry = res.data?.enquiry;
        const savedInspectors = savedEnquiry?.inspectors
          ? savedEnquiry.inspectors.map((insp, idx) => ({ ...insp, id: idx + 1 }))
          : inspectors;
        if (savedEnquiry?.inspectors) {
          setInspectors(savedInspectors);
        }
        if (savedEnquiry?.remarkLogs) setRemarkLogs(savedEnquiry.remarkLogs);
        lastLoggedRemarkStatusRef.current = formData.remarkStatus;
        // Re-baseline snapshot so back-button dirty check doesn't fire after save
        initialSnapshotRef.current = JSON.stringify({ formData, inspectors: savedInspectors, deliverables, invoices, clientDocuments, remarkLogs: savedEnquiry?.remarkLogs || remarkLogs });
        toast.success('Enquiry saved successfully!');
        const newId = savedEnquiry?._id || savedEnquiry?.id;
        if (redirectTo === returnTo) goReturnTo();
        else navigate(redirectTo ?? (newId ? `/enquiry/manday/${String(newId)}` : '/enquiry'), redirectTo ? undefined : { state: location.state, replace: !!location.state?.returnTo });
      } else toast.error(`Error: ${res?.message || 'Failed to save enquiry'}`);
    } catch (error) {
      console.error('Save error:', error);
      toast.error('An error occurred while saving. Please try again.');
    } finally { setSavingEnquiry(false); }
  };

  // Shared by the Save Draft button and the invite-send flow (which auto-saves right
  // after a mail goes out) — `inspectorsOverride` lets a caller save a just-computed
  // inspectors array without waiting on the async setInspectors state update.
  const saveEnquiry = async (inspectorsOverride) => {
    if (savingDraft) return toast.info('Save already in progress. Please wait...');
    if (user?.role === 'global_superadmin' && !formData.company) return toast.info('Please select a company.');
    if (!formData.enquiryDate) return toast.info('Please select an enquiry date.');

    setSavingDraft(true);
    try {
      const token = localStorage.getItem('token');
      const preparedInspectors = (inspectorsOverride ?? inspectors).map(insp => ({ ...insp, numberOfDays: insp.numberOfDays || 0 }));
      const payload = {
        ...formData,
        company: formData.company?.trim() ? formData.company : null,
        customerId: formData.customerId?.trim() ? formData.customerId : null,
        manufacturerVendor: (formData.manufacturerVendor?.trim() && formData.manufacturerVendor !== 'TBD') ? formData.manufacturerVendor : null,
        status: 'NEW',
        inspectors: preparedInspectors,
        // Strip local row ids/files — a numeric `id` would be cast onto the subdocument `_id`
        deliverables: deliverables.map(({ id: _localId, file: _f, ...rest }) => rest),
        invoices: invoices.map(({ id: _localId, file: _f, ...rest }) => rest),
        clientDocuments: clientDocuments.map(({ id: _localId, file: _f, ...rest }) => rest),
        remarkLogs: buildRemarkLogsForSave()
      };

      const res = await apiRequest(id ? `${API_URLS.ENQUIRIES}/${id}` : API_URLS.ENQUIRIES, { method: id ? 'PUT' : 'POST', body: payload }, token);
      if (res?.ok) {
        const savedEnquiry = res.data?.enquiry;
        if (savedEnquiry?.inspectors) {
          setInspectors(savedEnquiry.inspectors.map((insp, idx) => ({ ...insp, id: idx + 1 })));
        }
        if (savedEnquiry?.remarkLogs) setRemarkLogs(savedEnquiry.remarkLogs);
        lastLoggedRemarkStatusRef.current = formData.remarkStatus;
         toast.success('Draft saved successfully!');
        const newId = savedEnquiry?._id || savedEnquiry?.id;
        navigate(newId ? `/enquiry/manday/${String(newId)}` : '/enquiry', { state: location.state, replace: !!location.state?.returnTo });
      } else toast.error(`Error: ${res?.message || 'Failed to save draft'}`);
    } catch (error) {
      console.error('Save error:', error);
      toast.error('An error occurred while saving. Please try again.');
    } finally { setSavingDraft(false); }
  };

  const handleSaveDraft = () => saveEnquiry();

  // Unsaved-changes guard for the page's Back button — same Yes/No pattern as the quick-add modals
  const isEnquiryDirty = () => {
    if (initialSnapshotRef.current === null) return false;
    try {
      return JSON.stringify({ formData, inspectors, deliverables, invoices, clientDocuments, remarkLogs }) !== initialSnapshotRef.current;
    } catch {
      return true;
    }
  };

  const requestNavigateBack = () => {
    if (!isView && isEnquiryDirty()) {
      toast.confirm(
        'You have unsaved changes to this enquiry. Do you want to save before leaving?',
        () => handleSave(returnTo),
        goReturnTo
      );
    } else {
      goReturnTo();
    }
  };

  // --- 13. Dynamic Inspector State ---
  const countryIso = ALL_COUNTRIES.find(c => c.name === newInspForm.country)?.isoCode || "";
  const stateOptions = countryIso ? State.getStatesOfCountry(countryIso) : [];
  const stateIso = stateOptions.find(s => s.name === newInspForm.state)?.isoCode || "";
  const cityOptions = (countryIso && stateIso) ? City.getCitiesOfState(countryIso, stateIso) : [];

  const vCountryIso = ALL_COUNTRIES.find(c => c.name === newVendorForm.country)?.isoCode || "";
  const vStateOptions = vCountryIso ? State.getStatesOfCountry(vCountryIso) : [];
  const vStateIso = vStateOptions.find(s => s.name === newVendorForm.state)?.isoCode || "";
  const vCityOptions = (vCountryIso && vStateIso) ? City.getCitiesOfState(vCountryIso, vStateIso) : [];

  // --- 14. Render ---
  return (
    <div className="register-container">
      {/* Top Header Bar */}
      <div style={{ padding: "10px 16px" }} className="register-header-bar" onClick={() => toggleSection('basicInfo')}>
        <h2 style={{ fontSize: "16px", color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FiFileText size={16} />
          {isView ? 'View Enquiry' : isAdd ? 'New Enquiry' : 'Edit Enquiry'}
          <input type="text" className="form-control job-number-input" disabled value={isAdd ? 'Auto-generated' : (formData.jobNumber || formData._id || 'Auto-generated')} />
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); requestNavigateBack(); }}
            style={{ padding: '4px 12px', fontSize: 13, border: '1px solid #ccc', borderRadius: 4, background: '#fff', cursor: 'pointer', color: '#333' , display : "flex" , alignItems : "center" , gap : "7px" }}
          >
            <IoArrowBackOutline /><span className="desktop-only">Back</span>
          </button>
          <span style={{ fontSize: "16px" }}>{collapsed.basicInfo ? <FaChevronDown /> : <FaChevronUp />}</span>
        </div>
      </div>

      {/* Main Enquiry Details Card */}
      {!collapsed.basicInfo && (
        <fieldset disabled={isView} style={{ border: 'none', padding: 0, margin: 0, width: '100%' }}>
          <div className="card-panel" style={{ display: "flex", justifyContent: "space-between", flexDirection: "column", gap: "20px" }}>

            {/* Project Details */}
            <div className='project-wrapper'>
              <div className='project-header' onClick={() => toggleSection('projectDetails')}>
                <h2 style={{ color: "#333" }}>Project Details</h2>
                <span style={{ fontSize: "12px" }}>{collapsed.projectDetails ? <FaChevronDown /> : <FaChevronUp />}</span>
              </div>
              {!collapsed.projectDetails && (
                <div className='card-panel' style={{ padding: "16px", margin: 0 }}>
                  <div className="form-grid" style={{ gridTemplateColumns: "repeat(4,1fr)" }}>
                    {user?.role === 'global_superadmin' && (
                      <div className="form-group">
                        <label>Select Company <span className="required-red">*</span></label>
                        <select
                          className="form-control"
                          name="company"
                          value={formData.company || ''}
                          onChange={handleInputChange}
                        >
                          <option value="">-- Select Company --</option>
                          {companies.map(c => (
                            <option key={c.id || c._id} value={c.id || c._id}>{c.companyName}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="form-group">
                      <label>Enquiry Date <span className="required">*</span></label>
                      <input type="date" style={{ padding : "7px 10px"}} className="form-control" name="enquiryDate" value={formData.enquiryDate || ''} onChange={handleInputChange} />
                    </div>
                    <div className="form-group">
                      <label>Enquiry Type <span className="required">*</span></label>
                      <select
                        className="form-control"
                        value={formData.enquiryType || 'MANDAY'}
                        disabled={!isAdd}
                        title={!isAdd ? 'Locked after the enquiry is first saved — its billing model (per-day vs monthly rate) is already baked into any inspectors/work orders on it' : undefined}
                        onChange={(e) => {
                          const nextType = e.target.value;
                          handleInputChange('enquiryType', nextType);
                          // Switching type changes what tentativeDates means (day list vs
                          // whole-month plan) — start that side fresh rather than carrying
                          // over dates picked under the other mode.
                          handleInputChange('tentativeDates', '');
                          setSelectedMonths([]);
                        }}
                      >
                        <option value="MANDAY">Manday</option>
                        <option value="MANMONTH">Manmonth</option>
                      </select>
                    </div>
                    {formData.enquiryType === 'MANMONTH' ? (
                      <div className="form-group">
                        <label>Select Month(s)</label>
                        <DatePicker
                          onlyMonthPicker
                          multiple
                          format="MMMM YYYY"
                          value={selectedMonths}
                          onChange={(monthObjects) => {
                            const months = monthObjects || [];
                            const generated = expandMonthsToDays(months);
                            // Keep any date already assigned to an inspector even if its
                            // month was just deselected — same protection the day picker
                            // gives Manday, applied at the month level.
                            const preserved = Array.from(assignedToInspectorDates).filter(d =>
                              formData.tentativeDates && formData.tentativeDates.split(',').map(s => s.trim()).includes(d)
                            );
                            const merged = Array.from(new Set([...generated, ...preserved])).sort();
                            handleInputChange('tentativeDates', merged.join(', '));
                            setSelectedMonths(months);
                          }}
                          inputClass="form-control"
                          containerStyle={{ width: '100%' }}
                          style={{ width: "93%" }}
                          placeholder="Select one or more months"
                        />
                      </div>
                    ) : (
                      <div className="form-group">
                        <label>Tentative Dates</label>
                        <DatePicker
                          multiple
                          format="DD-MM-YYYY"
                          value={formData.tentativeDates ? formData.tentativeDates.split(',').map(d => d.trim()).filter(Boolean).map(d => new Date(d)) : []}
                          onChange={(dateObjects) => {
                            // Keep any date already assigned to an inspector regardless of
                            // what the picker fires — locked dates are disabled in mapDays
                            // but a double-click can still toggle them in some versions.
                            const incoming = dateObjects ? dateObjects.map(d => d.format("YYYY-MM-DD")) : [];
                            const preserved = Array.from(assignedToInspectorDates).filter(d =>
                              formData.tentativeDates && formData.tentativeDates.split(',').map(s => s.trim()).includes(d)
                            );
                            const merged = Array.from(new Set([...incoming, ...preserved])).sort();
                            handleInputChange('tentativeDates', merged.join(', '));
                          }}
                          mapDays={makeShiftRangeMapDays({
                            getValues: () => formData.tentativeDates ? formData.tentativeDates.split(',').map(d => d.trim()).filter(Boolean) : [],
                            setValues: (arr) => handleInputChange('tentativeDates', arr.join(', ')),
                            anchorRef: tentativeAnchorRef,
                            isLocked: (d) => assignedToInspectorDates.has(d),
                            lockedTitle: 'Assigned to an inspector — remove from the financials table first',
                            lockedStyle: { backgroundColor: '#e67e22', color: '#fff' },
                          })}
                          inputClass="form-control"
                          containerStyle={{ width: '100%' }}
                          style={{ width: "93%" }}
                          placeholder="Select multiple dates (Shift+click for range)"
                        />
                      </div>
                    )}
                    <div className="form-group">
                      <label>Total Days (Inspection)</label>
                      <input type="text" className="form-control" disabled value={formData.totalDays ?? ''} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Customer & Vendor Details */}
            <div className='customer-vendor-wrapper' style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div className='customer-wrapper'>
                <div className='customer-header' onClick={() => toggleSection('customerDetails')}>
                  <h2 style={{ color: "#333" }}>Customer Details</h2>
                  <span style={{ fontSize: "12px" }}>{collapsed.customerDetails ? <FaChevronDown /> : <FaChevronUp />}</span>
                </div>
                {!collapsed.customerDetails && (
                  <div className='card-panel' style={{ padding: '16px', margin: 0 }}>
                    <div className="form-grid" style={{ gridTemplateColumns: "repeat(4,1fr)" }}>
                      {/* Select Customer */}
                      <div className="form-group" style={{ position: 'relative' }}>
                        <label>Select Customer <span className="required">*</span></label>
                        <div className="searchable-select-container" onClick={() => !formData.customerId && setDropdownVisible(prev => ({ ...prev, customer: true }))}>
                          {formData.customerId ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', color: '#000', fontSize: '14px' }}>
                              <span style={{ flex: 1 }}>{customers.find(c => c.id === formData.customerId)?.customerName || customers.find(c => c.id === formData.customerId)?.companyName}</span>
                              <button type="button" onClick={(e) => { e.stopPropagation(); setFormData(prev => ({ ...prev, customerId: '' })); }} style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '18px', padding: '0 4px' }}>×</button>
                            </div>
                          ) : (
                            <input
                              type="text" className="search-input" placeholder="Select Customer" value={searchTerms.customer}
                              onChange={(e) => { setSearchTerms(prev => ({ ...prev, customer: e.target.value })); setDropdownFocusIdx(prev => ({ ...prev, customer: -1 })); }}
                              onFocus={() => setDropdownVisible(prev => ({ ...prev, customer: true }))}
                              onBlur={() => setTimeout(() => { setDropdownVisible(prev => ({ ...prev, customer: false })); setDropdownFocusIdx(prev => ({ ...prev, customer: -1 })); }, 200)}
                              onKeyDown={(e) => {
                                const filtered = customers.filter(c => (c.customerName || c.companyName || '').toLowerCase().includes(searchTerms.customer.toLowerCase()));
                                if (e.key === 'ArrowDown') { e.preventDefault(); setDropdownVisible(prev => ({ ...prev, customer: true })); setDropdownFocusIdx(prev => ({ ...prev, customer: Math.min(prev.customer + 1, filtered.length - 1) })); }
                                else if (e.key === 'ArrowUp') { e.preventDefault(); setDropdownFocusIdx(prev => ({ ...prev, customer: Math.max(prev.customer - 1, -1) })); }
                                else if (e.key === 'Enter' && dropdownFocusIdx.customer >= 0) { const s = filtered[dropdownFocusIdx.customer]; if (s) { handleInputChange({ target: { name: 'customerId', value: s.id } }); setSearchTerms(prev => ({ ...prev, customer: '' })); setDropdownVisible(prev => ({ ...prev, customer: false })); setDropdownFocusIdx(prev => ({ ...prev, customer: -1 })); } }
                                else if (e.key === 'Escape') { setDropdownVisible(prev => ({ ...prev, customer: false })); }
                              }}
                              style={{ width: '100%' }}
                            />
                          )}
                          {dropdownVisible.customer && (
                            <div className="searchable-dropdown" onMouseDown={(e) => e.preventDefault()} onTouchStart={(e) => e.preventDefault()}>
                              {customers.filter(c => (c.customerName || c.companyName || '').toLowerCase().includes(searchTerms.customer.toLowerCase())).map((c, idx) => (
                                <div key={c.id} className="dropdown-item" style={{ background: idx === dropdownFocusIdx.customer ? '#e8f0fb' : '' }} onClick={(e) => { e.stopPropagation(); handleInputChange({ target: { name: 'customerId', value: c.id } }); setSearchTerms(prev => ({ ...prev, customer: '' })); setDropdownVisible(prev => ({ ...prev, customer: false })); setDropdownFocusIdx(prev => ({ ...prev, customer: -1 })); }}>{c.customerName || c.companyName}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Customer Requirement <span className="required">*</span></label>
                        <input type="text" className="form-control" name="customerRequirement" placeholder="Enter Customer Requirement" value={formData.customerRequirement || ''} onChange={handleInputChange} />
                      </div>
                      {/* Coordinator */}
                      <div className="form-group" style={{ position: 'relative' }}>
                        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                          <span>Select Customer Coordinator <span className="required">*</span></span>
                          <span
                            style={{ color: '#437cf0', border: '1px solid #437cf0', padding: '2px 4px', display: 'inline-flex', alignItems: 'center', borderRadius: 3, cursor: 'pointer', fontSize: 11 }}
                            onClick={() => { setCoordModalDragged(false); setAddCoordForm({ customerId: formData.customerId || '', name: '', email: '', mobileNumber: '' }); setShowAddCoordModal(true); }}
                            title="Add new coordinator"
                          >
                            <FiPlus size={11} />
                          </span>
                        </label>
                        <div className="searchable-select-container" onClick={() => setDropdownVisible(prev => ({ ...prev, coordinator: true }))}>
                          {formData.customerCoordinator.length === 0 && !searchTerms.coordinator && <span style={{ color: '#999', alignSelf: 'center', fontSize: '13px', position: 'absolute', left: '10px', pointerEvents: 'none' }}>Select coordinators...</span>}
                          {formData.customerCoordinator.map(coord => (
                            <div key={coord} style={{ background: '#437cf0', color: '#fff', padding: '2px 8px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: "10px" }}>
                              <span>{coord}</span>
                              <button type="button" onClick={(e) => { e.stopPropagation(); setFormData(prev => ({ ...prev, customerCoordinator: prev.customerCoordinator.filter(c => c !== coord) })); }} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '16px', padding: 0 }}>×</button>
                            </div>
                          ))}
                          <input
                            type="text" className="search-input" placeholder={formData.customerCoordinator.length === 0 ? "" : "Search..."} value={searchTerms.coordinator}
                            onChange={(e) => { setSearchTerms(prev => ({ ...prev, coordinator: e.target.value })); setDropdownFocusIdx(prev => ({ ...prev, coordinator: -1 })); }}
                            onFocus={() => setDropdownVisible(prev => ({ ...prev, coordinator: true }))}
                            onBlur={() => setTimeout(() => { setDropdownVisible(prev => ({ ...prev, coordinator: false })); setDropdownFocusIdx(prev => ({ ...prev, coordinator: -1 })); }, 200)}
                            onKeyDown={(e) => {
                              const filtered = coordinators.filter(c => !formData.customerCoordinator.includes(c.name) && (c.name || '').toLowerCase().includes(searchTerms.coordinator.toLowerCase()));
                              if (e.key === 'ArrowDown') { e.preventDefault(); setDropdownVisible(prev => ({ ...prev, coordinator: true })); setDropdownFocusIdx(prev => ({ ...prev, coordinator: Math.min(prev.coordinator + 1, filtered.length - 1) })); }
                              else if (e.key === 'ArrowUp') { e.preventDefault(); setDropdownFocusIdx(prev => ({ ...prev, coordinator: Math.max(prev.coordinator - 1, -1) })); }
                              else if (e.key === 'Enter' && dropdownFocusIdx.coordinator >= 0) { const s = filtered[dropdownFocusIdx.coordinator]; if (s) { setFormData(prev => ({ ...prev, customerCoordinator: [...prev.customerCoordinator, s.name] })); setSearchTerms(prev => ({ ...prev, coordinator: '' })); setDropdownFocusIdx(prev => ({ ...prev, coordinator: -1 })); } }
                              else if (e.key === 'Escape') { setDropdownVisible(prev => ({ ...prev, coordinator: false })); }
                            }}
                          />
                          {dropdownVisible.coordinator && (
                            <div className="searchable-dropdown" onMouseDown={(e) => e.preventDefault()} onTouchStart={(e) => e.preventDefault()}>
                              {coordinators.filter(c => !formData.customerCoordinator.includes(c.name) && (c.name || '').toLowerCase().includes(searchTerms.coordinator.toLowerCase())).map((c, idx) => (
                                <div key={c._id} className="dropdown-item" style={{ background: idx === dropdownFocusIdx.coordinator ? '#e8f0fb' : '' }} onClick={(e) => { e.stopPropagation(); setFormData(prev => ({ ...prev, customerCoordinator: [...prev.customerCoordinator, c.name] })); setSearchTerms(prev => ({ ...prev, coordinator: '' })); setDropdownVisible(prev => ({ ...prev, coordinator: false })); setDropdownFocusIdx(prev => ({ ...prev, coordinator: -1 })); }}>{c.name}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Products */}
                      <div className="form-group" style={{ position: 'relative' }}>
                        <label style={{ justifyContent : "space-between"}}>Select Products <span style={{ color: '#437cf0', border: '1px solid #437cf0', padding: '2px 3px', display : " flex" , alignItems : "center" , justifyContent : "center" , borderRadius: '3px', cursor: 'pointer' }} onClick={() => { setProductModalDragged(false); setShowAddProductModal(true); }} title="Add new product"><FiPlus/></span></label>
                        <div className="searchable-select-container" onClick={() => setDropdownVisible(prev => ({ ...prev, product: true }))}>
                          {formData.selectedProducts.length === 0 && !searchTerms.product && <span style={{ color: '#999', alignSelf: 'center', fontSize: '13px', position: 'absolute', left: '10px', pointerEvents: 'none' }}>Select products...</span>}
                          {formData.selectedProducts.map(pid => {
                            const prod = products.find(p => p.id === pid);
                            return prod ? (
                              <div key={pid} style={{ background: '#437cf0', color: '#fff', padding: '3px 8px', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px' }}>
                                <span>{prod.name}</span>
                                <button type="button" onClick={(e) => { e.stopPropagation(); setFormData(prev => ({ ...prev, selectedProducts: prev.selectedProducts.filter(id => id !== pid) })); }} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '14px', padding: 0, lineHeight: 1 }}>×</button>
                              </div>
                            ) : null;
                          })}
                          <input
                            type="text" className="search-input" placeholder={formData.selectedProducts.length === 0 ? "" : "Search..."} value={searchTerms.product}
                            onChange={(e) => { setSearchTerms(prev => ({ ...prev, product: e.target.value })); setDropdownFocusIdx(prev => ({ ...prev, product: -1 })); }}
                            onFocus={() => setDropdownVisible(prev => ({ ...prev, product: true }))}
                            onBlur={() => setTimeout(() => { setDropdownVisible(prev => ({ ...prev, product: false })); setDropdownFocusIdx(prev => ({ ...prev, product: -1 })); }, 200)}
                            onKeyDown={(e) => {
                              const filtered = products.filter(p => !formData.selectedProducts.includes(p.id) && (p.name || '').toLowerCase().includes(searchTerms.product.toLowerCase()));
                              if (e.key === 'ArrowDown') { e.preventDefault(); setDropdownVisible(prev => ({ ...prev, product: true })); setDropdownFocusIdx(prev => ({ ...prev, product: Math.min(prev.product + 1, filtered.length - 1) })); }
                              else if (e.key === 'ArrowUp') { e.preventDefault(); setDropdownFocusIdx(prev => ({ ...prev, product: Math.max(prev.product - 1, -1) })); }
                              else if (e.key === 'Enter' && dropdownFocusIdx.product >= 0) { const s = filtered[dropdownFocusIdx.product]; if (s) { setFormData(prev => ({ ...prev, selectedProducts: [...prev.selectedProducts, s.id] })); setSearchTerms(prev => ({ ...prev, product: '' })); setDropdownFocusIdx(prev => ({ ...prev, product: -1 })); } }
                              else if (e.key === 'Escape') { setDropdownVisible(prev => ({ ...prev, product: false })); }
                            }}
                          />
                          {dropdownVisible.product && (
                            <div className="searchable-dropdown" onMouseDown={(e) => e.preventDefault()} onTouchStart={(e) => e.preventDefault()}>
                              {products.filter(p => !formData.selectedProducts.includes(p.id) && (p.name || '').toLowerCase().includes(searchTerms.product.toLowerCase())).map((p, idx) => (
                                <div key={p.id} className="dropdown-item" style={{ background: idx === dropdownFocusIdx.product ? '#e8f0fb' : '' }} onClick={(e) => { e.stopPropagation(); setFormData(prev => ({ ...prev, selectedProducts: [...prev.selectedProducts, p.id] })); setSearchTerms(prev => ({ ...prev, product: '' })); setDropdownVisible(prev => ({ ...prev, product: false })); setDropdownFocusIdx(prev => ({ ...prev, product: -1 })); }}>{p.name}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className='Vendor-wrapper'>
                <div className='vendor-header' onClick={() => toggleSection('vendorDetails')}>
                  <h2 style={{ color: "#333" }}>Vendor Details</h2>
                  <span style={{ fontSize: "12px" }}>{collapsed.vendorDetails ? <FaChevronDown /> : <FaChevronUp />}</span>
                </div>
                {!collapsed.vendorDetails && (
                  <div className='card-panel' style={{ padding: "16px", margin: 0 }}>
                    <div className="form-grid" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
                      <div className="form-group" style={{ position: 'relative' }}>
                        <label style={{ justifyContent : "space-between"}}><span>Select Vendor <span className="required">*</span> </span><span style={{ color: '#437cf0', border: '1px solid #437cf0', padding: '2px 3px', display : " flex" , alignItems : "center" , fontSize : "10px" , justifyContent : "center" , borderRadius: '3px', cursor: 'pointer' }} onClick={() => { setVendorModalDragged(false); setShowAddVendorModal(true); }} title="Add new vendor"><FiPlus /></span></label>
                        <div className="searchable-select-container" onClick={() => !formData.manufacturerVendor && setDropdownVisible(prev => ({ ...prev, vendor: true }))}>
                          {formData.manufacturerVendor ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', color: '#000', fontSize: '14px' }}>
                              <span style={{ flex: 1, fontStyle: formData.manufacturerVendor === 'TBD' ? 'italic' : 'normal', color: formData.manufacturerVendor === 'TBD' ? '#888' : '#000' }}>
                                {formData.manufacturerVendor === 'TBD'
                                  ? 'To Be Determined'
                                  : (vendors.find(v => v.id === formData.manufacturerVendor)?.vendorName || vendors.find(v => v.id === formData.manufacturerVendor)?.companyName)}
                              </span>
                              <button type="button" onClick={(e) => { e.stopPropagation(); setFormData(prev => ({ ...prev, manufacturerVendor: '', country: '', state: '', city: '' })); }} style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '18px', padding: '0 4px' }}>×</button>
                            </div>
                          ) : (
                            <input
                              type="text" className="search-input" placeholder="Select Vendor" value={searchTerms.vendor}
                              onChange={(e) => { setSearchTerms(prev => ({ ...prev, vendor: e.target.value })); setDropdownFocusIdx(prev => ({ ...prev, vendor: -1 })); }}
                              onFocus={() => setDropdownVisible(prev => ({ ...prev, vendor: true }))}
                              onBlur={() => setTimeout(() => { setDropdownVisible(prev => ({ ...prev, vendor: false })); setDropdownFocusIdx(prev => ({ ...prev, vendor: -1 })); }, 200)}
                              onKeyDown={(e) => {
                                const filtered = vendors.filter(v => (v.vendorName || v.companyName || '').toLowerCase().includes(searchTerms.vendor.toLowerCase()));
                                if (e.key === 'ArrowDown') { e.preventDefault(); setDropdownVisible(prev => ({ ...prev, vendor: true })); setDropdownFocusIdx(prev => ({ ...prev, vendor: Math.min(prev.vendor + 1, filtered.length - 1) })); }
                                else if (e.key === 'ArrowUp') { e.preventDefault(); setDropdownFocusIdx(prev => ({ ...prev, vendor: Math.max(prev.vendor - 1, -1) })); }
                                else if (e.key === 'Enter' && dropdownFocusIdx.vendor >= 0) { const s = filtered[dropdownFocusIdx.vendor]; if (s) { setFormData(prev => ({ ...prev, manufacturerVendor: s.id, country: s.country || '', state: s.state || '', city: s.city || '' })); setSearchTerms(prev => ({ ...prev, vendor: '' })); setDropdownVisible(prev => ({ ...prev, vendor: false })); setDropdownFocusIdx(prev => ({ ...prev, vendor: -1 })); } }
                                else if (e.key === 'Escape') { setDropdownVisible(prev => ({ ...prev, vendor: false })); }
                              }}
                              style={{ width: '100%' }}
                            />
                          )}
                          {dropdownVisible.vendor && (
                            <div className="searchable-dropdown" onMouseDown={(e) => e.preventDefault()} onTouchStart={(e) => e.preventDefault()}>
                              {(!searchTerms.vendor || 'tbd'.includes(searchTerms.vendor.toLowerCase())) && (
                                <div
                                  className="dropdown-item"
                                  style={{ fontStyle: 'italic', color: '#888', borderBottom: '1px solid #eee' }}
                                  onClick={(e) => { e.stopPropagation(); setFormData(prev => ({ ...prev, manufacturerVendor: 'TBD', country: '', state: '', city: '' })); setSearchTerms(prev => ({ ...prev, vendor: '' })); setDropdownVisible(prev => ({ ...prev, vendor: false })); setDropdownFocusIdx(prev => ({ ...prev, vendor: -1 })); }}
                                >
                                  To Be Determined
                                </div>
                              )}
                              {vendors.filter(v => (v.vendorName || v.companyName || '').toLowerCase().includes(searchTerms.vendor.toLowerCase())).map((v, idx) => (
                                <div key={v.id} className="dropdown-item" style={{ background: idx === dropdownFocusIdx.vendor ? '#e8f0fb' : '' }} onClick={(e) => { e.stopPropagation(); setFormData(prev => ({ ...prev, manufacturerVendor: v.id, country: v.country || '', state: v.state || '', city: v.city || '' })); setSearchTerms(prev => ({ ...prev, vendor: '' })); setDropdownVisible(prev => ({ ...prev, vendor: false })); setDropdownFocusIdx(prev => ({ ...prev, vendor: -1 })); }}>{v.vendorName || v.companyName}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Full Address (Country, State, City)</label>
                        <input type="text" className="form-control" name="fullAddress" value={`${formData.country}${formData.state ? ', ' + formData.state : ''}${formData.city ? ', ' + formData.city : ''}`} disabled placeholder="Auto-populated (Country, State, City)" />
                      </div>
                      <div className="form-group">
                        <label>Vendor Full Address</label>
                        <input type="text" className="form-control" value={vendors.find(v => v.id === formData.manufacturerVendor)?.address || ''} disabled placeholder="Auto-populated from Vendor Master" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Client Documents — client-facing paperwork (IR, NOT, ITP, ...), PDF/Word/Excel/ZIP/RAR only */}
            <div className='clientdoc-wrapper'>
              <div className='clientdoc-header' onClick={() => toggleSection('clientDocuments')}>
                <h2 style={{ color: "#333" }}>Client Documents</h2>
                <span style={{ fontSize: "12px" }}>{collapsed.clientDocuments ? <FaChevronDown /> : <FaChevronUp />}</span>
              </div>
              {!collapsed.clientDocuments && (
                <div className='card-panel' style={{ padding: 0, margin: 0 }}>
                  <input ref={addClientDocFileRef} type="file" accept={CLIENT_DOC_ACCEPT} style={{ display: 'none' }} disabled={!!editingClientDocumentId} onChange={e => { const f = e.target.files?.[0]; if (!f) return; if (!CLIENT_DOC_EXT_RE.test(f.name)) { toast.warning('Only PDF, Word, Excel, ZIP or RAR files are allowed.'); e.target.value = ''; return; } setCurrentClientDocument(prev => ({ ...prev, file: f })); }} />
                  {!isView && (
                    <div className="add-clientdoc-form form-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr) auto', padding: "16px 16px 0", alignItems: 'flex-end' }}>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>Document Type <span className="required">*</span></label>
                        <select className="form-control" value={currentClientDocument.documentType} onChange={(e) => handleClientDocumentChange('documentType', e.target.value)} required>
                          <option value="">Select Type</option>
                          <option value="NOI">NOI</option>
                          <option value="ITP">ITP</option>
                          <option value="Document">Document</option>
                        </select>
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}><label>Number</label><input type="text" className="form-control" value={currentClientDocument.number} onChange={(e) => handleClientDocumentChange('number', e.target.value)} /></div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>Upload File</label>
                        <button type="button" className="form-control" disabled={!!editingClientDocumentId} title="PDF, Word, Excel, ZIP or RAR only" style={{ textAlign: 'left', cursor: editingClientDocumentId ? 'not-allowed' : 'pointer', fontSize: 11, height: '33px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', color: currentClientDocument.file ? '#1a7e4a' : '#888', opacity: editingClientDocumentId ? 0.5 : 1 }} onClick={() => addClientDocFileRef.current.click()}>
                          <FaUpload size={10} /> {editingClientDocumentId ? 'Use row Upload button' : (currentClientDocument.file ? currentClientDocument.file.name : 'PDF / Word / Excel…')}
                        </button>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
                        <button className="add-more-btn" onClick={addClientDocument} disabled={addingClientDocument} title={editingClientDocumentId ? 'Save changes' : (currentClientDocument.file && id ? 'Add row and upload to OneDrive' : 'Add row')}>
                          {addingClientDocument
                            ? <><FaUpload size={12} /> Uploading…</>
                            : editingClientDocumentId ? <><FiCheck size={12} /> Update</> : <><FaPlus size={12} /> Add</>}
                        </button>
                        {editingClientDocumentId && (
                          <button type="button" className="add-more-btn" onClick={cancelEditClientDocument} style={{ background: '#6b7280' }}>
                            <FiX size={12} /> Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="table-responsive" style={{ padding: "16px", margin: 0, width: 'auto' }}>
                    <table className="data-table">
                      <thead>
                        <tr><th>Document Type</th><th>Number</th><th>File</th>{!isView && <th>Actions</th>}</tr>
                      </thead>
                      <tbody>
                        {clientDocuments.length === 0
                          ? <tr><td colSpan="4" style={{ textAlign: 'center' }}>No client documents added.</td></tr>
                          : clientDocuments.map(d => {
                              const rowKey = `c_${d._id}`;
                              const isUploading = uploadingRows[rowKey];
                              const cProgress = uploadProgress[rowKey] ?? 0;
                              return (
                                <tr key={d._id || d.id}>
                                  <td>{d.documentType}</td>
                                  <td>{d.number || '—'}</td>
                                  <td>
                                    {isUploading ? (
                                      <div style={{ minWidth: 140 }}>
                                        <div style={{ fontSize: 11, color: '#555', marginBottom: 3 }}>
                                          {cProgress >= 100 ? '✓ Done!' : cProgress >= 95 ? 'Saving to SharePoint…' : `Uploading ${cProgress}%`}
                                        </div>
                                        <div style={{ background: '#e0e0e0', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                                          <div style={{ width: `${cProgress}%`, background: cProgress >= 100 ? '#27ae60' : '#437cf0', height: 6, borderRadius: 4, transition: 'width 0.3s ease' }} />
                                        </div>
                                      </div>
                                    ) : (
                                      <span style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                                        {d.fileUrl && (
                                          <a href={d.fileUrl} target="_blank" rel="noopener noreferrer" style={{ background: '#007bff', color: '#fff', textDecoration: 'none', padding: '6px', border: '0', borderRadius : 4 , height : "fit-content" , display : "flex" }}>
                                            <FiEye size={14} />
                                          </a>
                                        )}
                                        {d._id && !isView && (
                                          <button className="" style={{ background: '#e67e22', color: '#fff', padding: '6px 12px', border: '0', display: 'flex', alignItems: 'center', gap: '4px' , borderRadius: "4px" }} onClick={() => handleUploadClick('clientDocument', d._id)}>
                                            <FaUpload size={10} /> {d.fileUrl ? 'Update' : 'Upload'}
                                          </button>
                                        )}
                                        {!d.fileUrl && !d._id && <span style={{ fontSize: 11, color: '#aaa' }}>—</span>}
                                      </span>
                                    )}
                                  </td>
                                  {!isView && (
                                    <td style={{ display: 'flex', gap: 4 }}>
                                      <button className="action-btn" style={{ background: '#28a745', color: '#fff', padding: '6px', border: '0', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: "fit-content" }} onClick={() => startEditClientDocument(d)} title="Edit"><FiEdit /></button>
                                      <button className="action-btn delete-btn" style={{ background: '#dc3545', color: '#fff', padding: '6px', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: "fit-content" }} onClick={() => deleteClientDocument(d.id || d._id)} title="Delete"><FaTrash /></button>
                                    </td>
                                  )}
                                </tr>
                              );
                            })
                        }
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </fieldset>
      )}

      {/* Suggested Inspectors Section */}
      <div className="card-panel mobile-friendly" style={{ padding: "0" }}>
        <div className="section-header" style={{ background: "linear-gradient(to bottom, #f7f7f7 0%, #dcdcdc 100%)", padding: "10px 16px", margin: 0, cursor: 'pointer' }} onClick={() => toggleSection('suggestedInspectors')}>
          <h3 style={{ fontWeight: 500 }}><FaUsers color="#333" /> Suggested Inspectors</h3>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {!isView && <button type="button" className="new-inspector" onClick={(e) => { e.stopPropagation(); setInspModalDragged(false); setShowAddInspectorModal(true); }}> + <span className="desktop-only">New</span></button>}
            {!isView && <button type="button" className="suggestion-btn" onClick={(e) => { e.stopPropagation(); suggestInspector(); setSuggestionModalDragged(false); setInspPage(1); setShowSuggestionModal(true); }}> <FaRegLightbulb /> <span className="desktop-only">Find Suggestions (City/Competency)</span> </button>}
            <span style={{ fontSize: "16px" }}>{collapsed.suggestedInspectors ? <FaChevronDown /> : <FaChevronUp />}</span>
          </div>
        </div>
        {!collapsed.suggestedInspectors && (
          <div style={{ padding: '16px' }}>
            <div className="table-responsive" style={{ padding: 0, margin: 0 }}>
              <table className="data-table">
                <thead><tr><th>Inspector Name</th><th>Contact Number</th><th>Email</th><th>Mail</th><th>Actions</th></tr></thead>
                <tbody>
                  {inspectors.length === 0 ? (
                    <tr><td colSpan="5" style={{ textAlign: 'center', color: '#888' }}>No inspectors added.</td></tr>
                  ) : (
                    inspectors.map((insp) => (
                      <tr key={insp.id}>
                        <td>{getDisplayName(insp.inspectorId, insp.inspectorName || insp.inspectorId, insp.id)}</td>
                        <td>{insp.contactNumber}</td>
                        <td>{insp.email || '—'}</td>
                        <td>
                          {insp.mailSent ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ background: '#28a745', color: '#fff', padding: '5px 10px', borderRadius: 4, fontSize: 12, whiteSpace: 'nowrap' }}>
                                Sent {formatDate(insp.mailSentAt)}
                              </span>
                              {!isView && insp.email && (
                                <button
                                  type="button"
                                  title="Resend"
                                  onClick={() => openInviteModal({ ...insp, inspectorName: getDisplayName(insp.inspectorId, insp.inspectorName || insp.inspectorId, insp.id) }, 'resend')}
                                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, padding: '6px 7px', border: 'none', borderRadius: 4, background: '#1a3a6b', cursor: 'pointer', color: '#fff' }}
                                >
                                  <BsSendPlusFill />
                                </button>
                              )}
                            </span>
                          ) : <span style={{ color: '#999', fontSize: 12 }}>—</span>}
                        </td>
                        <td>
                          {!isView ? (
                            <div className="table-actions" style={{ display: 'flex', gap: '8px' }}>
                              <button className="action-btn" disabled={!!insp.workOrder} style={{ background: '#28a745', height: "fit-content" , color: '#fff', padding: '6px 12px', border: 'none', fontSize: '12px', opacity: insp.workOrder ? 0.5 : 1, cursor: insp.workOrder ? 'not-allowed' : 'pointer' }} onClick={() => { handleInspectorFieldChange(insp.id, 'status', 'CONFIRM'); setShowConfirmTable(true); }}>Confirm</button>
                              <button className="action-btn delete-btn" onClick={() => deleteInspector(insp.id)} disabled={insp.status === 'APPROVED' || !!insp.workOrder} style={{ opacity: (insp.status === 'APPROVED' || insp.workOrder) ? 0.5 : 1, cursor: (insp.status === 'APPROVED' || insp.workOrder) ? 'not-allowed' : 'pointer' , background: '#dc3545', color: '#fff', padding: '6px', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: "fit-content" }}><FaTrash /></button>
                            </div>
                          ) : <span style={{ color: '#888', fontSize: '12px' }}>{insp.status}</span>}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Inspector Confirm Table & Financials */}
      {(showConfirmTable || isAdd) && confirmedInspectors.length > 0 && (
        <div className="card-panel mobile-friendly" style={{ marginTop: "16px", padding: 0 }}>
          <div className="section-header" style={{ background: "linear-gradient(to bottom, #f7f7f7 0%, #dcdcdc 100%)", padding: "10px 16px", margin: 0, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ fontWeight: 500 }}><FaFileInvoiceDollar color="#333" /> Inspector Confirm Table & Financials</h3>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {/* Visible to every role (unlike the Cost/Offered INR & Margin columns below) */}
              <div
                style={{ fontSize: '12px', color: headerIncentiveRate ? '#1a7e4a' : '#999', whiteSpace: 'nowrap', border: "1px solid #1a7e4a" , padding: "2px 6px" , background : "#fff" }}
              >
                Incentive : <strong>{headerIncentiveRate == null ? '—' : `₹${headerIncentiveRate}`}</strong>
              </div>
            </div>
          </div>
          <fieldset disabled={isView} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0, width: '100%' }}>
            <div style={{ overflowX: 'auto', margin: '16px' }}>
              <table className="data-table" style={{ minWidth: '100%', width: 'max-content' }}>
                <thead><tr><th>Inspector Name</th><th>Inspection Dates</th><th>Absent</th><th>Cost Currency</th><th>Cost</th>{canSeePricing && <th>Cost INR</th>}<th>Offered Currency</th><th>Offered Price</th>{canSeePricing && <th>Offered INR</th>}{canSeePricing && <th>Margin (%)</th>}<th>Status</th></tr></thead>
                <tbody>
                  {confirmedInspectors.map((insp) => {
                    const offeredINR = parseFloat(insp.offeredINR) || 0;
                    const costINR = parseFloat(insp.costINR) || 0;
                    const calculatedMargin = offeredINR > 0 ? (((offeredINR - costINR) / offeredINR) * 100).toFixed(2) : 0;
                    // A date another inspector is marked Absent for isn't "taken" — free
                    // to assign to this inspector instead.
                    const takenByOthers = new Set(
                      confirmedInspectors
                        .filter(other => other.id !== insp.id)
                        .flatMap(other => {
                          const otherDates = Array.isArray(other.inspectionDates) ? other.inspectionDates : (other.inspectionDates ? [other.inspectionDates] : []);
                          const otherAbsent = new Set(Array.isArray(other.absentDates) ? other.absentDates : []);
                          return otherDates.filter(d => !otherAbsent.has(d));
                        })
                    );
                    const invoicedDates = new Set(invoicedDatesFor(insp));
                    const invoiceUploaded = isInspectorInvoiceUploaded(insp);
                    const ownDates = new Set(Array.isArray(insp.inspectionDates) ? insp.inspectionDates : (insp.inspectionDates ? [insp.inspectionDates] : []));
                    // Finance-invoice lock (isInspectorLocked): once Finance has created an
                    // invoice against this inspector's dates, those dates/pricing can't change.
                    // Inspector-invoice-uploaded lock only locks Cost/Cost Currency — everything
                    // else (dates, offered pricing) stays editable.
                    const financeLocked = isInspectorLocked(insp);
                    const costLocked = financeLocked || invoiceUploaded;
                    const costLockedTitle = invoiceUploaded ? 'Locked — inspector invoice uploaded' : financeLocked ? 'Locked — invoice created for this inspector' : undefined;
                    const financeLockedTitle = financeLocked ? 'Locked — invoice created for this inspector' : undefined;
                    // Invoiced dates must survive any edit of the date list
                    const keepInvoiced = (arr) => Array.from(new Set([...invoicedDatesFor(insp), ...(arr || [])])).sort();
                    return (
                      <tr key={insp.id}>
                        <td><button onClick={(e) => { e.preventDefault(); const fullInsp = dbInspectors.find(d => d.id === insp.inspectorId || d._id === insp.inspectorId); setViewingInspector(fullInsp || insp); setShowViewInspectorModal(true); }} style={{ background: 'none', border: 'none', color: '#437cf0', cursor: 'pointer', textDecoration: 'underline', padding: 0, fontWeight: 500 }}>{getDisplayName(insp.inspectorId, insp.inspectorName || insp.inspectorId, insp.id)}</button></td>
                        <td style={{ minWidth: '150px' }}>
                          <DatePicker
                            multiple
                            format="DD-MM-YYYY"
                            value={(Array.isArray(insp.inspectionDates) ? insp.inspectionDates : (insp.inspectionDates ? [insp.inspectionDates] : [])).map(d => new Date(d)).filter(d => !isNaN(d))}
                            onChange={(dates) => { const arr = keepInvoiced(dates ? dates.map(d => d.format("YYYY-MM-DD")) : []); applyAttendanceChange(insp, arr, insp.absentDates); }}
                            mapDays={makeShiftRangeMapDays({
                              getValues: () => Array.isArray(insp.inspectionDates) ? insp.inspectionDates : (insp.inspectionDates ? [insp.inspectionDates] : []),
                              setValues: (arr) => { const merged = keepInvoiced(arr); applyAttendanceChange(insp, merged, insp.absentDates); },
                              anchorRef: {
                                get current() { return inspectionAnchorsRef.current[insp.id]; },
                                set current(v) { inspectionAnchorsRef.current[insp.id] = v; },
                              },
                              isAllowed: (d) => allowedDates.includes(d) && !takenByOthers.has(d),
                              isLocked: (d) => invoicedDates.has(d),
                              lockedTitle: financeLockedTitle,
                            })}
                            title={costLockedTitle}
                            style={{ width: 'auto', padding: '2px 6px', borderRadius: 0 }}
                          />
                        </td>
                        <td style={{ minWidth: '150px' }}>
                          {/* Only meaningful once a work order exists — marks which planned
                              dates the inspector was actually absent for, shrinking
                              billing.days/amount/total immediately (applyAttendanceChange). */}
                          {!!insp.workOrder && ownDates.size > 0 ? (
                            <DatePicker
                              multiple
                              format="DD-MM-YYYY"
                              value={(Array.isArray(insp.absentDates) ? insp.absentDates : []).map(d => new Date(d)).filter(d => !isNaN(d))}
                              onChange={(dates) => applyAttendanceChange(insp, insp.inspectionDates, dates ? dates.map(d => d.format("YYYY-MM-DD")) : [])}
                              mapDays={({ date }) => {
                                const d = date.format('YYYY-MM-DD');
                                if (!ownDates.has(d)) return { disabled: true, style: { color: '#ccc' } };
                                // Already invoiced (Finance) or the inspector's own invoice was
                                // uploaded for this date — can't retroactively mark absent.
                                if (invoicedDates.has(d) || invoiceUploaded) return { disabled: true, style: { color: '#ccc' } };
                                return { style: { color: '#c0392b' } };
                              }}
                              title={costLockedTitle || 'Mark dates the inspector didn’t actually attend'}
                              placeholder="None"
                              style={{ width: 'auto', padding: '2px 6px', borderRadius: 0 }}
                            />
                          ) : (
                            <span style={{ color: '#ccc', fontSize: 12 }}>—</span>
                          )}
                        </td>
                        <td>
                          <select className="form-control" style={{ width: '85px', fontSize: '12px' }} disabled={costLocked} title={costLockedTitle} value={insp.currencyCost} onChange={(e) => { handleInspectorFieldChange(insp.id, 'currencyCost', e.target.value); if (insp.cost) { const newCostINR = (parseFloat(insp.cost) * (CURRENCY_RATES[e.target.value] || 1)).toFixed(2); handleInspectorFieldChange(insp.id, 'costINR', newCostINR); if (isModalForInspector(insp.id)) syncCostToModal(insp.cost); } }}>
                            {Object.keys(CURRENCY_RATES).map(curr => <option key={curr} value={curr}>{curr}</option>)}
                          </select>
                        </td>
                        <td><input type="number" className="form-control" style={{ width: '80px', fontSize: '12px' }} disabled={costLocked} title={costLockedTitle} value={insp.cost ?? ''} onChange={(e) => { handleInspectorFieldChange(insp.id, 'cost', e.target.value); const newCostINR = (parseFloat(e.target.value) * (CURRENCY_RATES[insp.currencyCost] || 1)).toFixed(2); handleInspectorFieldChange(insp.id, 'costINR', newCostINR); if (isModalForInspector(insp.id)) syncCostToModal(e.target.value); }} /></td>
                        {canSeePricing && <td><input type="number" className="form-control" style={{ width: '80px', fontSize: '12px' }} value={insp.costINR ?? ''} disabled /></td>}
                        <td>
                          <select className="form-control" style={{ width: '85px', fontSize: '12px' }} disabled={financeLocked} title={financeLockedTitle} value={insp.currencyOffered} onChange={(e) => { handleInspectorFieldChange(insp.id, 'currencyOffered', e.target.value); if (insp.offered) handleInspectorFieldChange(insp.id, 'offeredINR', (parseFloat(insp.offered) * (CURRENCY_RATES[e.target.value] || 1)).toFixed(2)); }}>
                            {Object.keys(CURRENCY_RATES).map(curr => <option key={curr} value={curr}>{curr}</option>)}
                          </select>
                        </td>
                        <td><input type="number" className="form-control" style={{ width: '80px', fontSize: '12px' }} disabled={financeLocked} title={financeLockedTitle} value={insp.offered ?? ''} onChange={(e) => { handleInspectorFieldChange(insp.id, 'offered', e.target.value); handleInspectorFieldChange(insp.id, 'offeredINR', (parseFloat(e.target.value) * (CURRENCY_RATES[insp.currencyOffered] || 1)).toFixed(2)); }} /></td>
                        {canSeePricing && <td><input type="number" className="form-control" style={{ width: '80px', fontSize: '12px' }} value={insp.offeredINR ?? ''} disabled /></td>}
                        {canSeePricing && <td><input type="text" className="form-control" style={{ width: '55px', fontSize: '12px' }} value={calculatedMargin} disabled /></td>}
                        <td>
                          <select className="form-control" value={insp.status} style={{ fontSize: '12px', background: insp.workOrder ? '#f5f5f5' : '', cursor: insp.workOrder ? 'not-allowed' : '' }} disabled={!!insp.workOrder} onChange={(e) => handleInspectorFieldChange(insp.id, 'status', e.target.value)}>
                            <option value="APPROACHED">Approached</option><option value="APPROVED">Approved</option><option value="REJECT">Reject</option>
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </fieldset>
        </div>
      )}

      {/* WON Status Sections */}
      {formData.status === 'WON' && (
        <>
          {/* Work Order Section */}
          {inspectors.filter(i => i.status === 'APPROVED').length > 0 && (
            <div className="card-panel mobile-friendly" style={{ padding: 0 }}>
              <div className="section-header" style={{ background: "linear-gradient(rgb(247, 247, 247) 0%, rgb(220, 220, 220) 100%)", padding: "10px 16px", margin: 0 }}><h3 style={{ fontWeight: 500 }}><IoNewspaperSharp color="#333" /> Work Order</h3></div>
              <div className="table-responsive" style={{ padding: "16px", width: "auto", margin: 0 }}>
                <table className="data-table">
                  <thead><tr><th>Date</th><th>Inspector Name</th><th>Work Order Number</th><th>Signed WO</th><th>Actions</th></tr></thead>
                  <tbody>
                    {inspectors.filter(i => i.status === 'APPROVED').map((insp) => (
                      <tr key={insp.id}>
                        <td>
                          <input
                            type="date"
                            className="form-control"
                            style={{ width: '120px', padding: '5px', backgroundColor: insp.workOrder ? '#f5f5f5' : '', cursor: insp.workOrder ? 'not-allowed' : '' }}
                            value={(insp.workOrderDate ? String(insp.workOrderDate).slice(0, 10) : '') || new Date().toISOString().split('T')[0]}
                            onChange={(e) => handleInspectorFieldChange(insp.id, 'workOrderDate', e.target.value)}
                            disabled={!!insp.workOrder}
                            title={insp.workOrder ? 'Locked after work order generation' : ''}
                          />
                        </td>
                        <td>{getDisplayName(insp.inspectorId, insp.inspectorName || insp.inspectorId, insp.id)}</td>
                        <td style={{ fontWeight: 500, color: insp.workOrder ? '#2c3e50' : '#999' }}>{insp.workOrder || 'Not Generated'}</td>
                        <td>
                          {insp.workOrder ? (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {insp.signedWorkOrderUrl && (
                                <a href={insp.signedWorkOrderUrl} target="_blank" rel="noopener noreferrer" className="action-btn" style={{ background: '#6f42c1', color: '#fff', textDecoration: 'none', padding: '6px 12px', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', gap: '4px', height: '16px' }}><FiEye size={12} /> View Signed</a>
                              )}
                              <button className="action-btn" style={{ background: '#e67e22', color: '#fff', padding: '6px 12px', borderRadius: '4px' }} onClick={() => { setSigningInspector(insp); setSignedWOFile(null); setShowSignedWOModal(true); }}>Upload Signed</button>
                            </div>
                          ) : <span style={{ color: '#999', fontSize: '12px' }}>—</span>}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            {insp.workOrderUrl && (
                              <a href={insp.workOrderUrl} target="_blank" rel="noopener noreferrer" className="action-btn" title="View" style={{ background: '#0078d4', color: '#fff', padding: '6px', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' , height: "fit-content" }}><FiEye size={14} /></a>
                            )}
                            {insp.workOrder ? (
                              <button className="action-btn" title="Update" style={{ background: '#28a745', color: '#fff', padding: '6px', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: "fit-content" }} onClick={() => openWorkOrderModal(insp)}><FiEdit size={14} /></button>
                            ) : (
                              <button className="action-btn" title="Generate" style={{ background: '#437cf0', color: '#fff', padding: '6px', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: "fit-content" }} onClick={() => openWorkOrderModal(insp)}><FiPlus size={14} /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {/* Document Upload Section */}
          {/* Hidden file input — shared by deliverable and invoice upload buttons */}
          <input ref={uploadInputRef} type="file" style={{ display: 'none' }} onChange={handleFileSelected} />
          <div className="card-panel mobile-friendly" style={{ padding: 0 }}>
            <div className="section-header" style={{ background: "linear-gradient(rgb(247, 247, 247) 0%, rgb(220, 220, 220) 100%)", padding: "10px 16px", margin: 0 }}>
              <h3 style={{ fontWeight: 500 }}><FaFolderOpen color="#333" /> Document Upload</h3>
            </div>
            {!isView && (
              <div className="add-deliverable-form form-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr) auto', padding: "16px", alignItems: 'flex-end' }}>
                <div className="form-group" style={{ marginBottom: 0 }}><label>Inspector Name <span className="required">*</span></label><select className="form-control" value={currentDeliverable.inspectorName} onChange={(e) => setCurrentDeliverable(prev => ({ ...prev, inspectorName: e.target.value, date: [] }))} required><option value="">Select Inspector</option>{approvedInspectors.map(i => { const name = getDisplayName(i.inspectorId, i.inspectorName || i.inspectorId, i.id); return <option key={i.id} value={name}>{name}</option>; })}</select></div>
                <div className="form-group" style={{ marginBottom: 0 }}><label>Document Type <span className="required">*</span></label><select className="form-control" value={currentDeliverable.documentType} onChange={(e) => handleDeliverableChange('documentType', e.target.value)} required><option value="">Select Type</option><option value="Report">Report</option><option value="IRN">IRN</option><option value="Flash Report">Flash Report</option><option value="SD">SD</option><option value="Inspection Photos">Inspection Photos</option></select></div>
                <div className="form-group" style={{ marginBottom: 0 }}><label>Number <span className="required">*</span></label><input type="text" className="form-control" value={currentDeliverable.number} onChange={(e) => handleDeliverableChange('number', e.target.value)} required /></div>
                <div className="form-group" style={{ marginBottom: 0 }}><label>Date <span className="required">*</span></label>
                  <DatePicker
                    multiple
                    format="DD-MM-YYYY"
                    value={(Array.isArray(currentDeliverable.date) ? currentDeliverable.date : (currentDeliverable.date ? [currentDeliverable.date] : [])).map(d => new Date(d)).filter(d => !isNaN(d))}
                    onChange={(ds) => handleDeliverableChange('date', ds ? ds.map(d => d.format("YYYY-MM-DD")) : [])}
                    mapDays={({ date }) => {
                      if (!deliverableAllowedDates.includes(date.format("YYYY-MM-DD")))
                        return { disabled: true, style: { color: '#ccc' } };
                    }}
                    disabled={!currentDeliverable.inspectorName}
                    placeholder={currentDeliverable.inspectorName ? 'Select dates' : 'Select inspector first'}
                    containerStyle={{ width: '100%', display: 'block' }}
                    style={{ width: '100%', height: '33px', border: '1px solid #ccc', margin: 0, padding: '0 8px', fontSize: 13, boxSizing: 'border-box' , borderRadius : '0px' }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Upload File</label>
                  <input ref={addFileRef} type="file" style={{ display: 'none' }} disabled={!!editingDeliverableId} onChange={e => { const f = e.target.files?.[0]; if (f) setCurrentDeliverable(prev => ({ ...prev, file: f })); }} />
                  <button type="button" className="form-control" disabled={!!editingDeliverableId} style={{ textAlign: 'left', cursor: editingDeliverableId ? 'not-allowed' : 'pointer', fontSize: 11, height: '33px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', color: currentDeliverable.file ? '#1a7e4a' : '#888', opacity: editingDeliverableId ? 0.5 : 1 }} onClick={() => addFileRef.current.click()}>
                    <FaUpload size={10} /> {editingDeliverableId ? 'Use row Upload button' : (currentDeliverable.file ? currentDeliverable.file.name : 'Choose file…')}
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
                  <button className="add-more-btn" onClick={addDeliverable} disabled={addingDeliverable} title={editingDeliverableId ? 'Save changes' : (currentDeliverable.file && id ? 'Add row and upload to OneDrive' : 'Add row')}>
                    {addingDeliverable
                      ? <><FaUpload size={12} /> Uploading…</>
                      : editingDeliverableId ? <><FiCheck size={12} /> Update</> : <><FaPlus size={12} /> Add</>}
                  </button>
                  {editingDeliverableId && (
                    <button type="button" className="add-more-btn" onClick={cancelEditDeliverable} style={{ background: '#6b7280' }}>
                      <FiX size={12} /> Cancel
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="table-responsive" style={{ padding: "16px", width: 'auto', margin: 0 }}>
              <table className="data-table">
                <thead>
                  <tr><th>Inspector Name</th><th>Document Type</th><th>Number</th><th>Date</th><th>File</th>{!isView && <th>Actions</th>}</tr>
                </thead>
                <tbody>
                  {deliverables.length === 0
                    ? <tr><td colSpan="6" style={{ textAlign: 'center' }}>No deliverables added.</td></tr>
                    : deliverables.map(d => {
                        const isUploading = uploadingRows[`d_${d._id}`];
                        const dProgress = uploadProgress[`d_${d._id}`] ?? 0;
                        return (
                          <tr key={d._id || d.id}>
                            <td>{d.inspectorName ? (dbInspectors.find(i => String(i.id) === d.inspectorName || String(i._id) === d.inspectorName)?.inspectorName || d.inspectorName) : '—'}</td>
                            <td>{d.documentType}</td>
                            <td>{d.number}</td>
                            <td>{Array.isArray(d.date) ? d.date.map(formatDate).join(', ') : formatDate(d.date)}</td>
                            <td>
                              {isUploading ? (
                                <div style={{ minWidth: 140 }}>
                                  <div style={{ fontSize: 11, color: '#555', marginBottom: 3 }}>
                                    {dProgress >= 100 ? '✓ Done!' : dProgress >= 95 ? 'Saving to SharePoint…' : `Uploading ${dProgress}%`}
                                  </div>
                                  <div style={{ background: '#e0e0e0', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                                    <div style={{ width: `${dProgress}%`, background: dProgress >= 100 ? '#27ae60' : '#437cf0', height: 6, borderRadius: 4, transition: 'width 0.3s ease' }} />
                                  </div>
                                </div>
                              ) : (
                                <span style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                                  {d.fileUrl && (
                                    <a href={d.fileUrl} target="_blank" rel="noopener noreferrer" className="" style={{ background: '#0078d4', color: '#fff', textDecoration: 'none' , padding: '6px', border: '0', display: 'flex', alignItems: 'center', gap: '4px' , borderRadius : 4 }}>
                                      <FiEye size={14} />
                                    </a>
                                  )}
                                  {d._id && !isView && (
                                    <button className="" style={{ background: '#e67e22', color: '#fff' , padding: '4px 12px', border: '0', display: 'flex', alignItems: 'center', gap: '4px' , borderRadius: '4px' }} onClick={() => handleUploadClick('deliverable', d._id)}>
                                      <FaUpload size={10} /> {d.fileUrl ? 'Update' : 'Upload'}
                                    </button>
                                  )}
                                  {!d.fileUrl && !d._id && <span style={{ fontSize: 11, color: '#aaa' }}>—</span>}
                                </span>
                              )}
                            </td>
                            {!isView && (
                              <td style={{ display: 'flex', gap: 4 }}>
                                <button className="action-btn" style={{ height: 'fit-content' , padding : 6 , borderRadius : 4 , background: '#28a745', color: '#fff' }} onClick={() => startEditDeliverable(d)} title="Edit"><FiEdit /></button>
                                <button className="action-btn delete-btn" style={{ background: '#dc3545', color: '#fff', padding: '6px', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: "fit-content" }} onClick={() => deleteDeliverable(d.id || d._id)} title="Delete"><FaTrash /></button>
                              </td>
                            )}
                          </tr>
                        );
                      })
                  }
                </tbody>
              </table>
            </div>
          </div>
          {/* Inspection Invoice Section */}
          <div className="card-panel mobile-friendly" style={{ padding: 0 }}>
            <div className="section-header" style={{ background: "linear-gradient(rgb(247, 247, 247) 0%, rgb(220, 220, 220) 100%)", padding: "10px 16px", margin: 0 }}>
              <h3 style={{ fontWeight: 500 }}><FaFileInvoiceDollar color="#333" /> Inspection Invoice</h3>
              {!isView && (
                <button
                  type="button"
                  className="add-more-btn"
                  style={{ fontSize: 12, padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 6 }}
                  onClick={() => {
                    setEditingInvoiceId(null);
                    setCurrentInvoice({ inspectorAgency: '', workOrder: [], invoiceNumber: '', invoiceDate: '', datesCovered: [], daysCovered: '', taxes: [], overtime: [], otherCostEntries: [], file: null });
                    if (addInvoiceFileRef.current) addInvoiceFileRef.current.value = '';
                    setShowInvoiceFormModal(true);
                  }}
                >
                  <FaPlus size={11} /> Add Invoice
                </button>
              )}
            </div>
            <div className="table-responsive" style={{ padding: "16px", margin: 0, width: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr><th>Inspector / Agency</th><th>Work Order</th><th>Invoice Number</th><th>Invoice Date</th><th>Days</th><th>Amount</th><th>File</th><th>Status</th>{!isView && <th>Actions</th>}</tr>
                </thead>
                <tbody>
                  {invoices.length === 0
                    ? <tr><td colSpan="9" style={{ textAlign: 'center' }}>No invoices added.</td></tr>
                    : invoices.map(inv => {
                        const rowKey = `i_${inv._id}`;
                        const isUploading = uploadingRows[rowKey];
                        const iProgress = uploadProgress[rowKey] ?? 0;
                        const invWO = Array.isArray(inv.workOrder) ? inv.workOrder[0] : inv.workOrder;
                        const invMatchedInspector = invWO ? inspectors.find(i => i.workOrder === invWO) : null;
                        // Derived from attended inspectionDates rather than billing.days,
                        // since Manmonth's billing has no days field of its own.
                        const invPlannedDays = (invMatchedInspector?.inspectionDates || []).filter(d => !(invMatchedInspector?.absentDates || []).includes(d)).length;
                        return (
                          <tr key={inv._id || inv.id}>
                            <td>{inv.inspectorAgency}</td>
                            <td>{Array.isArray(inv.workOrder) ? inv.workOrder.join(', ') : (inv.workOrder || '—')}</td>
                            <td>{inv.invoiceNumber}</td>
                            <td>{formatDate(inv.invoiceDate)}</td>
                            <td title={Array.isArray(inv.datesCovered) && inv.datesCovered.length ? inv.datesCovered.map(formatDate).join(', ') : undefined}>
                              {inv.daysCovered ? `${inv.daysCovered}${invPlannedDays ? ` of ${invPlannedDays}` : ''}` : (invPlannedDays ? `${invPlannedDays} (full)` : '—')}
                            </td>
                            <td>{inv.amount != null && inv.amount !== 0 ? `${CURRENCY_SYMBOLS[invMatchedInspector?.currencyCost] || '₹'}${(+inv.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}</td>
                            <td>
                              {isUploading ? (
                                <div style={{ minWidth: 140 }}>
                                  <div style={{ fontSize: 11, color: '#555', marginBottom: 3 }}>
                                    {iProgress >= 100 ? '✓ Done!' : iProgress >= 95 ? 'Saving to SharePoint…' : `Uploading ${iProgress}%`}
                                  </div>
                                  <div style={{ background: '#e0e0e0', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                                    <div style={{ width: `${iProgress}%`, background: iProgress >= 100 ? '#27ae60' : '#437cf0', height: 6, borderRadius: 4, transition: 'width 0.3s ease' }} />
                                  </div>
                                </div>
                              ) : (
                                <span style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                                  {inv.fileUrl && (
                                    <a href={inv.fileUrl} target="_blank" rel="noopener noreferrer" style={{ background: '#0078d4', color: '#fff', textDecoration: 'none' , height : "fit-content" , borderRadius : "4px" , padding : "6px"  , display : "flex" }}>
                                      <FiEye size={14} /> 
                                    </a>
                                  )}
                                  {inv._id && !isView && (
                                    <button className="" style={{ background: '#e67e22', color: '#fff' , display: "flex" , alignItems : "center" , gap : "4px" , padding : "4px 12px" , border : "none" , borderRadius : "4px" }} onClick={() => handleUploadClick('invoice', inv._id)}>
                                      <FaUpload size={10} /> {inv.fileUrl ? 'Update' : 'Upload'}
                                    </button>
                                  )}
                                  {!inv.fileUrl && !inv._id && <span style={{ fontSize: 11, color: '#aaa' }}>—</span>}
                                </span>
                              )}
                            </td>
                            <td>
                              <span style={{
                                ...payableStatusStyle(inv.payableStatus),
                                borderRadius: 4, padding: '3px 6px', fontSize: 11, fontWeight: 600, display: 'inline-block',
                              }}>
                                {inv.payableStatus || 'Booked'}
                              </span>
                            </td>
                            {!isView && (() => {
                              // Once uploaded the invoice is live on Finance's Inspection
                              // Invoice page, but stays editable here too as long as its
                              // Status is still "Booked" — the moment Finance moves it past
                              // that, Finance owns it and this locks.
                              const sentToFinance = !!inv.fileUrl && (inv.payableStatus || 'Booked') !== 'Booked';
                              const financeLockTitle = 'Status is no longer Booked — edit from the Inspection Invoice page instead';
                              return (
                                <td style={{ display: 'flex', gap: 4 }}>
                                  <button
                                    className="action-btn"
                                    style={{ background: sentToFinance ? '#ccc' : '#27ae60', color: '#fff', padding: '6px', borderRadius: '4px', height: 'fit-content', cursor: sentToFinance ? 'not-allowed' : 'pointer' }}
                                    disabled={sentToFinance}
                                    onClick={() => startEditInvoice(inv)}
                                    title={sentToFinance ? financeLockTitle : 'Edit'}
                                  ><FiEdit size={14} /></button>
                                  <button
                                    className="action-btn delete-btn"
                                    style={{ background: sentToFinance ? '#ccc' : '#dc3545', color: '#fff', padding: '6px', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: 'fit-content', cursor: sentToFinance ? 'not-allowed' : 'pointer' }}
                                    disabled={sentToFinance}
                                    onClick={() => deleteInvoice(inv.id || inv._id)}
                                    title={sentToFinance ? financeLockTitle : 'Delete'}
                                  ><FaTrash /></button>
                                </td>
                              );
                            })()}
                          </tr>
                        );
                      })
                  }
                </tbody>
              </table>
            </div>
          </div>
          {/* Customer Invoice Section — hidden entirely until the enquiry has been sent to Finance */}
          {!isAdd && (financeStatus.sentToFinance || financeStatus.invoices.length > 0) && (
            <div className="card-panel" style={{ padding: 0 }}>
              <div className="section-header" style={{ background: "linear-gradient(rgb(247, 247, 247) 0%, rgb(220, 220, 220) 100%)", padding: "10px 16px", margin: 0 }}>
                <h3 style={{ fontWeight: 500 }}><FaFileInvoiceDollar color="#333" /> Customer Invoice</h3>
              </div>
              {financeStatus.invoiceExists && financeStatus.hasNewWork && (
                <div style={{ padding: '8px 16px', background: '#fff4e5', color: '#8a5a00', fontSize: 12, fontWeight: 500, borderBottom: '1px solid #f0d9a8' }}>
                  New inspection dates have been added since the last invoice — resend from the Enquiry List page to invoice them.
                </div>
              )}
              <div className="table-responsive" style={{ padding: "16px", margin: 0, width: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr><th>Invoice Number</th><th>Job Number</th><th>Invoice Date</th><th>Status</th><th>Total</th><th>Total (INR)</th><th>File</th></tr>
                  </thead>
                  <tbody>
                    {financeStatus.invoices.length === 0
                      ? <tr><td colSpan="7" style={{ textAlign: 'center' }}>Sent to Finance — awaiting invoice</td></tr>
                      : financeStatus.invoices.map(inv => (
                        <tr key={inv._id}>
                          <td>{inv.invoiceNumber || '—'}</td>
                          <td>{inv.jobNumbers?.length ? inv.jobNumbers.join(', ') : (inv.jobNumber || '—')}</td>
                          <td>{formatDate(inv.invoiceDate)}</td>
                          <td>
                            {inv.sentToCoordinator
                              ? <span style={{ background: '#e8f8ee', color: '#1a7e4a', border: '1px solid #27ae60', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>SENT</span>
                              : inv.status === 'INVOICE_ISSUED'
                              ? <span style={{ background: '#eaf1fd', color: '#1a3a6b', border: '1px solid #437cf0', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>Invoice Issued</span>
                              : <span style={{ background: '#f2f2f2', color: '#666', border: '1px solid #ccc', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>Proforma Invoice</span>}
                          </td>
                          <td>{inv.total != null ? `${CURRENCY_SYMBOLS[inv.invoiceCurrency] || '₹'}${(+inv.total).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}</td>
                          <td>{inv.total != null ? `₹${((+inv.total) * (rateFromHistoryAsOf(rateHistory, inv.invoiceCurrency, inv.invoiceDate) || CURRENCY_RATES[inv.invoiceCurrency] || 1)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}</td>
                          <td>
                            {inv._id ? (() => {
                              const isDownloading = downloadingInvoices.has(inv._id);
                              const isPreviewing = previewingInvoices.has(inv._id);
                              return (
                                <div style={{ display: 'flex', gap: 6 }}>
                                  <button
                                    type="button"
                                    disabled={isPreviewing}
                                    style={{ background: isPreviewing ? '#5b8ebd' : '#2980b9', color: '#fff', border: 0, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: '4px', cursor: isPreviewing ? 'not-allowed' : 'pointer', borderRadius: 4 }}
                                    onClick={() => handlePreviewInvoice(inv._id)}
                                  >
                                    {isPreviewing ? (
                                      <><span style={{ display: 'inline-block', width: 11, height: 11, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} /></>
                                    ) : (
                                      <><LuEye size={12} /></>
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    disabled={isDownloading}
                                    style={{ background: isDownloading ? '#6b9e7a' : '#27ae60', color: '#fff', border: 0, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: '4px', cursor: isDownloading ? 'not-allowed' : 'pointer', borderRadius: 4 }}
                                    onClick={() => handleDownloadInvoice(inv._id, inv.invoiceNumber)}
                                  >
                                    {isDownloading ? (
                                      <><span style={{ display: 'inline-block', width: 11, height: 11, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} /> Downloading…</>
                                    ) : (
                                      <><LuDownload size={12} /> Download</>
                                    )}
                                  </button>
                                </div>
                              );
                            })() : (
                              <span style={{ fontSize: 11, color: '#aaa' }}>—</span>
                            )}
                          </td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Bottom Status Section */}
      <div className="card-panel" style={{ background: '#f8f9fc', border: 'none' }}>
        {!isView && (
          <div className="form-grid two-cols" style={user?.role === 'global_superadmin' ? { gridTemplateColumns: 'repeat(4, 1fr)' } : undefined}>
            {user?.role === 'global_superadmin' && (
              <div className="form-group">
                <label>Select Status <span className="required">*</span></label>
                <select className="form-control" name="status" value={formData.status} onChange={(e) => {
                  const newStatus = e.target.value;
                  if (newStatus === 'WON' && inspectors.filter(i => i.status === 'APPROVED').length === 0) return toast.info('At least one inspector must be approved to set status to WON.');
                  if (newStatus === 'WON' && !inspectors.some(i => i.inspectionDates?.length > 0)) return toast.info('At least one inspection date must be selected before setting status to Won.');
                  setFormData(prev => ({ ...prev, status: newStatus, remarkStatus: STATUS_DEFAULT_REMARK[newStatus] ?? prev.remarkStatus, remark: '' }));
                }}>
                  <option value="NEW">New</option><option value="INPROCESS">In Process</option><option value="WON">Won</option><option value="LOST">Lost</option>
                </select>
              </div>
            )}
            {(user?.role === 'global_superadmin' || user?.role === 'finance') && (
              <div className="form-group">
                <label>Quarterly Incentive</label>
                <select
                  className="form-control"
                  value={formData.quarterlyIncentiveEligible === false ? 'NOT_ELIGIBLE' : 'ELIGIBLE'}
                  onChange={(e) => setFormData(prev => ({ ...prev, quarterlyIncentiveEligible: e.target.value !== 'NOT_ELIGIBLE' }))}
                >
                  <option value="ELIGIBLE">Eligible</option>
                  <option value="NOT_ELIGIBLE">Not Eligible</option>
                </select>
              </div>
            )}
            <div className="form-group">
              <label>Remark Status</label>
              <select className="form-control" name="remarkStatus" value={formData.remarkStatus || ''} onChange={(e) => {
                const val = e.target.value;
                const nextStatus = REMARK_STATUS_TRANSITIONS[formData.status]?.[val];
                if (nextStatus === 'WON' && !inspectors.some(i => i.inspectionDates?.length > 0)) {
                  return toast.info('At least one inspection date must be selected before setting enquiry to Won.');
                }
                // Clear Remark on every Remark Status change — each status gets its own
                // fresh remark typed for it, not the previous status's leftover text.
                setFormData(prev => {
                  const ns = REMARK_STATUS_TRANSITIONS[prev.status]?.[val];
                  if (!ns) return { ...prev, remarkStatus: val, remark: '' };
                  return { ...prev, status: ns, remarkStatus: STATUS_DEFAULT_REMARK[ns] ?? val, remark: '' };
                });
              }}>
                {remarkStatusOptionsFor(formData.status, formData.remarkStatus).map(opt => (
                  <option key={opt || '_blank'} value={opt}>{opt || 'Select'}</option>
                ))}
              </select>
            </div>
            {REMARK_STATUS_SHOWS_REMARK.includes(formData.remarkStatus) && (
              <div className="form-group">
                <label>Remark</label>
                <input type="text" className="form-control" name="remark" placeholder="Enter Remark" value={formData.remark || ''} onChange={handleInputChange} />
              </div>
            )}
            {['global_superadmin', 'project_manager'].includes(user?.role) ? (
              <div className="form-group">
                <label>Assigned To</label>
                <select
                  className="form-control"
                  value={formData.createdBy || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    const picked = availableUsers.find(u => String(u.id) === val);
                    setFormData(prev => ({ ...prev, createdBy: val || null }));
                    setCoordinatorName(picked?.employeeName || '');
                  }}
                >
                  <option value="">{coordinatorName || '-- Select Coordinator --'}</option>
                  {availableUsers
                    .filter(u => String(u.company?._id || u.company) === String(formData.company))
                    .map(u => (
                      <option key={u.id} value={u.id}>{u.employeeName}</option>
                    ))
                  }
                </select>
              </div>
            ) : user?.role !== 'coordinator' && (
              <div className="form-group">
                <label>Coordinator</label>
                <input type="text" className="form-control" disabled value={coordinatorName || '—'} />
              </div>
            )}
          </div>
        )}
        {isView && (
          <div className="form-grid two-cols" style={user?.role === 'global_superadmin' ? { gridTemplateColumns: 'repeat(4, 1fr)' } : undefined}>
            {user?.role === 'global_superadmin' && (
              <div className="form-group"><label>Status</label><input type="text" className="form-control" disabled value={formData.status} /></div>
            )}
            {(user?.role === 'global_superadmin' || user?.role === 'finance') && (
              <div className="form-group"><label>Quarterly Incentive</label><input type="text" className="form-control" disabled value={formData.quarterlyIncentiveEligible === false ? 'Not Eligible' : 'Eligible'} /></div>
            )}
            <div className="form-group"><label>Remark Status</label><input type="text" className="form-control" disabled value={formData.remarkStatus || '—'} /></div>
            {REMARK_STATUS_SHOWS_REMARK.includes(formData.remarkStatus) && (
              <div className="form-group"><label>Remark</label><input type="text" className="form-control" disabled value={formData.remark || '—'} /></div>
            )}
            {user?.role !== 'coordinator' && (
              <div className="form-group"><label>Coordinator</label><input type="text" className="form-control" disabled value={coordinatorName || '—'} /></div>
            )}
          </div>
        )}
        <div className="bottom-action" style={{ display: 'flex', gap: '15px' }}>
          {isView && <button className="save-btn" onClick={requestNavigateBack} style={{ background: '#f8f9fc', color: '#333', borderColor: '#ccc' }}>← Back</button>}
          {!isView && (
            <>
              <button className="save-btn" onClick={handleSaveDraft} disabled={savingDraft || savingEnquiry} style={{ background: '#f8f9fc', color: '#333', borderColor: '#ccc' }}> <FaRegSave /> {savingDraft ? 'Saving...' : 'Save Draft'} </button>
              <button className="save-btn" onClick={() => handleSave()} disabled={savingEnquiry || savingDraft}> <FiSave /> {savingEnquiry ? 'Saving...' : 'Save Enquiry'} </button>
              {!isAdd && hasUploadedReport && (
                <button
                  className="save-btn"
                  style={{
                    background: financeStatus.invoiceExists && !financeStatus.hasNewWork ? '#95a5a6'
                      : (financeStatus.sentToFinance || financeStatus.hasNewWork) ? '#e67e22' : undefined,
                    color: (financeStatus.invoiceExists || financeStatus.sentToFinance) ? '#fff' : undefined,
                    borderColor: financeStatus.invoiceExists && !financeStatus.hasNewWork ? '#95a5a6'
                      : (financeStatus.sentToFinance || financeStatus.hasNewWork) ? '#e67e22' : undefined,
                  }}
                  disabled={(financeStatus.invoiceExists && !financeStatus.hasNewWork) || sendingToFinance}
                  title={
                    financeStatus.invoiceExists && financeStatus.hasNewWork ? 'New inspection dates have been added since the last invoice'
                    : financeStatus.invoiceExists ? 'Finance has already created an invoice for this enquiry'
                    : financeStatus.sentToFinance ? 'Already sent — sending again updates the same finance entry'
                    : 'Notify Finance that reports are ready for invoicing'
                  }
                  onClick={handleSendToFinance}
                >
                  <FaFileInvoiceDollar />
                  {sendingToFinance ? 'Sending…'
                    : financeStatus.invoiceExists && financeStatus.hasNewWork ? 'Send New Dates to Finance'
                    : financeStatus.invoiceExists ? 'Invoice Created'
                    : financeStatus.sentToFinance ? 'Re-send to Finance'
                    : 'Send to Finance'}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Remark Logs — fixed floating button (bottom-right); opens the log in a modal
          instead of an inline section. See "Remark Logs Modal" under --- MODALS ---. */}
      <button
        type="button"
        onClick={() => { setRemarkLogsModalDragged(false); setShowRemarkLogsModal(true); }}
        title="Remark Logs"
        style={{
          position: 'fixed', bottom: 36, right: 36, zIndex: 900,
          width: 52, height: 52, borderRadius: '50%', border: 'none',
          background: '#1a2a4d', color: '#fff', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
        }}
      >
        <CgNotes size={20} />
      </button>

      {/* Send-to-Finance date selection popup */}
      {showSendModal && (
        <div
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => !sendingToFinance && setShowSendModal(false)}
        >
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', maxWidth: '560px', width: '90%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,.18)' }}>
            <div style={{ background: '#1a2a4d', color: '#fff', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontWeight: 600, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}><FaFileInvoiceDollar /> SEND TO FINANCE — SELECT INSPECTION DATES</h3>
              <button onClick={() => !sendingToFinance && setShowSendModal(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, display: 'flex', cursor: 'pointer' }}><FiX /></button>
            </div>
            <div style={{ padding: '20px', overflowY: 'auto' }}>
            <p style={{ fontSize: 12, color: '#777', margin: '0 0 14px' }}>
              Only dates with an uploaded report can be selected. Dates already invoiced are locked.
            </p>
            {sendInspectorDates.map((ins, idx) => {
              const isLegacy = (ins.dates || []).length === 0;
              const reported = new Set(ins.reportedDates || []);
              const invoiced = new Set(ins.invoicedDates || []);
              const picked = pickedDates[idx] || [];
              return (
                <div key={idx} style={{ marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid #eee' }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#222' }}>{ins.inspectorName || '—'}</div>
                  {isLegacy ? (
                    ins.hasInvoice ? (
                      <span style={{ fontSize: 12, background: '#27ae60', color: '#fff', padding: '4px 10px', borderRadius: 4 }} title="Invoice already created">
                        ✓ Invoiced — {ins.numberOfDays} day(s)
                      </span>
                    ) : reported.size > 0 ? (
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                        <input type="checkbox" checked={picked.includes('*')} onChange={() => toggleSendDate(idx, '*')} />
                        {ins.numberOfDays} day(s) — no date list
                      </label>
                    ) : (
                      <span style={{ fontSize: 12, color: '#999' }} title="No report uploaded for this inspector">
                        {ins.numberOfDays} day(s) — no report uploaded
                      </span>
                    )
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {(ins.dates || []).map(d => {
                        if (invoiced.has(d)) {
                          return (
                            <span key={d} title="Invoice already created for this date" style={{ fontSize: 12, background: '#27ae60', color: '#fff', padding: '4px 10px', borderRadius: 4, cursor: 'not-allowed' }}>
                              ✓ {formatDate(d)}
                            </span>
                          );
                        }
                        if (!reported.has(d)) {
                          return (
                            <span key={d} title="No report uploaded for this date" style={{ fontSize: 12, background: '#f0f0f0', color: '#aaa', padding: '4px 10px', borderRadius: 4, cursor: 'not-allowed' }}>
                              {formatDate(d)}
                            </span>
                          );
                        }
                        const isPicked = picked.includes(d);
                        return (
                          <label key={d} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, padding: '4px 10px', borderRadius: 4, cursor: 'pointer', border: `1px solid ${isPicked ? '#437cf0' : '#ddd'}`, background: isPicked ? '#eaf3ff' : '#fff', color: '#333' }}>
                            <input type="checkbox" checked={isPicked} onChange={() => toggleSendDate(idx, d)} style={{ margin: 0 }} />
                            {formatDate(d)}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <button type="button" style={{ background: '#f0f0f0', padding: '10px 20px', border: 'none', borderRadius: '4px', cursor: sendingToFinance ? 'not-allowed' : 'pointer' }} disabled={sendingToFinance} onClick={() => setShowSendModal(false)}>Cancel</button>
              <button
                type="button"
                style={{ background: '#1a2a4d', color: '#fff', padding: '10px 20px', border: 'none', borderRadius: '4px', cursor: pickedCount === 0 || sendingToFinance ? 'not-allowed' : 'pointer', opacity: pickedCount === 0 || sendingToFinance ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 6 }}
                disabled={pickedCount === 0 || sendingToFinance}
                onClick={confirmSendToFinance}
              >
                <FaFileInvoiceDollar /> {sendingToFinance ? 'Sending…' : `Send to Finance (${pickedCount})`}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* --- MODALS --- */}

      {/* Remark Logs Modal */}
      {showRemarkLogsModal && (
        <div
          style={remarkLogsModalDragged
            ? { position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, pointerEvents: 'none' }
            : { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={remarkLogsModalDragged ? undefined : () => setShowRemarkLogsModal(false)}
        >
          <Draggable handle=".modal-drag-handle" nodeRef={remarkLogsModalRef} onStart={() => setRemarkLogsModalDragged(true)}>
            <div ref={remarkLogsModalRef} onClick={(e) => e.stopPropagation()} style={{ background: '#fff', width: 560, maxWidth: '90%', maxHeight: '75vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', pointerEvents: 'auto', boxShadow: remarkLogsModalDragged ? '0 30px 80px rgba(0,0,0,0.45)' : undefined }}>
              <div className="modal-drag-handle" style={{ background: '#1a2a4d', color: '#fff', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'grab' }}>
                <h3 style={{ fontSize: '1rem', margin: 0, fontWeight: 500, display: 'flex', gap: '10px', alignItems: 'center' }}><CgNotes /> Remark Logs</h3>
                <button onClick={() => setShowRemarkLogsModal(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer' }}><FiX /></button>
              </div>
              <div className="table-responsive" style={{ padding: "16px", margin: 0, width: 'auto', overflowY: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr><th>Date</th><th>Remark Status</th><th>Remark</th></tr>
                  </thead>
                  <tbody>
                    {remarkLogs.length === 0
                      ? <tr><td colSpan={3} style={{ textAlign: 'center' }}>No remark logs yet.</td></tr>
                      : [...remarkLogs].sort((a, b) => (a.date || '').localeCompare(b.date || '')).map(r => (
                          <tr key={r._id || r.id}>
                            <td>{r.date ? formatDate(r.date) : '—'}</td>
                            <td>{r.remarkStatus || '—'}</td>
                            <td>{r.remark || '—'}</td>
                          </tr>
                        ))
                    }
                  </tbody>
                </table>
              </div>
            </div>
          </Draggable>
        </div>
      )}

      {/* Generate Work Order Modal */}
      {showGenerateWorkOrderModal && selectedInspectorForWO && (
        <div
          style={workOrderModalDragged
            ? { position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, pointerEvents: 'none' }
            : { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={workOrderModalDragged ? undefined : () => setShowGenerateWorkOrderModal(false)}
        >
          <Draggable handle=".modal-drag-handle" nodeRef={workOrderModalRef} onStart={() => setWorkOrderModalDragged(true)}>
          <div ref={workOrderModalRef} onClick={(e) => e.stopPropagation()} style={{ background: '#fff', width: '85%', overflow: 'hidden', maxHeight: '85vh', display: 'flex', flexDirection: 'column', pointerEvents: 'auto', boxShadow: workOrderModalDragged ? '0 30px 80px rgba(0,0,0,0.45)' : undefined }}>
            <div className="modal-drag-handle" style={{ background: '#1a2a4d', color: '#fff', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'grab' }}>
              <h3 style={{fontSize: "1rem" , margin: 0, fontWeight: 500 , display: "flex" ,  gap : "5px" ,  alignItems : "center" }}><IoNewspaperSharp />{selectedInspectorForWO.workOrder ? 'UPDATE WORK ORDER' : 'GENERATE WORK ORDER'}</h3>
              <button onClick={() => setShowGenerateWorkOrderModal(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer' }}><FiX /></button>
            </div>
            <form style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
              {/* Basic */}
              <div style={{ display: 'grid', gridTemplateColumns: "repeat(3,1fr)", gap: '15px', marginBottom: '20px', paddingBottom: '15px' }}>
                <div className="form-group"><label>Work Order Number <span className="required">*</span></label><input type="text" className="form-control" value={workOrderForm.workOrderNumber} disabled style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }} /></div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    Inspector Name
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 'normal', fontSize: '12px', color: '#555', marginLeft: 'auto' }}>
                      <input type="checkbox" checked={editInspectorName} onChange={(e) => setEditInspectorName(e.target.checked)} style={{ cursor: 'pointer' }} />
                      Edit
                    </span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={workOrderForm.inspectorName}
                    onChange={(e) => setWorkOrderForm(prev => ({ ...prev, inspectorName: e.target.value }))}
                    disabled={!editInspectorName}
                    style={{ backgroundColor: editInspectorName ? '#fff' : '#f5f5f5', transition: 'background-color 0.2s' }}
                  />
                </div>
                <div className="form-group"><label>Order Date <span className="required">*</span></label><input type="date" className="form-control" value={workOrderForm.date} onChange={(e) => { setWorkOrderForm(prev => ({ ...prev, date: e.target.value })); handleInspectorFieldChange(selectedInspectorForWO.id, 'workOrderDate', e.target.value); }} required /></div>
                <div className="form-group"><label>Expected Start Date</label><input type="date" className="form-control" value={workOrderForm.expectedStartDate} onChange={(e) => { setWorkOrderForm(prev => ({ ...prev, expectedStartDate: e.target.value })); handleInspectorFieldChange(selectedInspectorForWO.id, 'expectedStartDate', e.target.value); }} /></div>
                <div className="form-group"><label>Expected End Date</label><input type="date" className="form-control" value={workOrderForm.expectedEndDate} onChange={(e) => { setWorkOrderForm(prev => ({ ...prev, expectedEndDate: e.target.value })); handleInspectorFieldChange(selectedInspectorForWO.id, 'expectedEndDate', e.target.value); }} /></div>
                <div className="form-group"><label>Issuing Company</label><textarea rows={2} className="form-control" value={workOrderForm.issuingCompany} onChange={(e) => setWorkOrderForm(prev => ({ ...prev, issuingCompany: e.target.value }))} /></div>
                <div className="form-group"><label>Work Location</label><input type="text" className="form-control" value={workOrderForm.workLocation} onChange={(e) => setWorkOrderForm(prev => ({ ...prev, workLocation: e.target.value }))} /></div>
                  <div className="form-group"><label>Description</label><textarea className="form-control" value={workOrderForm.workDescription} onChange={(e) => setWorkOrderForm(prev => ({ ...prev, workDescription: e.target.value }))} rows="2" /></div>
                <div className="form-group"><label>Additional Comments</label><textarea className="form-control" value={workOrderForm.additionalComments} onChange={(e) => setWorkOrderForm(prev => ({ ...prev, additionalComments: e.target.value }))} rows="2" /></div>
              </div>

              {/* Billing */}
              <div className={`sub-card-header ${!woSectionCollapsed.billing ? 'expanded' : ''}`} onClick={() => toggleWoSection('billing')} style={{ marginBottom: woSectionCollapsed.billing ? '15px' : 0 }}>
                <h3>BILLING DETAILS</h3>
                {!woSectionCollapsed.billing ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
              </div>
              {!woSectionCollapsed.billing && (
                <div className="sub-card-content" style={{ marginBottom: '20px' , padding: "1rem" }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '15px' }}>
                    {formData.enquiryType === 'MANMONTH' ? (
                      <>
                        <div className="form-group"><label>Months</label><input type="number" className="form-control" disabled title="Number of distinct calendar months assigned — derived from Inspection Dates" value={workOrderForm.billing.months} /></div>
                        <div className="form-group"><label>Monthly Rate</label><input type="number" className="form-control" disabled={isWOBillingLocked(liveSelectedInspector || selectedInspectorForWO || {})} title={isWOBillingLocked(liveSelectedInspector || selectedInspectorForWO || {}) ? 'Locked — inspector invoice uploaded for this work order' : undefined} value={workOrderForm.billing.monthlyRate} onChange={(e) => {
                          const r = parseFloat(e.target.value) || 0;
                          const inspectionDates = selectedInspectorForWO?.inspectionDates || [];
                          const attendedDates = inspectionDates.filter(d => !(selectedInspectorForWO?.absentDates || []).includes(d));
                          setWorkOrderForm(prev => {
                            const amt = parseFloat(monthlyShareAmount(attendedDates, inspectionDates, r, prev.billing.workingDaysByMonth).toFixed(2));
                            const billing = { ...prev.billing, monthlyRate: r, amount: amt };
                            return { ...prev, billing: { ...billing, total: recomputeWOTotal(billing) } };
                          });
                          handleInspectorFieldChange(selectedInspectorForWO.id, 'cost', String(r));
                        }} /></div>
                      </>
                    ) : (
                      <>
                        <div className="form-group"><label>Days</label><input type="number" className="form-control" disabled={isWOBillingLocked(liveSelectedInspector || selectedInspectorForWO || {})} title={isWOBillingLocked(liveSelectedInspector || selectedInspectorForWO || {}) ? 'Locked — inspector invoice uploaded for this work order' : undefined} value={workOrderForm.billing.days} onChange={(e) => { const d = parseFloat(e.target.value) || 0; const amt = d * workOrderForm.billing.rate; setWorkOrderForm(prev => { const billing = { ...prev.billing, days: d, amount: amt }; return { ...prev, billing: { ...billing, total: recomputeWOTotal(billing) } }; }); handleInspectorFieldChange(selectedInspectorForWO.id, 'numberOfDays', d); }} /></div>
                        <div className="form-group"><label>Rate</label><input type="number" className="form-control" disabled={isWOBillingLocked(liveSelectedInspector || selectedInspectorForWO || {})} title={isWOBillingLocked(liveSelectedInspector || selectedInspectorForWO || {}) ? 'Locked — inspector invoice uploaded for this work order' : undefined} value={workOrderForm.billing.rate} onChange={(e) => { const r = parseFloat(e.target.value) || 0; const amt = workOrderForm.billing.days * r; setWorkOrderForm(prev => { const billing = { ...prev.billing, rate: r, amount: amt }; return { ...prev, billing: { ...billing, total: recomputeWOTotal(billing) } }; }); handleInspectorFieldChange(selectedInspectorForWO.id, 'cost', String(r)); }} /></div>
                      </>
                    )}
                    <div className="form-group"><label>Amount</label><div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}><span style={{ position: 'absolute', left: '8px', color: '#555', pointerEvents: 'none', fontSize: '13px' }}>{CURRENCY_SYMBOLS[(liveSelectedInspector || selectedInspectorForWO)?.currencyCost] || '₹'}</span><input type="number" className="form-control" value={workOrderForm.billing.amount} disabled style={{ backgroundColor: '#f9f9f9', paddingLeft: '22px', width: "100%" }} /></div></div>
                    <div className="form-group"><label>Tax Rate (%)</label><input type="number" className="form-control" disabled={isWOBillingLocked(liveSelectedInspector || selectedInspectorForWO || {})} title={isWOBillingLocked(liveSelectedInspector || selectedInspectorForWO || {}) ? 'Locked — inspector invoice uploaded for this work order' : 'Applies to the Amount only'} value={workOrderForm.billing.tax} onChange={(e) => { const tax = parseFloat(e.target.value) || 0; setWorkOrderForm(prev => { const billing = { ...prev.billing, tax }; return { ...prev, billing: { ...billing, total: recomputeWOTotal(billing) } }; }); }} /></div>
                    <div className="form-group"><label>Other Costs</label><div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}><span style={{ position: 'absolute', left: '8px', color: '#555', pointerEvents: 'none', fontSize: '13px' }}>{CURRENCY_SYMBOLS[(liveSelectedInspector || selectedInspectorForWO)?.currencyCost] || '₹'}</span><input type="number" className="form-control" disabled={isWOBillingLocked(liveSelectedInspector || selectedInspectorForWO || {})} title={isWOBillingLocked(liveSelectedInspector || selectedInspectorForWO || {}) ? 'Locked — inspector invoice uploaded for this work order' : 'A flat lump-sum figure, added after tax'} style={{ paddingLeft: '22px' }} value={workOrderForm.billing.otherCosts} onChange={(e) => { const otherCosts = parseFloat(e.target.value) || 0; setWorkOrderForm(prev => { const billing = { ...prev.billing, otherCosts }; return { ...prev, billing: { ...billing, total: recomputeWOTotal(billing) } }; }); }} /></div></div>
                    <div className="form-group"><label>Total</label><input type="number" className="form-control" value={workOrderForm.billing.total} disabled style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }} /></div>
                  </div>
                  {formData.enquiryType === 'MANMONTH' && groupDatesByMonth(selectedInspectorForWO?.inspectionDates || []).length > 0 && (
                    <div style={{ marginTop: '15px' }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px' }}>
                        Working Days per Month
                      </label>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ background: '#f5f6fa' }}>
                            <th style={{ textAlign: 'left', padding: '6px 8px', border: '1px solid #e2e4ea' }}>Month</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px', border: '1px solid #e2e4ea' }}>Days Assigned</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px', border: '1px solid #e2e4ea', width: 130 }}>Working Days</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px', border: '1px solid #e2e4ea' }}>Month's Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupDatesByMonth((selectedInspectorForWO?.inspectionDates || []).filter(d => !(selectedInspectorForWO?.absentDates || []).includes(d))).map(g => {
                            const workingDays = workOrderForm.billing.workingDaysByMonth?.[g.ym];
                            const rate = parseFloat(workOrderForm.billing.monthlyRate) || 0;
                            const divisor = parseFloat(workingDays) || 0;
                            const monthAmount = divisor > 0 ? (rate / divisor) * g.count : 0;
                            return (
                              <tr key={g.ym}>
                                <td style={{ padding: '6px 8px', border: '1px solid #e2e4ea' }}>{g.ym}</td>
                                <td style={{ textAlign: 'right', padding: '6px 8px', border: '1px solid #e2e4ea' }}>{g.count}</td>
                                <td style={{ textAlign: 'right', padding: '6px 8px', border: '1px solid #e2e4ea' }}>
                                  <input
                                    type="number"
                                    className="form-control"
                                    disabled={isWOBillingLocked(liveSelectedInspector || selectedInspectorForWO || {})}
                                    value={workingDays ?? ''}
                                    onChange={(e) => handleWorkingDaysChange(g.ym, e.target.value)}
                                    style={{ textAlign: 'right' }}
                                  />
                                </td>
                                <td style={{ textAlign: 'right', padding: '6px 8px', border: '1px solid #e2e4ea' }}>{monthAmount.toFixed(2)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
              {/* Administrative */}
              <div className={`sub-card-header ${!woSectionCollapsed.adminDetails ? 'expanded' : ''}`} onClick={() => toggleWoSection('adminDetails')} style={{ marginBottom: woSectionCollapsed.adminDetails ? '15px' : 0 }}>
                <h3>ADMINISTRATIVE DETAILS</h3>
                {!woSectionCollapsed.adminDetails ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
              </div>
              {!woSectionCollapsed.adminDetails && (
                <div className="sub-card-content" style={{ padding: "1rem" }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '15px' }}>
                    <div className="form-group"><label>Order Received By</label><input type="text" className="form-control" value={workOrderForm.administrative.orderReceivedBy} onChange={(e) => setWorkOrderForm(prev => ({ ...prev, administrative: { ...prev.administrative, orderReceivedBy: e.target.value } }))} /></div>
                    <div className="form-group"><label>Compiled By</label><input type="text" className="form-control" value={workOrderForm.administrative.compiledBy} onChange={(e) => setWorkOrderForm(prev => ({ ...prev, administrative: { ...prev.administrative, compiledBy: e.target.value } }))} /></div>
                    <div className="form-group">
                      <label>Approving Party Name &amp; Title</label>
                      <select
                        className="form-control"
                        value={workOrderForm.administrative.approvingParty}
                        onChange={(e) => setWorkOrderForm(prev => ({ ...prev, administrative: { ...prev.administrative, approvingParty: e.target.value } }))}
                      >
                        <option value="">-- Select Approving Party --</option>
                        {availableUsers
                          .filter(u =>
                            ['project_manager', 'company_admin', 'admin'].includes(u.employeeType) &&
                            String(u.company?._id || u.company) === String(formData.company)
                          )
                          .map(u => (
                            <option key={u.id} value={u.employeeName}>
                              {u.employeeName}{u.designation ? ` — ${u.designation}` : ''}
                            </option>
                          ))
                        }
                      </select>
                    </div>
                    <div className="form-group"><label>Date of Approval</label><input type="date" className="form-control" value={workOrderForm.administrative.dateOfApproval} onChange={(e) => setWorkOrderForm(prev => ({ ...prev, administrative: { ...prev.administrative, dateOfApproval: e.target.value } }))} /></div>
                  </div>
                </div>
              )}
            </form>
            {/* Actions — pinned footer outside the scroll area */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '12px 20px', borderTop: '1px solid #eee', flexShrink: 0, background: '#fff' }}>
              <button type="button" onClick={handleGenerateWorkOrder} disabled={savingWorkOrder} style={{ background: savingWorkOrder ? '#7aa3f5' : '#437cf0', color: '#fff', padding: '10px 20px', border: 'none', borderRadius: '4px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', cursor: savingWorkOrder ? 'not-allowed' : 'pointer' }}>
                {savingWorkOrder
                  ? <><span style={{ width: 16, height: 16, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} /> Saving to OneDrive…</>
                  : <><FiCheck size={18} /> {selectedInspectorForWO.workOrder ? 'Update Work Order' : 'Save to OneDrive'}</>}
              </button>
              <button type="button" onClick={() => setShowGenerateWorkOrderModal(false)} disabled={savingWorkOrder} style={{ background: '#f0f0f0', padding: '10px 20px', border: 'none', borderRadius: '4px', cursor: savingWorkOrder ? 'not-allowed' : 'pointer' }}>Cancel</button>
            </div>
          </div>
          </Draggable>
        </div>
      )}

      {/* Inspection Invoice Form Modal */}
      {showInvoiceFormModal && (
        <div
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 }}
          onClick={(e) => { if (e.target === e.currentTarget && !addingInvoice) cancelEditInvoice(); }}
        >
          <div
            style={{ background: '#fff', width: "85%", maxWidth: '95vw', maxHeight: '75vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
          >
            {/* Header */}
            <div style={{ background: '#1a2a4d', color: '#fff', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
                <FaFileInvoiceDollar /> {editingInvoiceId ? 'Edit Inspection Invoice' : 'Add Inspection Invoice'}
              </h3>
              <button onClick={() => { if (!addingInvoice) cancelEditInvoice(); }} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer' }}>
                <FiX />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
              {/* Hidden file input */}
              <input
                ref={addInvoiceFileRef}
                type="file"
                style={{ display: 'none' }}
                disabled={!!editingInvoiceId}
                onChange={e => { const f = e.target.files?.[0]; if (f) setCurrentInvoice(prev => ({ ...prev, file: f })); }}
              />

              {/* Main fields — 2-column grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: '16px', marginBottom: 4 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Inspector / Agency</label>
                  <select
                    className="form-control"
                    value={currentInvoice.inspectorAgency}
                    onChange={(e) => setCurrentInvoice(prev => ({ ...prev, inspectorAgency: e.target.value, workOrder: [] }))}
                  >
                    <option value="">Select Inspector</option>
                    {approvedInspectors.map(i => { const name = getDisplayName(i.inspectorId, i.inspectorName || i.inspectorId, i.id); return <option key={i.id} value={name}>{name}</option>; })}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Work Order</label>
                  <select
                    className="form-control"
                    value={Array.isArray(currentInvoice.workOrder) ? currentInvoice.workOrder[0] || '' : currentInvoice.workOrder || ''}
                    onChange={e => setCurrentInvoice(prev => ({ ...prev, workOrder: e.target.value ? [e.target.value] : [], datesCovered: [], daysCovered: '', overtime: [], otherCostEntries: [] }))}
                    disabled={!currentInvoice.inspectorAgency}
                  >
                    <option value="">Select work order</option>
                    {workOrderOptions.map(o => <option key={o.value} value={o.value}>{o.value}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Invoice Number <span className="required">*</span></label>
                  <input type="text" className="form-control" value={currentInvoice.invoiceNumber} onChange={(e) => handleInvoiceChange('invoiceNumber', e.target.value)} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Invoice Date <span className="required">*</span></label>
                  <input
                    type="date"
                    className="form-control"
                    style={{ padding: '7px 10px' }}
                    value={currentInvoice.invoiceDate}
                    // Keep the picker inside the window — unless an edited invoice still
                    // carries an older saved date, so that date isn't shown as invalid.
                    min={invoiceDateWin && !(editingInvoiceSavedDate && editingInvoiceSavedDate < invoiceDateWin.from) ? invoiceDateWin.from : undefined}
                    max={invoiceDateWin ? invoiceDateWin.to : undefined}
                    onChange={(e) => handleInvoiceChange('invoiceDate', e.target.value)}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Completed Dates</label>
                  {invoiceInspector && (Array.isArray(invoiceInspector.inspectionDates) ? invoiceInspector.inspectionDates.length : 0) > 0 ? (
                    // Specific dates, restricted to what's left to bill on this work order.
                    <DatePicker
                      multiple
                      format="DD-MM-YYYY"
                      value={currentInvoice.datesCovered.map(d => new Date(d)).filter(d => !isNaN(d))}
                      onChange={(dates) => handleInvoiceChange('datesCovered', dates ? dates.map(d => d.format('YYYY-MM-DD')) : [])}
                      mapDays={makeShiftRangeMapDays({
                        getValues: () => currentInvoice.datesCovered || [],
                        setValues: (arr) => handleInvoiceChange('datesCovered', arr),
                        anchorRef: invoiceDatesCoveredAnchorRef,
                        isAllowed: (d) => invoiceAvailableDates.includes(d),
                      })}
                      placeholder={invoiceDaysInfo?.remaining ? `up to ${invoiceDaysInfo.remaining} day(s) left` : 'No days left to invoice'}
                      inputClass="form-control"
                      containerStyle={{ width: '100%' }}
                      style={{ width: '90%' }}
                    />
                  ) : (
                    // Fallback for work orders with no inspectionDates on record.
                    <input
                      type="number"
                      min="0"
                      step="1"
                      className="form-control"
                      placeholder={invoiceDaysInfo ? `up to ${invoiceDaysInfo.remaining}` : 'Select a work order first'}
                      value={currentInvoice.daysCovered}
                      disabled={!invoiceInspector}
                      onChange={(e) => handleInvoiceChange('daysCovered', e.target.value)}
                    />
                  )}
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Upload File</label>
                  <button
                    type="button"
                    className="form-control"
                    disabled={!!editingInvoiceId}
                    style={{ textAlign: 'left', cursor: editingInvoiceId ? 'not-allowed' : 'pointer', fontSize: 12, height: '36px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', color: currentInvoice.file ? '#1a7e4a' : '#888', opacity: editingInvoiceId ? 0.5 : 1 }}
                    onClick={() => addInvoiceFileRef.current?.click()}
                  >
                    <FaUpload size={11} style={{ marginRight: 6 }} />
                    {editingInvoiceId ? 'Use the row Upload button to replace the file' : (currentInvoice.file ? currentInvoice.file.name : 'Choose file…')}
                  </button>
                </div>
              </div>
              {/* Left blank = this invoice covers the whole work order. Only fill it in
                  when splitting one work order across several invoices. */}
              {invoiceDaysInfo && (
                <div style={{ fontSize: 12, color: invoiceDaysInfo.remaining === 0 ? '#c0392b' : '#666', marginBottom: 8 }}>
                  Work order planned: {invoiceDaysInfo.planned} day(s) · Already invoiced by other rows: {invoiceDaysInfo.alreadyUsed} day(s) · Remaining: {invoiceDaysInfo.remaining} day(s)
                </div>
              )}

              {/* Date-by-date breakdown — reconciles exactly to the invoice's saved amount. */}
              {invoiceBreakdown && invoiceBreakdown.n > 0 && (
                <div style={{ marginBottom: 16, padding: "16px 0", borderBottom: '1px solid #eee' }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>
                    Invoice Breakdown
                    {invoiceBreakdown.isManmonth && (
                      <span style={{ fontWeight: 400, color: '#888', marginLeft: 8 }}>
                        (Monthly Rate ÷ Working Days that month × days this invoice covers)
                      </span>
                    )}
                  </label>
                  {invoiceBreakdown.isManmonth ? (
                    invoiceBreakdown.perMonth.length > 0 && (
                      <table className="vendor-table" style={{ fontSize: 12, width: '100%', margin: "8px 0 16px 0" }}>
                        <thead>
                          <tr>
                            <th>Month</th>
                            <th style={{ textAlign: 'right', width: 90 }}>Days Covered (this invoice)</th>
                            <th style={{ textAlign: 'right', width: 90 }}>Working Days (this month)</th>
                            <th style={{ textAlign: 'right', width: 90 }}>Monthly Rate</th>
                            <th style={{ textAlign: 'right', width: 110 }}>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoiceBreakdown.perMonth.map(g => (
                            <tr key={g.ym}>
                              <td>{new Date(g.year, g.month - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}</td>
                              <td style={{ textAlign: 'right' }}>{g.count}</td>
                              <td style={{ textAlign: 'right' }}>{g.assignedCount}</td>
                              <td style={{ textAlign: 'right' }}>{CURRENCY_SYMBOLS[invoiceInspector?.currencyCost] || '₹'}{invoiceBreakdown.rate.toFixed(2)}</td>
                              <td style={{ textAlign: 'right' }}>{CURRENCY_SYMBOLS[invoiceInspector?.currencyCost] || '₹'}{g.amount.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )
                  ) : (
                    invoiceBreakdown.dates.length > 0 && (
                      <table className="vendor-table" style={{ fontSize: 12, width: '100%', margin: "8px 0 16px 0" }}>
                        <thead>
                          <tr><th>Date(s)</th><th style={{ textAlign: 'right', width: 90 }}>Rate / Day</th><th style={{ textAlign: 'right', width: 110 }}>Amount</th></tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{invoiceBreakdown.dates.map(formatDate).join(', ')}</td>
                            <td style={{ textAlign: 'right' }}>{CURRENCY_SYMBOLS[invoiceInspector?.currencyCost] || '₹'}{invoiceBreakdown.rate.toFixed(2)}</td>
                            <td style={{ textAlign: 'right' }}>{CURRENCY_SYMBOLS[invoiceInspector?.currencyCost] || '₹'}{invoiceBreakdown.subtotal.toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                    )
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, fontSize: 12 }}>
                    <div><span style={{ color: '#888' }}>{invoiceBreakdown.isManmonth ? 'Prorated Amount' : 'Days × Rate'}</span><br />{CURRENCY_SYMBOLS[invoiceInspector?.currencyCost] || '₹'}{invoiceBreakdown.subtotal.toFixed(2)}</div>
                    <div><span style={{ color: '#888' }} title="This invoice's own Overtime entries (Overtime table above)">Overtime</span><br />{CURRENCY_SYMBOLS[invoiceInspector?.currencyCost] || '₹'}{invoiceBreakdown.otTotal.toFixed(2)}</div>
                    <div><span style={{ color: '#888' }} title="This invoice's own Other Cost entries (Other Costs table above)">Other Costs</span><br />{CURRENCY_SYMBOLS[invoiceInspector?.currencyCost] || '₹'}{invoiceBreakdown.otherCosts.toFixed(2)}</div>
                    <div><span style={{ color: '#888' }}>Tax ({invoiceBreakdown.taxPct}%)</span><br />{CURRENCY_SYMBOLS[invoiceInspector?.currencyCost] || '₹'}{invoiceBreakdown.tax.toFixed(2)}</div>
                    <div style={{ fontWeight: 700 }}><span style={{ color: '#888', fontWeight: 400 }}>Invoice Amount</span><br />{CURRENCY_SYMBOLS[invoiceInspector?.currencyCost] || '₹'}{invoiceBreakdown.total.toFixed(2)}</div>
                  </div>
                </div>
              )}

              {/* Overtime / Other Costs — shown once inspector+work order selected */}
              {invoiceInspector ? (
                <>
                {/* Overtime — full width */}
                <div style={{ marginTop: 8, paddingTop: 16, borderTop: '1px solid #eee' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <label style={{ margin: 0, fontWeight: 600, fontSize: 13 }}>Overtime</label>
                    <button type="button" title="Add OT" className="action-btn" style={{ height: 'fit-content', color: '#437cf0', padding: 4, border: 'none', display: 'flex' }} onClick={handleAddInvoiceOvertime}>
                      <FiPlus size={12} />
                    </button>
                  </div>
                  {currentInvoice.overtime.length === 0 ? (
                    <span style={{ color: '#888', fontSize: 12 }}>No overtime entries</span>
                  ) : (
                    <table className="vendor-table" style={{ fontSize: 11, width: '100%' }}>
                      <thead>
                        <tr><th>Date(s)</th><th style={{ width: 80 }}>Hours</th><th style={{ width: 80 }}>Rate</th><th style={{ width: 90, textAlign: 'right' }}>Amount</th><th style={{ width: 30 }}></th></tr>
                      </thead>
                      <tbody>
                        {currentInvoice.overtime.map((entry, idx) => (
                          <tr key={idx}>
                            <td>
                              <DatePicker
                                multiple
                                format="DD-MM-YYYY"
                                value={(entry.dates || []).map(d => new Date(d)).filter(d => !isNaN(d))}
                                onChange={(dates) => handleInvoiceOvertimeChange(idx, { dates: dates ? dates.map(d => d.format('YYYY-MM-DD')) : [] })}
                                inputClass="form-control"
                                style={{ width: '100%', fontSize: 11 }}
                                placeholder="Select date(s)"
                              />
                            </td>
                            <td><input type="number" className="form-control" style={{ fontSize: 11 }} value={entry.hours} onChange={(e) => handleInvoiceOvertimeChange(idx, { hours: e.target.value })} /></td>
                            <td><input type="number" className="form-control" style={{ fontSize: 11 }} value={entry.rate} onChange={(e) => handleInvoiceOvertimeChange(idx, { rate: e.target.value })} /></td>
                            <td style={{ textAlign: 'right' }}>{CURRENCY_SYMBOLS[invoiceInspector.currencyCost] || '₹'}{overtimeEntryAmount(entry).toFixed(2)}</td>
                            <td>
                              <button type="button" title="Remove" onClick={() => handleRemoveInvoiceOvertime(idx)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 2, display: 'flex' }}>
                                <FiX size={12} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Other Costs + Tax — 50/50 */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16, paddingTop: 16, borderTop: '1px solid #eee' }}>
                  {/* Other Costs */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <label style={{ margin: 0, fontWeight: 600, fontSize: 13 }}>Other Costs</label>
                      <button type="button" title="Add Other Cost" className="action-btn" style={{ height: 'fit-content', color: '#437cf0', padding: 4, border: 'none', display: 'flex' }} onClick={handleAddInvoiceOtherCost}>
                        <FiPlus size={12} />
                      </button>
                    </div>
                    {currentInvoice.otherCostEntries.length === 0 ? (
                      <span style={{ color: '#888', fontSize: 12 }}>No other cost entries</span>
                    ) : (
                      <table className="vendor-table" style={{ fontSize: 11, width: '100%' }}>
                        <thead>
                          <tr><th>Description</th><th style={{ width: 110, textAlign: 'right' }}>Amount</th><th style={{ width: 30 }}></th></tr>
                        </thead>
                        <tbody>
                          {currentInvoice.otherCostEntries.map((entry, idx) => (
                            <tr key={idx}>
                              <td><input type="text" className="form-control" style={{ fontSize: 11 }} placeholder="e.g. Travel" value={entry.description} onChange={(e) => handleInvoiceOtherCostChange(idx, 'description', e.target.value)} /></td>
                              <td><input type="number" className="form-control" style={{ fontSize: 11, textAlign: 'right' }} value={entry.amount} onChange={(e) => handleInvoiceOtherCostChange(idx, 'amount', e.target.value)} /></td>
                              <td>
                                <button type="button" title="Remove" onClick={() => handleRemoveInvoiceOtherCost(idx)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 2, display: 'flex' }}>
                                  <FiX size={12} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Tax section */}
                  <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <label style={{ margin: 0, fontWeight: 600, fontSize: 13 }}>Tax</label>
                    <button type="button" title="Add Tax" className="action-btn" style={{ height: 'fit-content', color: '#437cf0', padding: 4, border: 'none', display: 'flex' }} onClick={handleAddInvoiceTax}>
                      <FiPlus size={12} />
                    </button>
                  </div>
                  {currentInvoice.taxes.length === 0 ? (
                    <span style={{ color: '#888', fontSize: 12 }}>No tax entries — defaults to Work Order tax ({parseFloat(invoiceInspectorWO?.billing?.tax) || 0}%)</span>
                  ) : (
                    <table className="vendor-table" style={{ fontSize: 11, width: '100%' }}>
                      <thead>
                        <tr>
                          <th>Tax Type</th>
                          <th style={{ textAlign: 'right', width: 90 }}>Percentage (%)</th>
                          <th style={{ textAlign: 'right', width: 110 }}>Amount</th>
                          <th style={{ width: 30 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentInvoice.taxes.map((t, idx) => {
                          const pct = parseFloat(t.percentage) || 0;
                          const taxBase = invoiceBreakdown ? invoiceBreakdown.subtotal + invoiceBreakdown.otTotal : 0;
                          const amt = parseFloat((taxBase * pct / 100).toFixed(2));
                          return (
                            <tr key={idx}>
                              <td>
                                <select className="form-control" style={{ fontSize: 11 }} value={t.type} onChange={e => handleInvoiceTaxChange(idx, 'type', e.target.value)}>
                                  <option value="">Select</option>
                                  <option value="CGST">CGST</option>
                                  <option value="IGST">IGST</option>
                                  <option value="SGST">SGST</option>
                                  <option value="VAT">VAT</option>
                                </select>
                              </td>
                              <td>
                                <input type="number" className="form-control" style={{ fontSize: 11, textAlign: 'right' }} min="0" max="100" step="0.01" placeholder="0" value={t.percentage} onChange={e => handleInvoiceTaxChange(idx, 'percentage', e.target.value)} />
                              </td>
                              <td style={{ textAlign: 'right', fontSize: 11 }}>
                                {CURRENCY_SYMBOLS[invoiceInspector?.currencyCost] || '₹'}{amt.toFixed(2)}
                              </td>
                              <td>
                                <button type="button" title="Remove" onClick={() => handleRemoveInvoiceTax(idx)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 2, display: 'flex' }}>
                                  <FiX size={12} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
                </div>
                </>
              ) : (
                <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
                  Select an Inspector and Work Order above to fill in Overtime / Other Costs for this invoice.
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px', borderTop: '1px solid #eee', background: '#fafafa' }}>
              <button
                type="button"
                className="add-more-btn"
                onClick={addInvoice}
                disabled={addingInvoice}
                title={editingInvoiceId ? 'Save changes' : (currentInvoice.file && id ? 'Add row and upload to OneDrive' : 'Add row')}
              >
                {addingInvoice
                  ? <><FaUpload size={12} /> Uploading…</>
                  : editingInvoiceId ? <><FiCheck size={12} /> Update</> : <><FaPlus size={12} /> Add</>}
              </button>
              <button
                type="button"
                style={{ background: '#6b7280', color: '#fff', border: 'none', borderRadius: 4, padding: '7px 16px', cursor: addingInvoice ? 'not-allowed' : 'pointer', fontSize: 13 }}
                onClick={cancelEditInvoice}
                disabled={addingInvoice}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suggestion Modal */}
      {showSuggestionModal && (() => {
        const uniqueCountries = [...new Set(suggestions.map(s => s.country).filter(Boolean))].sort();
        const uniqueStates    = [...new Set(suggestions.filter(s => !inspFilterCountry || s.country === inspFilterCountry).map(s => s.state).filter(Boolean))].sort();
        const uniqueCities    = [...new Set(suggestions.filter(s => (!inspFilterCountry || s.country === inspFilterCountry) && (!inspFilterState || s.state === inspFilterState)).map(s => s.city).filter(Boolean))].sort();

        const filtered = suggestions.filter(s => {
          if (inspSearch) {
            const q = inspSearch.toLowerCase();
            const comp = s.competency || {};
            const productIds = [];
            if (comp.disciplines) {
              comp.disciplines.forEach(d => (d.subDisciplines || []).forEach(sd => productIds.push(...(sd.products || []))));
            } else {
              Object.values(comp).forEach(subMap => {
                if (typeof subMap === 'object') Object.values(subMap).forEach(prods => { if (Array.isArray(prods)) productIds.push(...prods); });
              });
            }
            const productNames = [...new Set(productIds)].map(pid => products.find(p => p.id === pid || String(p._id) === String(pid))?.name).filter(Boolean);
            const matches =
              s.inspectorName?.toLowerCase().includes(q) ||
              s.email?.toLowerCase().includes(q) ||
              s.phone?.toLowerCase().includes(q) ||
              s.city?.toLowerCase().includes(q) ||
              s.state?.toLowerCase().includes(q) ||
              s.country?.toLowerCase().includes(q) ||
              productNames.some(n => n.toLowerCase().includes(q));
            if (!matches) return false;
          }
          if (inspFilterCountry && s.country !== inspFilterCountry) return false;
          if (inspFilterState   && s.state   !== inspFilterState)   return false;
          if (inspFilterCity    && s.city    !== inspFilterCity)     return false;
          if (inspFilterProduct) {
            const allProductIds = [];
            const comp = s.competency;
            if (comp?.disciplines) {
              comp.disciplines.forEach(d => (d.subDisciplines || []).forEach(sd => allProductIds.push(...(sd.products || []))));
            } else if (typeof comp === 'object') {
              Object.values(comp).forEach(subMap => {
                if (typeof subMap === 'object') Object.values(subMap).forEach(prods => { if (Array.isArray(prods)) allProductIds.push(...prods); });
              });
            }
            if (!allProductIds.some(pid => String(pid) === inspFilterProduct)) return false;
          }
          if (inspFilterCertificate) {
            const hasCert = (s.certificates || []).some(c => {
              const cid = c.certificateId?._id || c.certificateId;
              return String(cid) === inspFilterCertificate;
            });
            if (!hasCert) return false;
          }
          const h = inspHistory[String(s.id)];
          if (inspFilterHistory === 'company'  && !h?.totalJobs)    return false;
          if (inspFilterHistory === 'customer' && !h?.customerJobs) return false;
          if (inspFilterHistory === 'new'      && h?.totalJobs)     return false;
          if (inspMinJobs && (h?.totalJobs || 0) < Number(inspMinJobs)) return false;
          if (inspFilterLastUsed) {
            const cutoff = new Date();
            cutoff.setMonth(cutoff.getMonth() - Number(inspFilterLastUsed));
            if (!h?.lastJobDate || h.lastJobDate < cutoff.toISOString().split('T')[0]) return false;
          }
          return true;
        });

        if (inspSort) {
          const hOf = s => inspHistory[String(s.id)] || {};
          filtered.sort((a, b) => {
            if (inspSort === 'jobs') return (hOf(b).totalJobs || 0) - (hOf(a).totalJobs || 0);
            if (inspSort === 'last') return (hOf(b).lastJobDate || '').localeCompare(hOf(a).lastJobDate || '');
            return (a.inspectorName || '').localeCompare(b.inspectorName || '');
          });
        }

        const totalInspPages = Math.max(1, Math.ceil(filtered.length / inspPageSize));
        const safeInspPage = Math.min(inspPage, totalInspPages);
        const pagedInspectors = filtered.slice((safeInspPage - 1) * inspPageSize, safeInspPage * inspPageSize);

        const resetFilters = () => { setInspSearch(''); setInspFilterCountry(''); setInspFilterState(''); setInspFilterCity(''); setInspFilterProduct(''); setInspFilterCertificate(''); setInspFilterHistory(''); setInspFilterLastUsed(''); setInspMinJobs(''); setInspSort(''); };

        const truncate = (str, n) => (str && str.length > n ? `${str.slice(0, n)}...` : str);

        const selStyle = { padding: '6px 8px', border: '1px solid #ddd', borderRadius: 4, fontSize: 12, background: '#fff', cursor: 'pointer' };

        return (
          <div
            style={suggestionModalDragged
              ? { position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, pointerEvents: 'none' }
              : { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={suggestionModalDragged ? undefined : () => { setShowSuggestionModal(false); resetFilters(); setSelectedSuggestionIds(new Set()); }}
          >
            <Draggable handle=".modal-drag-handle" nodeRef={suggestionModalRef} onStart={() => setSuggestionModalDragged(true)}>
            <div ref={suggestionModalRef} onClick={(e) => e.stopPropagation()} style={{ background: '#fff', maxWidth: '100%', width: '95%', maxHeight: '75vh', display: 'flex', flexDirection: 'column', pointerEvents: 'auto', boxShadow: suggestionModalDragged ? '0 30px 80px rgba(0,0,0,0.45)' : undefined }}>

              {/* Header */}
              <div className="modal-drag-handle" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fff', padding: '14px 20px', background: '#1a2a4d', flexShrink: 0, cursor: 'grab' }}>
                <h3 style={{ margin: 0, fontWeight: 600 }}>Available Inspectors ({filtered.length}{filtered.length !== suggestions.length ? ` / ${suggestions.length}` : ''})</h3>
                <button onClick={() => { setShowSuggestionModal(false); resetFilters(); setSelectedSuggestionIds(new Set()); }} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#fff' }}>×</button>
              </div>

              {/* Filters */}
              <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee', background: '#f9fafb', flexShrink: 0 }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder="Search name, email, phone, city…"
                      value={inspSearch}
                      onChange={e => { setInspSearch(e.target.value); setInspPage(1); }}
                      style={{ ...selStyle, width: 190, cursor: 'text' }}
                    />
                    <select value={inspFilterCountry} onChange={e => { setInspFilterCountry(e.target.value); setInspFilterState(''); setInspFilterCity(''); setInspPage(1); }} style={selStyle}>
                      <option value="">All Countries</option>
                      {uniqueCountries.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select value={inspFilterState} onChange={e => { setInspFilterState(e.target.value); setInspFilterCity(''); setInspPage(1); }} style={{ ...selStyle, opacity: !inspFilterCountry ? 0.5 : 1 }} disabled={!inspFilterCountry}>
                      <option value="">All States</option>
                      {uniqueStates.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <select value={inspFilterCity} onChange={e => { setInspFilterCity(e.target.value); setInspPage(1); }} style={{ ...selStyle, opacity: !inspFilterCountry ? 0.5 : 1 }} disabled={!inspFilterCountry}>
                      <option value="">All Cities</option>
                      {uniqueCities.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select value={inspFilterProduct} onChange={e => { setInspFilterProduct(e.target.value); setInspPage(1); }} style={selStyle}>
                      <option value="">All Products</option>
                      {products.map(p => <option key={p.id || p._id} value={String(p.id || p._id)}>{p.name}</option>)}
                    </select>
                    <select value={inspFilterCertificate} onChange={e => { setInspFilterCertificate(e.target.value); setInspPage(1); }} style={selStyle}>
                      <option value="">All Certificates</option>
                      {certificateOptions.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
                    </select>
                    <select value={inspFilterHistory} onChange={e => { setInspFilterHistory(e.target.value); setInspPage(1); }} style={selStyle} title="Past jobs with this enquiry's company">
                      <option value="">All Inspectors</option>
                      <option value="company">Worked with our company</option>
                      <option value="customer" disabled={!formData.customerId}>Worked for this customer</option>
                      <option value="new">New to our company</option>
                    </select>
                    <select value={inspFilterLastUsed} onChange={e => { setInspFilterLastUsed(e.target.value); setInspPage(1); }} style={selStyle}>
                      <option value="">Last Used: Any time</option>
                      <option value="3">Last 3 months</option>
                      <option value="6">Last 6 months</option>
                      <option value="12">Last 1 year</option>
                    </select>
                    <input
                      type="number"
                      min="0"
                      placeholder="Min jobs"
                      value={inspMinJobs}
                      onChange={e => { setInspMinJobs(e.target.value); setInspPage(1); }}
                      style={{ ...selStyle, width: 80, cursor: 'text' }}
                    />
                    <select value={inspSort} onChange={e => { setInspSort(e.target.value); setInspPage(1); }} style={selStyle}>
                      <option value="">Sort: Default</option>
                      <option value="jobs">Most jobs</option>
                      <option value="last">Last used</option>
                      <option value="name">Name</option>
                    </select>
                    {(inspSearch || inspFilterCountry || inspFilterState || inspFilterCity || inspFilterProduct || inspFilterCertificate || inspFilterHistory || inspFilterLastUsed || inspMinJobs || inspSort) && (
                      <button onClick={() => { resetFilters(); setInspPage(1); }} style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 4, fontSize: 12, background: '#fff', cursor: 'pointer', color: '#666' }}>Clear</button>
                    )}
                  </div>

                  {/* Pagination — top right corner */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <select
                      value={inspPageSize}
                      onChange={e => { setInspPageSize(Number(e.target.value)); setInspPage(1); }}
                      style={selStyle}
                    >
                      {[25, 50, 75, 100].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span style={{ fontSize: 12, color: '#666' }}>Page {safeInspPage} of {totalInspPages}</span>
                    <button
                      type="button"
                      disabled={safeInspPage === 1}
                      onClick={() => setInspPage(p => p - 1)}
                      style={{ ...selStyle, cursor: safeInspPage === 1 ? 'not-allowed' : 'pointer', opacity: safeInspPage === 1 ? 0.5 : 1 }}
                    >
                      Prev
                    </button>
                    <button
                      type="button"
                      disabled={safeInspPage >= totalInspPages}
                      onClick={() => setInspPage(p => p + 1)}
                      style={{ ...selStyle, cursor: safeInspPage >= totalInspPages ? 'not-allowed' : 'pointer', opacity: safeInspPage >= totalInspPages ? 0.5 : 1 }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>

              {/* Bulk selection bar — appears once at least one row is checked */}
              {selectedSuggestionIds.size > 0 && (
                <div style={{ padding: '8px 16px', background: '#eef2ff', borderBottom: '1px solid #dbe3ff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                  <span style={{ fontSize: 13, color: '#333' }}>{selectedSuggestionIds.size} inspector{selectedSuggestionIds.size > 1 ? 's' : ''} selected</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" onClick={() => setSelectedSuggestionIds(new Set())} style={{ padding: '5px 10px', border: '1px solid #ccc', borderRadius: 4, background: '#fff', fontSize: 12, cursor: 'pointer' }}>Clear</button>
                    <button
                      type="button"
                      onClick={addSelectedInspectors}
                      title="Add without sending an email"
                      style={{ padding: '5px 14px', border: '1px solid #437cf0', borderRadius: 4, background: '#fff', color: '#437cf0', fontSize: 12, cursor: 'pointer' }}
                    >
                      Add Selected ({selectedSuggestionIds.size})
                    </button>
                    <button
                      type="button"
                      onClick={() => openBulkInviteModal(suggestions.filter(s => selectedSuggestionIds.has(s.id)))}
                      style={{ padding: '5px 14px', border: 'none', borderRadius: 4, background: '#437cf0', color: '#fff', fontSize: 12, cursor: 'pointer' }}
                    >
                      Send Invitation to Selected ({selectedSuggestionIds.size})
                    </button>
                  </div>
                </div>
              )}

              {/* Table */}
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {filtered.length === 0
                  ? <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}><p>No inspectors match the selected filters.</p></div>
                  : (
                    <div style={{ overflowX: 'auto', padding: '16px' }}>
                      <table className="data-table" style={{ width: '100%' }}>
                        <thead>
                          <tr>
                            <th style={{ width: 32 }}>
                              <input
                                type="checkbox"
                                checked={pagedInspectors.length > 0 && pagedInspectors.every(s => selectedSuggestionIds.has(s.id))}
                                onChange={() => {
                                  const pageIds = pagedInspectors.map(s => s.id);
                                  const allSelected = pageIds.every(pid => selectedSuggestionIds.has(pid));
                                  setSelectedSuggestionIds(prev => {
                                    const next = new Set(prev);
                                    pageIds.forEach(pid => allSelected ? next.delete(pid) : next.add(pid));
                                    return next;
                                  });
                                }}
                                title="Select all on this page"
                              />
                            </th>
                            <th>Inspector Name</th><th>City</th><th>State</th><th>Country</th><th>Phone</th><th>Email</th><th>Products</th><th>Standard Rate</th><th>Jobs With Us</th><th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedInspectors.map(s => {
                            const comp = s.competency || {};
                            const productIds = [];
                            if (comp.disciplines) {
                              comp.disciplines.forEach(d => (d.subDisciplines || []).forEach(sd => productIds.push(...(sd.products || []))));
                            } else {
                              Object.values(comp).forEach(subMap => {
                                if (typeof subMap === 'object') Object.values(subMap).forEach(prods => { if (Array.isArray(prods)) productIds.push(...prods); });
                              });
                            }
                            const productNames = [...new Set(productIds)].map(pid => products.find(p => p.id === pid || String(p._id) === String(pid))?.name).filter(Boolean);
                            const h = inspHistory[String(s.id)];
                            return (
                            <tr key={s.id}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selectedSuggestionIds.has(s.id)}
                                  onChange={() => setSelectedSuggestionIds(prev => {
                                    const next = new Set(prev);
                                    if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                                    return next;
                                  })}
                                />
                              </td>
                              <td>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  {s.inspectorName}
                                  <a
                                    href={s.cvUrl || undefined}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    download
                                    onClick={(e) => { if (!s.cvUrl) e.preventDefault(); }}
                                    title={s.cvUrl ? 'Download CV' : 'CV not uploaded'}
                                    style={{ display: 'flex', alignItems: 'center', color: s.cvUrl ? '#437cf0' : '#888', cursor: s.cvUrl ? 'pointer' : 'not-allowed', pointerEvents: s.cvUrl ? 'auto' : 'none' }}
                                  >
                                    <FiFileText size={14} />
                                  </a>
                                </span>
                              </td>
                              <td>{s.city || '—'}</td>
                              <td>{s.state || '—'}</td>
                              <td>{s.country}</td>
                              <td>
                                {s.phone ? (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontSize: '12px' }}>{s.phone}</span>
                                    <button
                                      type="button"
                                      title="Copy phone"
                                      onClick={() => handleCopy(s.phone, 'Phone')}
                                      style={{ background: 'none', border: 'none', padding: 2, display: 'flex', cursor: 'pointer', color: '#888' }}
                                    >
                                      <FiCopy size={12} />
                                    </button>
                                  </span>
                                ) : '—'}
                              </td>
                              <td>
                                {s.email ? (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ color: '#437cf0', fontSize: '12px' }}>{s.email}</span>
                                    <button
                                      type="button"
                                      title="Copy email"
                                      onClick={() => handleCopy(s.email, 'Email')}
                                      style={{ background: 'none', border: 'none', padding: 2, display: 'flex', cursor: 'pointer', color: '#888' }}
                                    >
                                      <FiCopy size={12} />
                                    </button>
                                  </span>
                                ) : '—'}
                              </td>
                              <td style={{ fontSize: '12px', color: '#444', maxWidth: '200px' }} title={productNames.length > 0 ? productNames.join(', ') : undefined}>
                                {productNames.length > 0 ? truncate(productNames.join(', '), 15) : '—'}
                              </td>
                              <td style={{ fontSize: '12px', color: '#444' }}>
                                {(() => {
                                  const rated = (h?.customers || []).filter(c => c.rate);
                                  const money = (cur, amt) => `${CURRENCY_SYMBOLS[cur] || cur || ''} ${amt}`;
                                  const avg = h?.avgRate || {};
                                  const master = s.standardRate?.amount != null ? money(s.standardRate.currency, s.standardRate.amount) : null;
                                  // Average past cost (INR) is the headline; master standard rate is the fallback.
                                  const headline = avg.day != null ? `${money('INR', avg.day)} /day`
                                    : avg.month != null ? `${money('INR', avg.month)} /month`
                                    : master || '—';
                                  return (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                      <span title={avg.day != null || avg.month != null ? `Average cost from past jobs${master ? ` · Master standard rate: ${master}` : ''}` : 'Master standard rate'}>
                                        {headline}
                                      </span>
                                      {(rated.length > 0 || avg.day != null || avg.month != null) && (
                                        <span
                                          title={[
                                            avg.day   != null && `Average cost: ${money('INR', avg.day)} per day (${avg.dayJobs} job${avg.dayJobs > 1 ? 's' : ''})`,
                                            avg.month != null && `Average cost: ${money('INR', avg.month)} per month (${avg.monthJobs} job${avg.monthJobs > 1 ? 's' : ''})`,
                                            master && `Master standard rate: ${master}`,
                                            rated.length > 0 && '',
                                            rated.length > 0 && 'Last rate per customer (Cost / Offered):',
                                            ...rated.map(c =>
                                              `• ${c.name}: ${money(c.rate.currencyCost, c.rate.cost)} / ${money(c.rate.currencyOffered, c.rate.offered)} per ${c.rate.per} (${c.rate.jobNumber})`
                                            ),
                                          ].filter(x => x !== false && x != null).join('\n')}
                                          style={{ display: 'flex', color: '#437cf0', cursor: 'help' }}
                                        >
                                          <FaHistory size={12} />
                                        </span>
                                      )}
                                    </span>
                                  );
                                })()}
                              </td>
                              <td style={{ fontSize: '12px', color: '#444' }}>
                                {h?.totalJobs ? (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                    {`${h.totalJobs} (${h.wonJobs} won)`}
                                    <span
                                      title={[
                                        h.lastJobDate && `Last job: ${formatDate(h.lastJobDate)} · ${h.lastJobNumber}`,
                                        `${h.totalDays} inspection day${h.totalDays === 1 ? '' : 's'}`,
                                      ].filter(Boolean).join('\n')}
                                      style={{ display: 'flex', color: '#437cf0', cursor: 'help' }}
                                    >
                                      <FaHistory size={12} />
                                    </span>
                                    {h.customers?.length > 0 && (
                                      <span
                                        title={['Customers worked for:', ...h.customers.map(c => `• ${c.name} (${c.jobs} job${c.jobs > 1 ? 's' : ''})`)].join('\n')}
                                        style={{ display: 'flex', color: h.customerJobs > 0 ? '#2e7d32' : '#888', cursor: 'help' }}
                                      >
                                        <FaUserTie size={12} />
                                      </span>
                                    )}
                                  </span>
                                ) : '—'}
                              </td>
                              <td style={{ display: 'flex', gap: 6 }}>
                                <button onClick={() => addInspectorFromSuggestion(s)} title="Add without sending an email" style={{ padding: '5px 12px', background: '#fff', color: '#437cf0', border: '1px solid #437cf0', borderRadius: '4px', fontSize: '12px' }}>Add</button>
                                <button onClick={() => openInviteModal(s, 'new')} style={{ padding: '5px 12px', background: '#437cf0', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '12px' }}>Invite</button>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )
                }
              </div>
            </div>
            </Draggable>
          </div>
        );
      })()}

      {/* Invite Inspector Modal — preview/edit the invitation email before it's sent */}
      {showInviteModal && (
        <div
          style={inviteModalDragged
            ? { position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, pointerEvents: 'none' }
            : { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}
          onClick={inviteModalDragged || sendingInvite ? undefined : () => setShowInviteModal(false)}
        >
          <Draggable handle=".modal-drag-handle" nodeRef={inviteModalRef} onStart={() => setInviteModalDragged(true)}>
          <div ref={inviteModalRef} onClick={(e) => e.stopPropagation()} style={{ background: '#fff', width: "80%", maxWidth: '95%', height: 'fit-content', overflow: 'hidden', borderRadius: 2, pointerEvents: 'auto', boxShadow: inviteModalDragged ? '0 30px 80px rgba(0,0,0,0.45)' : undefined }}>
            {/* Modal Header */}
            <div className="modal-drag-handle" style={{ background: '#1a2a4d', color: '#fff', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'grab' }}>
              <h3 style={{ margin: 0, fontWeight: 600, fontSize: 15 }}>
                {inviteMode === 'bulk'
                  ? `INVITE INSPECTORS (${bulkInviteTargets.length})`
                  : `${inviteMode === 'resend' ? 'RESEND INVITATION' : 'INVITE INSPECTOR'} - ${inviteTarget?.inspectorName}`}
              </h3>
              <button onClick={() => setShowInviteModal(false)} disabled={sendingInvite} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: sendingInvite ? 'not-allowed' : 'pointer', display: 'flex' }}><FiX /></button>
            </div>
            {/* Modal Body */}
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="form-group">
                <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>TO</label>
                {inviteMode === 'bulk' ? (
                  <div style={{ maxHeight: 130, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 4, padding: '8px 10px', fontSize: 12, background: '#fafafa' }}>
                    {bulkInviteTargets.map(t => (
                      <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '2px 0', color: '#444' }}>
                        <span>{t.inspectorName}</span>
                        <span style={{ color: t.email ? '#437cf0' : '#c0392b' }}>{t.email || 'No email — will be skipped'}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <input className="form-control" value={inviteTarget?.email || ''} disabled style={{ background: '#f5f5f5', color: '#555', cursor: 'not-allowed' }} />
                )}
              </div>
              <div className="form-group">
                <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>SUBJECT <span className="required">*</span></label>
                <input
                  className="form-control"
                  value={inviteForm.subject}
                  onChange={e => setInviteForm(f => ({ ...f, subject: e.target.value }))}
                  disabled={sendingInvite}
                  required
                />
              </div>
              <div className="form-group">
                <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>MESSAGE <span className="required">*</span></label>
                <textarea
                  className="form-control"
                  style={{ height : "30vh"}}
                  rows={7}
                  value={inviteForm.message}
                  onChange={e => setInviteForm(f => ({ ...f, message: e.target.value }))}
                  disabled={sendingInvite}
                  required
                />
              </div>
              <div className="form-group">
                <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>ATTACHMENTS (optional)</label>
                <input
                  id="invite-attachment-input"
                  type="file"
                  multiple
                  disabled={sendingInvite}
                  onChange={e => {
                    const files = Array.from(e.target.files || []);
                    setInviteAttachments(prev => [...prev, ...files]);
                    e.target.value = '';
                  }}
                  style={{ display: 'none' }}
                />
                <label
                  htmlFor="invite-attachment-input"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '6px 14px', fontSize: 12, fontWeight: 500,
                    border: '1px solid #d1d5db', borderRadius: 4,
                    background: '#fff', color: '#374151',
                    cursor: sendingInvite ? 'not-allowed' : 'pointer',
                    opacity: sendingInvite ? 0.5 : 1,
                    transition: 'background 0.15s',
                    width: 'fit-content'
                  }}
                >
                  <FiPaperclip size={13} /> Pin Attachment
                </label>
                {inviteAttachments.length > 0 && (
                  <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {inviteAttachments.map((f, idx) => (
                      <div key={`${f.name}-${idx}`} style={{ display: 'flex', width : "fit-content", gap: 8, fontSize: 12, background: '#f5f5f5', padding: '4px 8px', borderRadius: 4 }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name} ({(f.size / 1024).toFixed(0)} KB)</span>
                        <button
                          type="button"
                          disabled={sendingInvite}
                          onClick={() => setInviteAttachments(prev => prev.filter((_, i) => i !== idx))}
                          style={{ border: 'none', background: 'none', cursor: sendingInvite ? 'not-allowed' : 'pointer', color: '#c0392b', fontSize: 14, lineHeight: 1, flexShrink: 0 }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <span style={{ fontSize: 11, color: inviteAttachmentsBytes > INVITE_ATTACHMENTS_MAX_BYTES ? '#c0392b' : '#888' }}>
                      Total: {(inviteAttachmentsBytes / 1024 / 1024).toFixed(2)} MB / 20 MB limit
                    </span>
                  </div>
                )}
              </div>
              {/* Footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
                <button type="button" className="cust-btn" onClick={() => setShowInviteModal(false)} disabled={sendingInvite}>Cancel</button>
                <button type="button" className="cust-btn cust-btn-primary" onClick={handleSendInvite} disabled={sendingInvite}>
                  <FiCheck size={12} /> {sendingInvite
                    ? 'Sending…'
                    : inviteMode === 'bulk'
                      ? `Send to ${bulkInviteTargets.filter(t => t.email).length} Inspector${bulkInviteTargets.filter(t => t.email).length > 1 ? 's' : ''}`
                      : 'Send Invitation'}
                </button>
              </div>
            </div>
          </div>
          </Draggable>
        </div>
      )}

      {/* Add Customer Coordinator Modal */}
      {showAddCoordModal && (
        <div
          style={coordModalDragged
            ? { position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, pointerEvents: 'none' }
            : { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}
          onClick={coordModalDragged ? undefined : requestCloseCoordModal}
        >
          <Draggable handle=".modal-drag-handle" nodeRef={addCoordModalRef} onStart={() => setCoordModalDragged(true)}>
          <div ref={addCoordModalRef} onClick={(e) => e.stopPropagation()} style={{ background: '#fff', width: 480, maxWidth: '95%', overflow: 'hidden', borderRadius: 2, pointerEvents: 'auto', boxShadow: coordModalDragged ? '0 30px 80px rgba(0,0,0,0.45)' : undefined }}>
            {/* Modal Header */}
            <div className="modal-drag-handle" style={{ background: '#1a2a4d', color: '#fff', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'grab' }}>
              <h3 style={{ margin: 0, fontWeight: 600, fontSize: 15 }}>ADD CUSTOMER COORDINATOR</h3>
              <button onClick={requestCloseCoordModal} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', display: 'flex' }}><FiX /></button>
            </div>
            {/* Modal Body */}
            <form onSubmit={handleAddCoordinator} style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="form-group">
                <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>CUSTOMER <span className="required">*</span></label>
                {formData.customerId ? (
                  <input
                    className="form-control"
                    value={customers.find(c => c.id === formData.customerId)?.customerName || customers.find(c => c.id === formData.customerId)?.companyName || ''}
                    disabled
                    style={{ background: '#f5f5f5', color: '#555', cursor: 'not-allowed' }}
                  />
                ) : (
                  <select
                    className="form-control"
                    value={addCoordForm.customerId}
                    onChange={e => setAddCoordForm(f => ({ ...f, customerId: e.target.value }))}
                    required
                  >
                    <option value="">-- Select Customer --</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>{c.customerName || c.companyName}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="form-group">
                <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>COORDINATOR NAME <span className="required">*</span></label>
                <input
                  className="form-control"
                  value={addCoordForm.name}
                  onChange={e => setAddCoordForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Full name"
                  required
                />
              </div>
              <div className="form-group">
                <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>EMAIL <span className="required">*</span></label>
                <input
                  className="form-control"
                  type="email"
                  value={addCoordForm.email}
                  onChange={e => setAddCoordForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="email@example.com"
                  required
                />
              </div>
              <div className="form-group">
                <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>PHONE</label>
                <input
                  className="form-control"
                  value={addCoordForm.mobileNumber}
                  onChange={e => setAddCoordForm(f => ({ ...f, mobileNumber: e.target.value }))}
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>
              {/* Footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
                <button type="button" className="cust-btn" onClick={requestCloseCoordModal}>Cancel</button>
                <button type="submit" className="cust-btn cust-btn-primary" disabled={savingCoord}>
                  <FiCheck size={12} /> {savingCoord ? 'Saving…' : 'Add Coordinator'}
                </button>
              </div>
            </form>
          </div>
          </Draggable>
        </div>
      )}

      {/* Add Vendor Modal */}
      {showAddVendorModal && (
        <div
          style={vendorModalDragged
            ? { position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, pointerEvents: 'none' }
            : { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={vendorModalDragged ? undefined : requestCloseVendorModal}
        >
          <Draggable handle=".modal-drag-handle" nodeRef={addVendorModalRef} onStart={() => setVendorModalDragged(true)}>
          <div ref={addVendorModalRef} onClick={(e) => e.stopPropagation()} style={{ background: '#fff', maxWidth: '500px', width: '90%', overflow: 'hidden', pointerEvents: 'auto', boxShadow: vendorModalDragged ? '0 30px 80px rgba(0,0,0,0.45)' : undefined }}><div className="modal-drag-handle" style={{ background: '#1a2a4d', color: '#fff', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'grab' }}><h3 style={{ margin: 0, fontWeight: 600, fontSize: 16 }}>ADD VENDOR</h3><button onClick={requestCloseVendorModal} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, display: 'flex', cursor: 'pointer' }}><FiX /></button></div>
            <form onSubmit={handleAddVendor} style={{ padding: '20px' }}>
              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label>Vendor Name <span className="required">*</span></label>
                <input type="text" className="form-control" value={newVendorForm.vendorName} onChange={(e) => setNewVendorForm(prev => ({ ...prev, vendorName: e.target.value }))} required />
              </div>
              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label>Country <span className="required">*</span></label>
                <div className="searchable-select-container">
                  <input
                    className="search-input"
                    placeholder="Search Country..."
                    value={vOpen.country ? vSearch.country : newVendorForm.country}
                    onFocus={() => { setVOpen(p => ({ ...p, country: true })); setVSearch(p => ({ ...p, country: '' })); setVFocusIdx(p => ({ ...p, country: -1 })); }}
                    onBlur={() => setTimeout(() => { setVOpen(p => ({ ...p, country: false })); setVFocusIdx(p => ({ ...p, country: -1 })); }, 200)}
                    onChange={(e) => { setVSearch(p => ({ ...p, country: e.target.value })); setVFocusIdx(p => ({ ...p, country: -1 })); }}
                    onKeyDown={(e) => {
                      const list = ALL_COUNTRIES.filter(c => c.name.toLowerCase().includes(vSearch.country.toLowerCase()));
                      if (e.key === 'ArrowDown') { e.preventDefault(); setVOpen(p => ({ ...p, country: true })); setVFocusIdx(p => ({ ...p, country: Math.min(p.country + 1, list.length - 1) })); }
                      else if (e.key === 'ArrowUp') { e.preventDefault(); setVFocusIdx(p => ({ ...p, country: Math.max(p.country - 1, -1) })); }
                      else if (e.key === 'Enter' && vFocusIdx.country >= 0) { e.preventDefault(); const c = list[vFocusIdx.country]; if (c) { setNewVendorForm(p => ({ ...p, country: c.name, state: '', city: '' })); setVOpen(p => ({ ...p, country: false })); setVFocusIdx(p => ({ ...p, country: -1 })); } }
                      else if (e.key === 'Escape') { setVOpen(p => ({ ...p, country: false })); setVFocusIdx(p => ({ ...p, country: -1 })); }
                    }}
                    style={{ width: '100%' }}
                  />
                  {vOpen.country && (
                    <div className="searchable-dropdown">
                      {ALL_COUNTRIES.filter(c => c.name.toLowerCase().includes(vSearch.country.toLowerCase())).map((c, idx) => (
                        <div key={c.isoCode} className="dropdown-item" style={{ background: idx === vFocusIdx.country ? '#e8f0fb' : '' }} onMouseDown={() => { setNewVendorForm(p => ({ ...p, country: c.name, state: '', city: '' })); setVOpen(p => ({ ...p, country: false })); }}>{c.name}</div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>State</label>
                  <div className="searchable-select-container" style={!newVendorForm.country ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                    <input
                      className="search-input"
                      placeholder="Search State..."
                      value={vOpen.state ? vSearch.state : newVendorForm.state}
                      onFocus={() => { setVOpen(p => ({ ...p, state: true })); setVSearch(p => ({ ...p, state: '' })); setVFocusIdx(p => ({ ...p, state: -1 })); }}
                      onBlur={() => setTimeout(() => { setVOpen(p => ({ ...p, state: false })); setVFocusIdx(p => ({ ...p, state: -1 })); }, 200)}
                      onChange={(e) => { setVSearch(p => ({ ...p, state: e.target.value })); setVFocusIdx(p => ({ ...p, state: -1 })); }}
                      onKeyDown={(e) => {
                        const list = vStateOptions.filter(s => s.name.toLowerCase().includes(vSearch.state.toLowerCase()));
                        if (e.key === 'ArrowDown') { e.preventDefault(); setVOpen(p => ({ ...p, state: true })); setVFocusIdx(p => ({ ...p, state: Math.min(p.state + 1, list.length - 1) })); }
                        else if (e.key === 'ArrowUp') { e.preventDefault(); setVFocusIdx(p => ({ ...p, state: Math.max(p.state - 1, -1) })); }
                        else if (e.key === 'Enter' && vFocusIdx.state >= 0) { e.preventDefault(); const s = list[vFocusIdx.state]; if (s) { setNewVendorForm(p => ({ ...p, state: s.name, city: '' })); setVOpen(p => ({ ...p, state: false })); setVFocusIdx(p => ({ ...p, state: -1 })); } }
                        else if (e.key === 'Escape') { setVOpen(p => ({ ...p, state: false })); setVFocusIdx(p => ({ ...p, state: -1 })); }
                      }}
                      style={{ width: '100%' }}
                    />
                    {vOpen.state && (
                      <div className="searchable-dropdown">
                        {vStateOptions.filter(s => s.name.toLowerCase().includes(vSearch.state.toLowerCase())).map((s, idx) => (
                          <div key={s.isoCode} className="dropdown-item" style={{ background: idx === vFocusIdx.state ? '#e8f0fb' : '' }} onMouseDown={() => { setNewVendorForm(p => ({ ...p, state: s.name, city: '' })); setVOpen(p => ({ ...p, state: false })); }}>{s.name}</div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>City</label>
                  <div className="searchable-select-container" style={!newVendorForm.state ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                    <input
                      className="search-input"
                      placeholder="Search City..."
                      value={vOpen.city ? vSearch.city : newVendorForm.city}
                      onFocus={() => { setVOpen(p => ({ ...p, city: true })); setVSearch(p => ({ ...p, city: '' })); setVFocusIdx(p => ({ ...p, city: -1 })); }}
                      onBlur={() => setTimeout(() => { setVOpen(p => ({ ...p, city: false })); setVFocusIdx(p => ({ ...p, city: -1 })); }, 200)}
                      onChange={(e) => { setVSearch(p => ({ ...p, city: e.target.value })); setVFocusIdx(p => ({ ...p, city: -1 })); }}
                      onKeyDown={(e) => {
                        const list = vCityOptions.filter(c => c.name.toLowerCase().includes(vSearch.city.toLowerCase()));
                        if (e.key === 'ArrowDown') { e.preventDefault(); setVOpen(p => ({ ...p, city: true })); setVFocusIdx(p => ({ ...p, city: Math.min(p.city + 1, list.length - 1) })); }
                        else if (e.key === 'ArrowUp') { e.preventDefault(); setVFocusIdx(p => ({ ...p, city: Math.max(p.city - 1, -1) })); }
                        else if (e.key === 'Enter' && vFocusIdx.city >= 0) { e.preventDefault(); const c = list[vFocusIdx.city]; if (c) { setNewVendorForm(p => ({ ...p, city: c.name })); setVOpen(p => ({ ...p, city: false })); setVFocusIdx(p => ({ ...p, city: -1 })); } }
                        else if (e.key === 'Escape') { setVOpen(p => ({ ...p, city: false })); setVFocusIdx(p => ({ ...p, city: -1 })); }
                      }}
                      style={{ width: '100%' }}
                    />
                    {vOpen.city && (
                      <div className="searchable-dropdown">
                        {vCityOptions.filter(c => c.name.toLowerCase().includes(vSearch.city.toLowerCase())).map((c, idx) => (
                          <div key={c.name} className="dropdown-item" style={{ background: idx === vFocusIdx.city ? '#e8f0fb' : '' }} onMouseDown={() => { setNewVendorForm(p => ({ ...p, city: c.name })); setVOpen(p => ({ ...p, city: false })); }}>{c.name}</div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label>Address <span className="required">*</span></label>
                <textarea className="form-control" value={newVendorForm.address} onChange={(e) => setNewVendorForm(prev => ({ ...prev, address: e.target.value }))} required rows="3" />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="submit" className="cust-btn-primary" style={{ padding: '8px 16px', borderRadius: '0', border: 'none' }} disabled={savingVendor}>{savingVendor ? 'Adding...' : 'Add Vendor'}</button>
                <button type="button" onClick={requestCloseVendorModal} className="cust-btn">Cancel</button>
              </div>
            </form>
          </div>
          </Draggable>
        </div>
      )}

      {/* Add Product Modal */}
      {showAddProductModal && (
        <div
          style={productModalDragged
            ? { position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, pointerEvents: 'none' }
            : { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={productModalDragged ? undefined : () => setShowAddProductModal(false)}
        >
          <Draggable handle=".modal-drag-handle" nodeRef={addProductModalRef} onStart={() => setProductModalDragged(true)}>
          <div ref={addProductModalRef} onClick={(e) => e.stopPropagation()} style={{ background: '#fff', maxWidth: '500px', width: '90%', overflow: 'hidden', pointerEvents: 'auto', boxShadow: productModalDragged ? '0 30px 80px rgba(0,0,0,0.45)' : undefined }}><div className="modal-drag-handle" style={{ background: '#1a2a4d', color: '#fff', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'grab' }}><h3 style={{ margin: 0, fontWeight: 600, fontSize: 16 }}>ADD PRODUCT</h3><button onClick={() => setShowAddProductModal(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, display: 'flex', cursor: 'pointer' }}><FiX /></button></div>
            <form onSubmit={handleAddProduct} style={{ padding: '20px' }}>
              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label>Product Name <span className="required">*</span></label>
                <input type="text" className="form-control" value={newProductForm.name} onChange={(e) => setNewProductForm(prev => ({ ...prev, name: e.target.value }))} required /></div>
              <div className="form-group" style={{ marginBottom: '15px' }}>

                <label>Discipline <span className="required">*</span></label>
                <select className="form-control" value={newProductForm.disciplineId} onChange={(e) => { setNewProductForm(prev => ({ ...prev, disciplineId: e.target.value, subDisciplineId: '' })); handleFetchSubDisciplines(e.target.value); }} required><option value="">Select Discipline</option>{disciplines.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}</select></div><div className="form-group" style={{ marginBottom: '20px' }}><label>Sub-Discipline <span className="required">*</span></label><select className="form-control" value={newProductForm.subDisciplineId} onChange={(e) => setNewProductForm(prev => ({ ...prev, subDisciplineId: e.target.value }))} disabled={!newProductForm.disciplineId} required><option value="">Select Sub-Discipline</option>{subDisciplines.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div><div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="submit" className="cust-btn-primary" style={{ padding: '8px 16px', borderRadius: '0', border: 'none' }} disabled={savingProduct}>{savingProduct ? 'Adding...' : 'Add Product'}</button>
                <button type="button" onClick={() => setShowAddProductModal(false)} className="cust-btn">Cancel</button>
              </div></form>
          </div>
          </Draggable>
        </div>
      )}

      {/* Add New Inspector Modal */}
      {showAddInspectorModal && (
        <div
          className={inspModalDragged ? undefined : 'cust-overlay'}
          style={inspModalDragged
            ? { position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, pointerEvents: 'none' }
            : { zIndex: 1100 }}
          onClick={inspModalDragged ? undefined : requestCloseInspectorModal}
        >
          <Draggable handle=".cust-modal-header" nodeRef={addInspectorModalRef} onStart={() => setInspModalDragged(true)}>
          <div className="insp-modal" ref={addInspectorModalRef} style={inspModalDragged ? { pointerEvents: 'auto', boxShadow: '0 30px 80px rgba(0,0,0,0.45)' } : {}} onClick={(e) => e.stopPropagation()} >
            <div className="cust-modal-header" style={{ cursor: 'grab' }}><span className="cust-modal-title"><FiUserCheck style={{ marginRight: 8 }} /> NEW INSPECTOR</span><button className="cust-modal-close" onClick={requestCloseInspectorModal} style={{ cursor: 'pointer' }}><FiX size={16} /></button></div>
            <div className="insp-modal-body">
              <div className="insp-row-wrap" style={{ display: 'flex', gap: '20px' }}>
                <div className="sub-card" style={{ flex: 1 }}>
                  <div className={`sub-card-header ${secBasic ? "expanded" : ""}`} onClick={() => setSecBasic(!secBasic)}><h2><FiUserCheck /> BASIC INFORMATION</h2>{secBasic ? <FiChevronUp /> : <FiChevronDown />}</div>
                  {secBasic && (<div className="sub-card-content"><div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}><div className="cust-fg-full"><label className="cust-label">INSPECTOR NAME <span className="required">*</span></label><input className="cust-input" value={newInspForm.inspectorName} onChange={(e) => setNewInspForm(f => ({ ...f, inspectorName: e.target.value }))} /></div><div><label className="cust-label">EMAIL <span className="required">*</span></label><input className="cust-input" type="email" value={newInspForm.email} onChange={(e) => setNewInspForm(f => ({ ...f, email: e.target.value }))} /></div><div><PhoneField label="PHONE" value={newInspForm.phone} onChange={(val) => setNewInspForm(f => ({ ...f, phone: val }))} /></div><div><label className="cust-label">INSPECTOR SOURCE</label><select className="cust-input" value={newInspForm.inspectorSource} onChange={(e) => setNewInspForm(f => ({ ...f, inspectorSource: e.target.value }))}><option value="Direct">Direct</option><option value="Agency">Agency</option></select></div><div><label className="cust-label">EXPERIENCE (YEARS)</label><input className="cust-input" type="number" value={newInspForm.experience} onChange={(e) => setNewInspForm(f => ({ ...f, experience: e.target.value }))} /></div><div><label className="cust-label">DEGREE</label><select className="cust-input" value={newInspForm.degree} onChange={(e) => setNewInspForm(f => ({ ...f, degree: e.target.value }))}><option value="">Select degree</option>{DEGREE_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}</select></div><div><label className="cust-label">BRANCH</label><select className="cust-input" value={newInspForm.branch} onChange={(e) => setNewInspForm(f => ({ ...f, branch: e.target.value }))}><option value="">Select branch</option>{BRANCH_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}</select></div><div><label className="cust-label">CV / RESUME</label><div className="insp-cv-upload-wrap">{newInspForm.cvFile && (<span className="insp-cv-selected"><FiCheck size={11} /> {newInspForm.cvFile.name}</span>)}<label htmlFor="manday-cv-upload" className="insp-cv-btn"><FiUploadCloud size={12} />{newInspForm.cvFile ? "Replace CV" : "Upload CV"}</label><input id="manday-cv-upload" type="file" accept=".pdf,.doc,.docx" style={{ display: "none" }} onChange={(e) => { const file = e.target.files?.[0]; if (file) setNewInspForm(f => ({ ...f, cvFile: file })); e.target.value = ""; }} /></div></div></div></div>)}
                </div>
                <div className="sub-card" style={{ flex: 1 }}>
                  <div className={`sub-card-header ${secLocation ? "expanded" : ""}`} onClick={() => setSecLocation(!secLocation)}><h2><FiMapPin /> LOCATION</h2>{secLocation ? <FiChevronUp /> : <FiChevronDown />}</div>
                  {secLocation && (
                    <div className="sub-card-content">
                      <div className="form-grid">
                        <div>
                          <label className="cust-label">COUNTRY <span className="required">*</span></label>
                          <Select
                            options={ALL_COUNTRIES.map(c => ({ value: c.name, label: c.name }))}
                            filterOption={matchFromStart}
                            value={newInspForm.country ? { value: newInspForm.country, label: newInspForm.country } : null}
                            onChange={(s) => setNewInspForm(f => ({ ...f, country: s ? s.value : '', state: '', city: '' }))}
                            placeholder="Search Country..."
                            isClearable
                            styles={filterSelectStyles}
                          />
                        </div>
                        <div>
                          <label className="cust-label">STATE</label>
                          <Select
                            options={stateOptions.map(s => ({ value: s.name, label: s.name }))}
                            filterOption={matchFromStart}
                            value={newInspForm.state ? { value: newInspForm.state, label: newInspForm.state } : null}
                            onChange={(s) => setNewInspForm(f => ({ ...f, state: s ? s.value : '', city: '' }))}
                            placeholder="Search State..."
                            isClearable
                            isDisabled={!newInspForm.country}
                            styles={filterSelectStyles}
                          />
                        </div>
                        <div>
                          <label className="cust-label">CITY</label>
                          <Select
                            options={cityOptions.map(c => ({ value: c.name, label: c.name }))}
                            filterOption={matchFromStart}
                            value={newInspForm.city ? { value: newInspForm.city, label: newInspForm.city } : null}
                            onChange={(s) => setNewInspForm(f => ({ ...f, city: s ? s.value : '' }))}
                            placeholder="Search City..."
                            isClearable
                            isDisabled={!newInspForm.state}
                            styles={filterSelectStyles}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="sub-card" style={{ marginTop: '20px' }}>
                <div className={`sub-card-header ${secQual ? "expanded" : ""}`} onClick={() => setSecQual(!secQual)}><h2><FiAward /> QUALIFICATION DETAILS</h2>{secQual ? <FiChevronUp /> : <FiChevronDown />}</div>
                {secQual && (<div className="sub-card-content"><Competency value={newInspForm.competency} onChange={(val) => setNewInspForm(f => ({ ...f, competency: val }))} /><div style={{ padding: 0, border: '1px solid #ccc', marginBottom: '1rem', marginTop: '1rem' }}><div className="insp-section-sub-header"><span>CERTIFICATES</span><span className="insp-cert-count">{newInspForm.certificates.length}</span></div>{newInspForm.certificates.length > 0 && (<table className="insp-cert-table"><thead><tr><th>CERTIFICATE</th><th>EXPIRY</th><th>FILE</th><th style={{ width: 80 }}></th></tr></thead><tbody>{newInspForm.certificates.map((c, idx) => (<tr key={idx}><td>{certificateOptions.find(o => o.id === c.certificateId)?.name || c.certificateId}</td><td>{c.expiryDate || '—'}</td><td>{c.certFile ? <span style={{ fontSize: 12, color: '#107c10' }}><FiUploadCloud size={11} /> {c.certFile.name}</span> : (c.fileUrl ? <a href={c.fileUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>View</a> : <span style={{ color: '#aaa', fontSize: 12 }}>None</span>)}</td><td><button type="button" className="cust-del-btn" onClick={() => setNewInspForm(f => ({ ...f, certificates: f.certificates.filter((_, i) => i !== idx) }))}><FaTrash size={11} /> Remove</button></td></tr>))}</tbody></table>)}{newInspForm.certificates.length === 0 && !certFormOpen && (<div className="insp-empty-state"><FiAward size={20} style={{ color: '#ccc' }} /><p>No certificates added yet</p><button type="button" className="cust-btn cust-btn-green" style={{ marginLeft: '20px' }} onClick={() => setCertFormOpen(true)}><FiPlus size={11} /> Add Certificate</button></div>)}{newInspForm.certificates.length > 0 && !certFormOpen && (<div style={{ padding: '10px 0' }}><button type="button" className="cust-btn cust-btn-green" onClick={() => setCertFormOpen(true)}><FiPlus size={11} /> Add Certificate</button></div>)}{certFormOpen && (<div className="insp-inline-add-panel"><div className="cust-panel-title"><span>ADD CERTIFICATE</span><button type="button" className="cust-cancel-link" onClick={() => { setCertFormOpen(false); setCertInput({ certificateId: "", expiryDate: "", fileUrl: "", certFile: null }); }}><FiX size={11} /> Cancel</button></div><div className="form-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 10 }}><div><label className="cust-label">CERTIFICATE <span className="required">*</span></label><select className="cust-input" value={certInput.certificateId} onChange={(e) => setCertInput(p => ({ ...p, certificateId: e.target.value }))}><option value="">-- Select --</option>{certificateOptions.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}</select></div><div><label className="cust-label">EXPIRY DATE</label><input className="cust-input" type="date" value={certInput.expiryDate} onChange={(e) => setCertInput(p => ({ ...p, expiryDate: e.target.value }))} /></div><div><label className="cust-label">CERTIFICATE FILE</label><input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { const file = e.target.files?.[0]; if (file) setCertInput(p => ({ ...p, certFile: file, fileUrl: file.name })); e.target.value = ""; }} style={{ fontSize: 12 }} /></div></div><div className="cust-panel-actions"><button type="button" className="cust-btn" onClick={() => { setCertFormOpen(false); setCertInput({ certificateId: "", expiryDate: "", fileUrl: "", certFile: null }); }}>Cancel</button><button type="button" className="cust-btn cust-btn-primary" onClick={handleAddCert}><FiCheck size={11} /> Save Certificate</button></div></div>)}</div><ClientSpecificApproval value={newInspForm.clientApprovals} onChange={(val) => setNewInspForm(f => ({ ...f, clientApprovals: val }))} /></div>)}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '15px 20px', borderTop: '1px solid #eee' }}><button className="save-btn" onClick={handleSaveNewInspector} disabled={savingInspector} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><FiSave /> {savingInspector ? "Saving..." : "Save Inspector"}</button></div>
          </div>
          </Draggable>
        </div>
      )}

      {/* View Inspector Modal */}
      {showViewInspectorModal && viewingInspector && (
        <div className="cust-overlay" onClick={() => setShowViewInspectorModal(false)} style={{ zIndex: 1100 }}>
          <div className="insp-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}><div className="cust-modal-header"><span className="cust-modal-title"><FiUserCheck style={{ marginRight: 8 }} /> INSPECTOR DETAILS</span><button className="cust-modal-close" onClick={() => setShowViewInspectorModal(false)}><FiX size={16} /></button></div>
            <div className="insp-modal-body" style={{ padding: '20px', maxHeight: '70vh', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', marginBottom: '20px' }}>
                {[
                  { key: 'inspectorName', label: 'Inspector Name', icon: FiUserCheck },
                  { key: 'email', label: 'Email', icon: FiMail },
                  { key: 'phone', label: 'Phone', icon: FiPhone },
                  { key: 'inspectorSource', label: 'Source', icon: FiBriefcase },
                  { key: 'country', label: 'Country', icon: FiGlobe },
                  { key: 'state', label: 'State', icon: FiMapPin },
                  { key: 'city', label: 'City', icon: FiMapPin },
                  { key: 'experience', label: 'Experience', icon: FiClock },
                  { key: 'degree', label: 'Degree', icon: FiBookOpen },
                  { key: 'branch', label: 'Branch', icon: FiBookOpen },
                ].map(({ key, label, icon: Icon }) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', background: '#f8f9fb', border: '1px solid #e5e7eb', padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(67,124,240,0.12)', color: '#437cf0', flexShrink: 0 }}>
                      <Icon size={15} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '11px', color: '#8a8f98', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
                      <div style={{ fontSize: '14px', color: '#1f2937', marginTop: '3px', wordBreak: 'break-word' }}>{viewingInspector[key] || '—'}</div>
                    </div>
                  </div>
                ))}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '12px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(34,197,94,0.15)', color: '#16a34a', flexShrink: 0 }}>
                    <FiDollarSign size={15} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '11px', color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Standard Rate</div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#15803d', marginTop: '3px' }}>
                      {viewingInspector.standardRate?.amount != null
                        ? `${CURRENCY_SYMBOLS[viewingInspector.standardRate.currency] || viewingInspector.standardRate.currency || ''} ${viewingInspector.standardRate.amount}`
                        : '—'}
                    </div>
                  </div>
                </div>
              </div>
              {viewingInspector.certificates?.length > 0 &&
                <div style={{margin: 0 }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '10px' }}>Certificates</h3>
                  <table className="insp-cert-table" style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left' }}>Certificate</th>
                        <th style={{ textAlign: 'left' }}>Expiry Date</th>
                        <th style={{ textAlign: 'left' }}>File</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewingInspector.certificates.map((cert, idx) => {
                        const certName = certificateOptions.find(o => o.id === (cert.certificateId?._id || cert.certificateId))?.name
                          || cert.certificateId?.name
                          || cert.certificateId
                          || '—';
                        const expiry = formatDate(cert.expiryDate);
                        return (
                          <tr key={idx}>
                            <td>{certName}</td>
                            <td>{expiry}</td>
                            <td>{cert.fileUrl
                              ? <a href={cert.fileUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#0078d4' }}><FiUploadCloud size={11} /> View</a>
                              : <span style={{ color: '#aaa', fontSize: 12 }}>None</span>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>}
              {viewingInspector.cvUrl && (
                <div style={{ margin: 0 }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '10px' }}>CV / Resume</h3>
                  <a
                    href={viewingInspector.cvUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,fontSize: 13
                    }}
                  >
                    <FiUploadCloud size={14} /> View CV
                  </a>
                </div>
              )}
            </div>
            <div className="cust-modal-footer"><button className="cust-btn" onClick={() => setShowViewInspectorModal(false)}>Close</button></div>
          </div>
        </div>
      )}

      {/* Upload Signed Work Order Modal */}
      {showSignedWOModal && signingInspector && (
        <div className="cust-overlay" onClick={() => { setShowSignedWOModal(false); setSignedWOFile(null); }} style={{ zIndex: 1100 }}>
          <div className="insp-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="cust-modal-header">
              <span className="cust-modal-title"><FiUploadCloud style={{ marginRight: 8 }} /> UPLOAD SIGNED WORK ORDER</span>
              <button className="cust-modal-close" onClick={() => { setShowSignedWOModal(false); setSignedWOFile(null); }}><FiX size={16} /></button>
            </div>
            <div className="insp-modal-body" style={{ padding: '24px 20px' }}>
              <p style={{ fontSize: 13, color: '#555', marginBottom: 16 }}>
                Inspector: <strong>{getDisplayName(signingInspector.inspectorId, signingInspector.inspectorName || signingInspector.inspectorId, signingInspector.id)}</strong><br />
                Work Order: <strong>{signingInspector.workOrder}</strong>
              </p>
              <div style={{ border: '2px dashed #d1d5db', borderRadius: 8, padding: '24px', textAlign: 'center', background: signedWOFile ? '#f0fdf4' : '#fafafa', cursor: 'pointer' }}
                onClick={() => document.getElementById('signed-wo-input').click()}>
                <FiUploadCloud size={32} color={signedWOFile ? '#22c55e' : '#9ca3af'} style={{ marginBottom: 8 }} />
                <p style={{ margin: 0, fontSize: 13, color: signedWOFile ? '#16a34a' : '#6b7280' }}>
                  {signedWOFile ? signedWOFile.name : 'Click to select signed work order file'}
                </p>
                <input id="signed-wo-input" type="file" style={{ display: 'none' }} onChange={e => setSignedWOFile(e.target.files?.[0] || null)} />
              </div>
            </div>
            <div className="cust-modal-footer">
              <button className="cust-btn" onClick={() => { setShowSignedWOModal(false); setSignedWOFile(null); }}>Cancel</button>
              <button
                className="cust-btn"
                style={{ background: signedWOFile && !uploadingSignedWO ? '#e67e22' : '#ccc', color: '#fff', cursor: signedWOFile && !uploadingSignedWO ? 'pointer' : 'not-allowed' }}
                disabled={!signedWOFile || uploadingSignedWO}
                onClick={handleUploadSignedWO}
              >
                {uploadingSignedWO ? 'Uploading…' : 'Upload to OneDrive'}
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default Manday;
